<template>
  <div class="vb-root">

    <!-- header rail. same ops rail language as ops_board: solo page title
         on the left (abbreviated on the mobile chrome), base filter +
         manual refresh on the right. -->
    <div class="vb-rail">
      <div class="vb-rail-text">
        <!-- tenant context rides above the title as an eyebrow-style text
             line (no pill: the pill's padded border broke the left
             alignment with the title, and inline it pushed the action
             cluster to a second line on the mobile chrome) -->
        <div v-if="contextTenantName" class="vb-rail-tenant-eyebrow">{{ contextTenantName }}</div>
        <h3 class="vb-rail-title">
          {{ mobileView ? 'VSM2' : 'Vehicle Status Manager v2' }}
          <!-- this board left the Preview nav section to sit under the page it
               replaces, so the badge is now the only thing saying it is still
               a preview. it has to stay until the board supersedes v1.
               NOT in standalone: that mount is the unauth customer kiosk at
               /vsm2, and "Preview" there advertises unfinished software to
               someone who has no idea there is a v1. -->
          <span v-if="!standalone" class="vb-rail-preview-badge">Preview</span>
          <FiFavStar />
        </h3>
      </div>
      <div class="vb-rail-actions-col">
      <div class="vb-rail-actions">
        <!-- base option list is shared with the legacy page; only the first
             label is re-cased client-side (see baseOptions) -->
        <b-form-select
          v-if="baseOptions.length > 2"
          v-model="selected_base"
          :options="baseOptions"
          size="sm"
          class="vb-base-select"
          @change="load_data()"
        />
        <!-- auto-refresh toggle: a live dot when on. the board is a wall
             surface, so this defaults on at the legacy standalone cadence -->
        <b-button
          size="sm"
          class="vb-auto-btn"
          :class="{ 'vb-auto-btn--on': auto_refresh }"
          variant="outline-secondary"
          :title="autoRefreshTitle"
          :aria-pressed="auto_refresh ? 'true' : 'false'"
          aria-label="Toggle auto-refresh"
          @click="toggleAutoRefresh"
        >
          <span class="vb-auto-dot" :class="{ 'vb-auto-dot--live': auto_refresh }" aria-hidden="true"></span>
          Auto
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          title="Print this board"
          aria-label="Print this board"
          @click="printPage"
        >
          <i class="fa fa-print"></i>
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          title="About this board"
          aria-label="About this board"
          @click="show_info = true"
        >
          <i class="fa fa-info-circle"></i>
        </b-button>
        <!-- tv display entry: desktop chrome only (the overlay is a wall
             surface, not a phone one); kiosks boot via ?display= instead -->
        <b-button
          v-if="!mobileView"
          size="sm"
          variant="outline-secondary"
          title="TV display"
          aria-label="Open the TV display"
          @click="enterDisplay()"
        >
          <i class="fa fa-television"></i>
        </b-button>
      </div>
      <div v-if="updatedAt" class="vb-rail-updated">updated {{ updatedAt }}</div>
      </div>
    </div>

    <!-- print-only header so a printed board is self-documenting (the rail
         actions and filters are hidden on paper) -->
    <div class="vb-print-meta" aria-hidden="true">
      <span class="vb-print-meta-title">Vehicle Status Manager<span v-if="contextTenantName"> &middot; {{ contextTenantName }}</span></span>
      <span v-if="summary" class="vb-print-meta-sub">{{ filteredItems.length }} of {{ summary.total_cnt }} vehicles<span v-if="summary.timezone"> &middot; {{ summary.timezone }}</span></span>
      <span class="vb-print-meta-when">Printed {{ printedStamp }}</span>
    </div>

    <!-- load failures surface inline so data already on screen stays put -->
    <div v-if="error" class="vb-error">
      <span>{{ error }}</span>
      <b-button size="sm" variant="outline-secondary" @click="load_data()">Retry</b-button>
    </div>

    <!-- the api sources the roster from the telematics feed, so a fleet
         with no connected provider comes back 404/empty. state that plainly
         instead of firing the retryable error banner. -->
    <div v-if="noData" class="vb-no-telematics">
      <i class="fa fa-plug" aria-hidden="true"></i>
      <div class="vb-no-telematics-title">No vehicle data for this fleet</div>
      <div class="vb-no-telematics-sub">Nothing is reporting here yet. If this tenant has no telematics integration, switch to one with a connected fleet (Samsara, Zonar, and the like) to preview vehicle status.</div>
    </div>

    <template v-else>
    <!-- filter chips: a standalone available chip (wash and maint both
         ready) leads, then two multi-select groups. available ORs with
         the wash group and the two groups AND together. counts always
         read from the unfiltered set so they stay stable while
         filtering. -->
    <div class="vb-chip-filters">
      <!-- on mobile this scroller is one swipeable pill row under the sticky
           search; on desktop it wraps inline to the left of the search -->
      <div class="vb-chip-scroll">
      <button
        type="button"
        :class="['vb-chip', 'vb-fc-green', { 'vb-chip--active': wash_filters.includes('available') }]"
        :aria-pressed="wash_filters.includes('available') ? 'true' : 'false'"
        @click="toggleWashFilter('available')"
      >Available ({{ countAvailable }})</button>
      <div class="vb-chip-group" role="group" aria-label="Wash status filters">
        <span class="vb-chip-group-label">Wash</span>
        <button
          v-for="chip in washChips"
          :key="'w-' + chip.value"
          type="button"
          :class="['vb-chip', 'vb-fc-' + chip.color, { 'vb-chip--active': wash_filters.includes(chip.value) }]"
          :aria-pressed="wash_filters.includes(chip.value) ? 'true' : 'false'"
          @click="toggleWashFilter(chip.value)"
        >{{ chip.label }} ({{ chip.count }})</button>
      </div>
      <div class="vb-chip-group" role="group" aria-label="Maintenance status filters">
        <span class="vb-chip-group-label">Maint</span>
        <button
          v-for="chip in maintChips"
          :key="'m-' + chip.value"
          type="button"
          :class="['vb-chip', 'vb-fc-' + chip.color, { 'vb-chip--active': maint_filters.includes(chip.value) }]"
          :aria-pressed="maint_filters.includes(chip.value) ? 'true' : 'false'"
          @click="toggleMaintFilter(chip.value)"
        >{{ chip.label }} ({{ chip.count }})</button>
      </div>
      </div>
      <!-- tags + search share one row. the wrapper is display:contents on
           desktop (so the filter row lays out exactly as before), and becomes a
           flex row only at mobile widths so the two pack onto a single line. -->
      <div class="vb-tag-search-row">
      <!-- tags are tenant-defined and open-ended, so a compact menu keeps the
           pill rail stable where the bounded wash/maint enums use inline chips.
           sits outside .vb-chip-scroll so its menu isn't clipped by the row's
           overflow; hidden entirely when the feed carries no tags. -->
      <b-dropdown
        v-if="tagChips.length"
        size="sm"
        variant="outline-secondary"
        class="vb-tag-dd"
        no-caret
        menu-class="vb-tag-menu"
      >
        <template slot="button-content">
          <i class="fa fa-tag" aria-hidden="true"></i>
          Tags<span v-if="tag_filters.length" class="vb-tag-count">{{ tag_filters.length }}</span>
        </template>
        <b-dropdown-form class="vb-tag-form">
          <label
            v-for="chip in tagChips"
            :key="'t-' + chip.value"
            class="vb-tag-opt"
          >
            <input
              type="checkbox"
              :checked="tag_filters.includes(chip.value)"
              @change="toggleTagFilter(chip.value)"
            />
            <span class="vb-tag-opt-label">{{ chip.label }}</span>
            <span class="vb-tag-opt-count">{{ chip.count }}</span>
          </label>
        </b-dropdown-form>
      </b-dropdown>
      <!-- free-text find across vehicle name + driver(s) + status/base/id,
           mirroring the legacy board's search -->
      <div class="vb-search">
        <i class="fa fa-search vb-search-icon" aria-hidden="true"></i>
        <input
          ref="searchInput"
          v-model="search"
          type="text"
          class="vb-search-input"
          placeholder="Find vehicle, driver, or note"
          aria-label="Find vehicle, driver, or note"
        />
        <button v-if="search" type="button" class="vb-search-clear" aria-label="Clear search" @click="clearSearch"><i class="fa fa-times" aria-hidden="true"></i></button>
      </div>
      </div>
    </div>

    <!-- result count sits with the list it describes: it tracks the active
         filters, and the timezone labels the times shown in the rows below -->
    <div v-if="summary" class="vb-results-meta">
      <span class="vb-results-count">{{ filteredItems.length }} of {{ summary.total_cnt }} vehicles</span>
      <span v-if="summary.timezone" class="vb-results-tz">times in {{ summary.timezone }}</span>
    </div>

    <div v-if="!mobileView" class="vb-card">
      <b-table
        :busy="loading"
        :items="filteredItems"
        :fields="visibleFields"
        :sort-compare="tableSortCompare"
        :tbody-tr-class="rowClass"
        responsive
        show-empty
      >

        <template slot="table-busy">
          <div class="vb-busy"><i class="fa fa-spinner fa-spin"></i>&nbsp;Loading vehicles...</div>
        </template>

        <template slot="empty">
          <div class="vb-empty">
            <template v-if="filtersActive || searchActive">
              No vehicles match.
              <button type="button" class="vb-clear-btn" @click="clearFilters">Clear</button>
            </template>
            <template v-else>No vehicle data yet.</template>
          </div>
        </template>

        <!-- expand button: fetches the status history on open, then
             toggles the details row -->
        <template slot="details" slot-scope="row">
          <button
            type="button"
            class="vb-expand-btn"
            :class="{ 'vb-expand-btn--open': expanded.includes(row.item.name) }"
            aria-label="Show status history"
            :aria-expanded="expanded.includes(row.item.name) ? 'true' : 'false'"
            @click="toggleHistory(row)"
          >
            <i class="fa fa-chevron-right"></i>
          </button>
        </template>

        <template slot="name" slot-scope="row">
          <a class="vb-vehicle-link" target="_blank" :href="'/#/vehicle-track/' + tenant + '/' + row.item.name">{{ row.item.name }}</a>
        </template>

        <!-- wash status: chip pill; click opens the anchored picker for
             roles that may write -->
        <template slot="vehicle_status" slot-scope="row">
          <button
            v-if="canEditWash"
            type="button"
            class="vb-chip-btn"
            :class="{ 'vb-chip-btn--active': editing_cell === cellKey(row.item, 'vehicle_status') }"
            title="Update wash status"
            aria-haspopup="menu"
            :aria-expanded="editing_cell === cellKey(row.item, 'vehicle_status') ? 'true' : 'false'"
            :aria-label="'Change wash status for ' + row.item.name"
            :data-status-trigger="cellKey(row.item, 'vehicle_status')"
            @click="startEdit(row.item, 'vehicle_status', $event)"
          ><span class="vb-status-chip vb-status-chip--trigger" :class="washChipClass(row.item.vehicle_status)">{{ washChipLabel(row.item.vehicle_status) }}<i class="fa fa-caret-down vb-status-caret" aria-hidden="true"></i></span></button>
          <span v-else class="vb-status-chip" :class="washChipClass(row.item.vehicle_status)">{{ washChipLabel(row.item.vehicle_status) }}</span>
        </template>

        <!-- maintenance status: same pattern, different gate + options -->
        <template slot="maintenance_status" slot-scope="row">
          <button
            v-if="canEditMaint"
            type="button"
            class="vb-chip-btn"
            :class="{ 'vb-chip-btn--active': editing_cell === cellKey(row.item, 'maintenance_status') }"
            title="Update maintenance status"
            aria-haspopup="menu"
            :aria-expanded="editing_cell === cellKey(row.item, 'maintenance_status') ? 'true' : 'false'"
            :aria-label="'Change maint status for ' + row.item.name"
            :data-status-trigger="cellKey(row.item, 'maintenance_status')"
            @click="startEdit(row.item, 'maintenance_status', $event)"
          ><span class="vb-status-chip vb-status-chip--trigger" :class="maintChipClass(row.item.maintenance_status)">{{ maintChipLabel(row.item.maintenance_status) }}<i class="fa fa-caret-down vb-status-caret" aria-hidden="true"></i></span></button>
          <span v-else class="vb-status-chip" :class="maintChipClass(row.item.maintenance_status)">{{ maintChipLabel(row.item.maintenance_status) }}</span>
        </template>

        <template slot="notes" slot-scope="row">
          <!-- standalone is read-only end to end: the note save goes through
               the authenticated endpoint, and a 403 there clears local
               storage and bounces to login -->
          <b-form-input
            v-if="!standalone"
            v-model="row.item.notes"
            size="sm"
            class="vb-notes-input"
            :class="{ 'vb-notes-input--saved': notes_saved === row.item.name }"
            :aria-label="'Notes for ' + row.item.name"
            @focus="noteFocus(row.item)"
            @blur="saveNotes(row.item)"
          />
          <span v-else>{{ row.item.notes || '-' }}</span>
        </template>

        <!-- departs-in: red is reserved for actual lateness, a sub-24h
             countdown is normal operations and stays default ink -->
        <template slot="next_mvmt_delta" slot-scope="row">
          <span :class="{ 'vb-late': departUrgency(row.item) === 'late', 'vb-soon': departUrgency(row.item) === 'soon' }">{{ formatDelta(row.item.next_mvmt_delta) || '-' }}</span>
          <span
            v-for="(nm, idx) in row.item.next_mvmt_list || []"
            :key="'nd-' + idx"
            class="vb-stack-line"
            :class="{ 'vb-late': isLate(nm.next_mvmt_delta) }"
          >{{ formatDelta(nm.next_mvmt_delta) }}</span>
        </template>

        <template slot="next_mvmt_time" slot-scope="row">
          {{ formatStamp(row.item.next_mvmt_time) }}
          <span
            v-for="(nm, idx) in row.item.next_mvmt_list || []"
            :key="'nt-' + idx"
            class="vb-stack-line"
          >{{ formatStamp(nm.next_mvmt_time) }}</span>
        </template>

        <!-- driver/id/return stack each upcoming booking on its own line, the
             way the standard vsm does, so a multi-movement row reads as one
             aligned line per booking across every next-movement column -->
        <template slot="next_mvmt_driver" slot-scope="row">
          {{ formatDrivers(row.item.next_mvmt_driver) }}
          <span
            v-for="(nm, idx) in row.item.next_mvmt_list || []"
            :key="'ndr-' + idx"
            class="vb-stack-line"
          >{{ formatDrivers(nm.next_mvmt_driver) }}</span>
        </template>

        <template slot="next_mvmt_id" slot-scope="row">
          {{ row.item.next_mvmt_id || '-' }}
          <span
            v-for="(nm, idx) in row.item.next_mvmt_list || []"
            :key="'nid-' + idx"
            class="vb-stack-line"
          >{{ nm.next_mvmt_id }}</span>
        </template>

        <template slot="next_mvmt_return_time" slot-scope="row">
          {{ formatStamp(row.item.next_mvmt_return_time) }}
          <span
            v-for="(nm, idx) in row.item.next_mvmt_list || []"
            :key="'nrt-' + idx"
            class="vb-stack-line"
          >{{ formatStamp(nm.next_mvmt_return_time) }}</span>
        </template>
        <template slot="current_mvmt_return_time" slot-scope="row">{{ formatStamp(row.item.current_mvmt_return_time) }}</template>

        <!-- the last_* stamps only exist once a vehicle_info row does,
             mirroring the legacy guards -->
        <template slot="last_dirty_time" slot-scope="row">
          <span v-if="row.item.vehicle_info">{{ formatStamp(row.item.last_dirty_time) }}</span>
        </template>
        <template slot="last_cleaned_time" slot-scope="row">
          <span v-if="row.item.vehicle_info">{{ formatStamp(row.item.last_cleaned_time) }}</span>
        </template>
        <template slot="last_washed_time" slot-scope="row">
          <span v-if="row.item.vehicle_info">{{ formatStamp(row.item.last_washed_time) }}</span>
        </template>
        <template slot="last_returned_time" slot-scope="row">
          <span v-if="row.item.vehicle_info">{{ formatStamp(row.item.last_returned_time) }}</span>
        </template>

        <!-- wheelchair / stay-and-do / tote render read-only here: the cm
             api has no tote write path and the legacy checkbox handler is
             broken, so the preview does not pretend they are editable -->
        <template slot="wheelchair" slot-scope="row">{{ row.item.wheelchair ? 'Yes' : '-' }}</template>
        <template slot="stayanddo" slot-scope="row">{{ row.item.stayanddo ? 'Yes' : '-' }}</template>
        <template slot="tote" slot-scope="row">{{ row.item.tote_label || (row.item.tote ? 'Yes' : '-') }}</template>

        <!-- status change history, fetched on expand. each entry renders as a
             grid row whose tracks are measured off the parent header cells
             (measureHistCols), so the stamp + author sit in the vehicle
             column's space, the wash/maint chips line up under their own
             columns, and the note lands in the next column over: the
             expansion reads as more rows of the parent table -->
        <template slot="row-details" slot-scope="row">
          <div class="vb-history">
            <div v-if="row.item.history_error" class="vb-history-error">
              History did not load.
              <button type="button" class="vb-clear-btn" @click="fetchHistory(row.item.name)">Retry</button>
            </div>
            <div v-else-if="row.item.history && row.item.history.length">
              <div
                v-for="(h, idx) in row.item.history"
                :key="idx"
                class="vb-hist2-row"
                :class="histRowClass(h)"
              >
                <span class="vb-hist2-when-cell">
                  <span class="vb-hist-when"><span class="vb-sr">Updated </span>{{ formatStamp(h.processed_time) }}</span>
                  <span class="vb-hist-by"><span class="vb-sr">by </span>{{ h.updated_by || '-' }}</span>
                </span>
                <span class="vb-hist2-cell"><span class="vb-status-chip" :class="[washChipClass(h.vehicle_status), { 'vb-status-chip--dim': histDim(row.item.history, idx, 'vehicle_status') }]"><span class="vb-sr">Wash status: </span>{{ statusLabel(h.vehicle_status) || '-' }}</span></span>
                <span class="vb-hist2-cell"><span class="vb-status-chip" :class="[maintChipClass(h.maintenance_status), { 'vb-status-chip--dim': histDim(row.item.history, idx, 'maintenance_status') }]"><span class="vb-sr">Maint status: </span>{{ statusLabel(h.maintenance_status) || '-' }}</span></span>
                <span v-if="h.notes" class="vb-hist2-cell vb-hist2-note" :class="{ 'vb-hist-note--dim': histDim(row.item.history, idx, 'notes') }"><i v-if="!histDim(row.item.history, idx, 'notes')" class="fa fa-sticky-note-o vb-hist-note-icon" aria-hidden="true"></i><span class="vb-sr">Note: </span>{{ h.notes }}</span>
                <span v-else-if="histNoteCleared(row.item.history, idx)" class="vb-hist2-cell vb-hist2-note vb-hist-note--dim vb-hist-note--cleared">Note cleared</span>
              </div>
            </div>
            <div v-else-if="row.item.history" class="vb-history-empty">No status history yet.</div>
            <div v-else class="vb-history-empty">Loading history...</div>
          </div>
        </template>

      </b-table>
    </div>

    <!-- mobile card layout: same data priority as the 3-column legacy
         phone view (identity, status, urgency) but built as cards so the
         secondary columns expand in place instead of scrolling off a
         shrunken table -->
    <div v-else class="vb-card-list">
      <div v-if="loading" class="vb-busy"><i class="fa fa-spinner fa-spin"></i>&nbsp;Loading vehicles...</div>
      <div v-else-if="!filteredItems.length" class="vb-empty">
        <template v-if="filtersActive || searchActive">No vehicles match.<button type="button" class="vb-clear-btn" @click="clearFilters">Clear</button></template>
        <template v-else>No vehicle data yet.</template>
      </div>
      <div v-for="item in filteredItems" :key="item.name" class="vb-vcard" :class="'vb-vcard--' + rowState(item)" @click="cardTap(item, $event)">
        <!-- two columns: expander | (name over chips) | departs. the desktop
             table's column order still reads left-to-right, but the name gets
             a row of its own instead of being the one element on a single line
             allowed to shrink - the fixed expander, two min-width chips and an
             unshrinkable depart deck spent ~258px before the vehicle number
             got a pixel, so it ellipsised first on anything under ~390px. -->
        <div class="vb-vcard-line">
          <!-- tapping anywhere on the card opens the detail sheet (cardTap
               ignores clicks on interactive children), so the effective touch
               target is the whole card - the 36px box below stops being the
               only way in. the chevron stays as the visible affordance and
               the keyboard / screen-reader entry point. -->
          <button
            type="button"
            class="vb-expand-btn"
            aria-haspopup="dialog"
            aria-label="Show details"
            :aria-expanded="sheet_name === item.name ? 'true' : 'false'"
            @click="openSheet(item)"
          >
            <i class="fa fa-chevron-right"></i>
          </button>

          <div class="vb-vcard-body">
            <a class="vb-vehicle-link" target="_blank" :href="'/#/vehicle-track/' + tenant + '/' + item.name">{{ item.name }}</a>

            <!-- wash + maint chips, captionless like the table row; an unset
                 editable status keeps the ghost "Set" placeholder -->
            <div class="vb-vcard-chiprow">
              <button
                v-if="canEditWash"
                type="button"
                class="vb-chip-btn"
                :class="{ 'vb-chip-btn--active': editing_cell === cellKey(item, 'vehicle_status') }"
                aria-haspopup="menu"
                :aria-expanded="editing_cell === cellKey(item, 'vehicle_status') ? 'true' : 'false'"
                :aria-label="'Change wash status for ' + item.name"
                :data-status-trigger="cellKey(item, 'vehicle_status')"
                @click="startEdit(item, 'vehicle_status', $event)"
              ><span class="vb-status-chip vb-status-chip--trigger" :class="[washChipClass(item.vehicle_status), { 'vb-status-chip--set': !item.vehicle_status }]">{{ item.vehicle_status ? washChipLabel(item.vehicle_status) : 'Set' }}<i class="fa fa-caret-down vb-status-caret" aria-hidden="true"></i></span></button>
              <!-- read-only chips carry their field name as hidden text + a title:
                   without the table's column headers, two unset chips would
                   otherwise be indistinguishable "-" boxes -->
              <span v-else class="vb-status-chip" :class="washChipClass(item.vehicle_status)" title="Wash status"><span class="vb-sr">Wash status: </span>{{ washChipLabel(item.vehicle_status) }}</span>

              <button
                v-if="canEditMaint"
                type="button"
                class="vb-chip-btn"
                :class="{ 'vb-chip-btn--active': editing_cell === cellKey(item, 'maintenance_status') }"
                aria-haspopup="menu"
                :aria-expanded="editing_cell === cellKey(item, 'maintenance_status') ? 'true' : 'false'"
                :aria-label="'Change maint status for ' + item.name"
                :data-status-trigger="cellKey(item, 'maintenance_status')"
                @click="startEdit(item, 'maintenance_status', $event)"
              ><span class="vb-status-chip vb-status-chip--trigger" :class="[maintChipClass(item.maintenance_status), { 'vb-status-chip--set': !item.maintenance_status }]">{{ item.maintenance_status ? maintChipLabel(item.maintenance_status) : 'Set' }}<i class="fa fa-caret-down vb-status-caret" aria-hidden="true"></i></span></button>
              <span v-else class="vb-status-chip" :class="maintChipClass(item.maintenance_status)" title="Maint status"><span class="vb-sr">Maint status: </span>{{ maintChipLabel(item.maintenance_status) }}</span>
            </div>

            <!-- aux row: the tenant's remaining configured mobile fields
                 promoted onto the card face (config order, empties skipped,
                 capped at 3). current return + return eta moved up into the
                 schedule deck (ticket 1566), so this only renders for tenants
                 with other secondary fields configured. the 4px top margin
                 keeps 18px between this text and the chips' 44px tap boxes
                 (which overhang their row by 14px), so an aux tap opens the
                 sheet instead of a status picker. -->
            <div v-if="auxPairs(item).length" class="vb-vcard-auxrow">
              <span v-for="p in auxPairs(item)" :key="p.key" class="vb-aux-pair">
                <span class="vb-aux-label">{{ p.label }}</span>
                <span class="vb-aux-val">{{ p.value }}</span>
              </span>
            </div>
          </div>

          <!-- depart deck pinned right: countdown with the precise stamp
               tucked under it (the fixed landmark a relative "19h 13m" can't
               give); absent entirely for an idle vehicle -->
          <div v-if="item.next_mvmt_delta" class="vb-vcard-depart">
            <div class="vb-vcard-depart-main">
              <span class="vb-vcard-depart-label">Departs in</span>
              <span
                class="vb-vcard-depart-val"
                :class="{
                  'vb-late': departUrgency(item) === 'late',
                  'vb-soon': departUrgency(item) === 'soon',
                  'vb-vcard-depart-val--distant': departIsDistant(item)
                }"
              >{{ formatDelta(item.next_mvmt_delta) }}</span>
            </div>
            <span v-if="departStamp(item)" class="vb-vcard-depart-when">{{ departStamp(item) }}</span>
          </div>
        </div>

        <!-- return band (ticket 1566): the current movement's return gets its
             own full-width strip under the main row - schedule left, live eta
             right - instead of competing with the depart deck for the same
             corner. the tinted base doubles as the scan cue for "this card
             has a return". plain text; taps fall through to cardTap, same
             rule as the aux row. absent entirely (no reserved space) for the
             majority of cards that carry no current movement. -->
        <div v-if="returnBandStamp(item) || item.current_mvmt_return_eta" class="vb-vcard-return-band">
          <span v-if="returnBandStamp(item)" class="vb-band-pair">
            <span class="vb-band-label">{{ returnBandLabel(item) }}</span>
            <span
              class="vb-band-time"
              :class="{
                'vb-band-time--dim': returnBandState(item) === 'dim',
                'vb-band-time--late': returnBandState(item) === 'late'
              }"
            >{{ returnBandStamp(item) }}</span>
          </span>
          <span v-if="item.current_mvmt_return_eta" class="vb-band-pair vb-band-pair--eta">
            <span class="vb-band-label">ETA</span>
            <span class="vb-band-eta">{{ item.current_mvmt_return_eta }}</span>
          </span>
        </div>
      </div>
    </div>
    </template>

    <!-- card detail bottom sheet: replaces the old in-card expansion. one
         sheet for the board, resolved by name so a background auto-refresh
         keeps its content live (and closes it if the vehicle leaves the
         feed). fixed positioning escapes the card list the same way the
         status picker does. -->
    <transition name="vb-sheet">
      <div v-if="sheetItem" class="vb-sheet-wrap" @click.self="closeSheet">
        <div class="vb-sheet" role="dialog" aria-modal="true" :aria-label="'Details for ' + sheetItem.name">
          <div class="vb-sheet-grab" aria-hidden="true"></div>
          <div class="vb-sheet-head">
            <a class="vb-vehicle-link vb-sheet-title" target="_blank" :href="'/#/vehicle-track/' + tenant + '/' + sheetItem.name">{{ sheetItem.name }}</a>
            <button ref="sheetClose" type="button" class="vb-sheet-close" aria-label="Close details" @click="closeSheet"><i class="fa fa-times" aria-hidden="true"></i></button>
          </div>
          <div v-for="f in mobileSecondaryFields" :key="f.key" class="vb-vcard-row">
            <span class="vb-vcard-row-label">{{ f.label }}</span>
            <!-- empty values go faint so the set ones carry the column -->
            <span class="vb-vcard-row-val" :class="{ 'vb-vcard-row-val--empty': cellDisplay(sheetItem, f.key) === '-' }">{{ cellDisplay(sheetItem, f.key) }}</span>
          </div>
          <div class="vb-vcard-row vb-vcard-notes">
            <span class="vb-vcard-row-label">Notes</span>
            <!-- read-only in standalone, same reason as the table's notes cell -->
            <b-form-input
              v-if="!standalone"
              v-model="sheetItem.notes"
              size="sm"
              class="vb-notes-input"
              :class="{ 'vb-notes-input--saved': notes_saved === sheetItem.name }"
              :aria-label="'Notes for ' + sheetItem.name"
              @focus="noteFocus(sheetItem)"
              @blur="saveNotes(sheetItem)"
            />
            <span v-else class="vb-vcard-row-val">{{ sheetItem.notes || '-' }}</span>
          </div>
          <!-- history as stacked entries so it never overflows the sheet the
               way the desktop table would on a phone. entries carry the same
               per-snapshot rails as the desktop expansion, and values that
               match the previous (older) snapshot render dimmed so the list
               reads as "what changed when" instead of a wall of repeats -->
          <div class="vb-vcard-history">
            <div class="vb-hist-head">Status history</div>
            <div v-if="sheetItem.history_error" class="vb-history-error">History did not load.<button type="button" class="vb-clear-btn" @click="fetchHistory(sheetItem.name)">Retry</button></div>
            <template v-else-if="sheetItem.history && sheetItem.history.length">
              <div v-for="(h, idx) in sheetItem.history" :key="idx" class="vb-hist-entry" :class="histEntryClass(h)">
                <!-- one line per entry, mirroring the desktop expansion's
                     stamp | wash | maint | author read: the chips sit in
                     fixed tracks so they land in columns down the drawer
                     instead of stacking under the stamp. chips stay
                     title-captioned: two adjacent captionless chips are
                     ambiguous, and more so since the maint labels dropped
                     their "DVIR" suffix - a bare "Pending" beside a wash
                     chip otherwise reads as pending a wash -->
                <div class="vb-hist-line">
                  <span class="vb-hist-when">{{ formatStamp(h.processed_time) }}</span>
                  <span class="vb-status-chip" :class="[washChipClass(h.vehicle_status), { 'vb-status-chip--dim': histDim(sheetItem.history, idx, 'vehicle_status') }]" title="Wash status"><span class="vb-sr">Wash status: </span>{{ statusLabel(h.vehicle_status) || '-' }}</span>
                  <span class="vb-status-chip" :class="[maintChipClass(h.maintenance_status), { 'vb-status-chip--dim': histDim(sheetItem.history, idx, 'maintenance_status') }]" title="Maint status"><span class="vb-sr">Maint status: </span>{{ statusLabel(h.maintenance_status) || '-' }}</span>
                  <span class="vb-hist-by">{{ h.updated_by || '-' }}</span>
                </div>
                <div v-if="h.notes" class="vb-hist-note" :class="{ 'vb-hist-note--dim': histDim(sheetItem.history, idx, 'notes') }">
                  <i v-if="!histDim(sheetItem.history, idx, 'notes')" class="fa fa-sticky-note-o vb-hist-note-icon" aria-hidden="true"></i>{{ h.notes }}
                </div>
                <div v-else-if="histNoteCleared(sheetItem.history, idx)" class="vb-hist-note vb-hist-note--dim vb-hist-note--cleared">Note cleared</div>
              </div>
            </template>
            <div v-else-if="sheetItem.history" class="vb-history-empty">No status history yet.</div>
            <div v-else class="vb-history-empty">Loading history...</div>
          </div>
        </div>
      </div>
    </transition>

    <!-- legend + how-to-read help. the key is drawn with the same chips and
         colors the board renders, so it always matches what is on screen.
         the triage-rail section is new to this board (the legacy vsm had no
         row coloring to explain). -->
    <b-modal v-model="show_info" title="About this board" size="lg" ok-only ok-title="Got it" hide-header-close>
      <div class="vb-info">
        <section class="vb-info-sec">
          <h6 class="vb-info-h">Reading the board</h6>
          <p class="vb-info-p">A colored edge marks rows that need a look. The most urgent reason wins.</p>
          <ul class="vb-info-legend">
            <li>
              <span class="vb-info-dot vb-info-dot--late"></span>
              <span><strong>Late or out of service.</strong> Overdue to depart, out of service, or an unsafe DVIR. Needs action now.</span>
            </li>
            <li>
              <span class="vb-info-dot vb-info-dot--attention"></span>
              <span><strong>Heads up.</strong> Leaving soon, dirty, in PM, or a pending DVIR.</span>
            </li>
            <li>
              <span class="vb-info-dot vb-info-dot--ready"></span>
              <span><strong>Available.</strong> Clean and ready to roll.</span>
            </li>
          </ul>
        </section>

        <section class="vb-info-sec">
          <h6 class="vb-info-h">Wash status</h6>
          <ul class="vb-info-chips">
            <li v-for="row in washLegend" :key="'wl-' + row.value">
              <span class="vb-status-chip" :class="washChipClass(row.value)">{{ washChipLabel(row.value) }}</span>
              <span class="vb-info-desc">{{ row.desc }}</span>
            </li>
          </ul>
        </section>

        <section class="vb-info-sec">
          <h6 class="vb-info-h">Maintenance status</h6>
          <ul class="vb-info-chips">
            <li v-for="row in maintLegend" :key="'ml-' + row.value">
              <span class="vb-status-chip" :class="maintChipClass(row.value)">{{ maintChipLabel(row.value) }}</span>
              <span class="vb-info-desc">{{ row.desc }}</span>
            </li>
          </ul>
        </section>

        <section class="vb-info-sec">
          <h6 class="vb-info-h">Filters &amp; refresh</h6>
          <p class="vb-info-p"><strong>Available</strong> counts vehicles that are {{ washedEnabled ? 'ready' : 'clean' }} and have a ready maintenance status.</p>
          <p class="vb-info-p">With auto-refresh on, the board reloads every {{ refresh_secs }} seconds. It holds when the tab is in the background or while you are editing a status or note, so nothing shifts under your hands.</p>
        </section>
      </div>
    </b-modal>

    <!-- one floating status picker for the whole board, anchored to the
         chip that opened it. position: fixed so it escapes the table's
         overflow scrollport and never reflows the neighboring chip. -->
    <div
      v-if="editing_cell"
      ref="pickerPop"
      class="vb-picker-pop"
      :style="picker_style"
      role="menu"
      :aria-label="picker_field === 'maintenance_status' ? 'Set maintenance status' : 'Set wash status'"
      @keydown.esc="cancelEdit(true)"
    >
      <button
        v-for="opt in pickerOptions"
        :key="opt.value"
        type="button"
        class="vb-status-opt"
        :class="{ 'vb-status-opt--current': picker_item && picker_item[picker_field] === opt.value }"
        role="menuitemradio"
        :aria-checked="picker_item && picker_item[picker_field] === opt.value ? 'true' : 'false'"
        @click="setStatus(picker_item, picker_field, opt.value)"
      ><span class="vb-status-chip" :class="pickerChipClass(opt.value)">{{ opt.text }}</span><i v-if="picker_item && picker_item[picker_field] === opt.value" class="fa fa-check vb-status-opt-check" aria-hidden="true"></i></button>
    </div>

    <!-- tv display overlay: a fixed presentation surface for wall tvs.
         entered from the tv button or a ?display=<theme> kiosk bookmark;
         esc or the auto-hiding control pill exits. sibling to the sheet
         and modals so it renders even while noData/error is showing. -->
    <div
      v-if="display_mode"
      ref="display_root"
      :class="['vb-display', 'vb-display--' + display_theme, display_theme === 'outrun' ? 'vb-display--sky-' + effectiveDisplaySky : '', { 'vb-display--idle': display_idle, 'vb-display--ss-dark': isScreensaverDisplay && display_ss_palette === 'dark', 'vb-display--phos-green': display_theme === 'ember' && display_phosphor === 'green' }]"
      role="region"
      aria-label="Vehicle status display"
      @mousemove="wakeControls"
      @click="wakeControls"
    >
      <!-- screen saver: brand orbs drift behind the board (the frame sits
           above them at z-index 1) -->
      <div v-if="isScreensaverDisplay" class="vb-ss-orbs" aria-hidden="true">
        <div class="vb-ss-orb vb-ss-orb--1"></div>
        <div class="vb-ss-orb vb-ss-orb--2"></div>
        <div class="vb-ss-orb vb-ss-orb--3"></div>
        <div class="vb-ss-orb vb-ss-orb--4"></div>
        <div class="vb-ss-orb vb-ss-orb--5"></div>
      </div>
      <!-- outrun: sunset sky, striped arcade sun, and a perspective grid
           floor rolling toward the viewer (all behind the z-1 frame) -->
      <div v-if="isOutrunDisplay" :key="'or-scene-' + outrunSceneryOn" class="vb-or-scene" aria-hidden="true">
        <div class="vb-or-stars"></div>
        <!-- day sky only: two-layer 16-bit parallax clouds -->
        <div class="vb-or-clouds"></div>
        <!-- day sky only: a gull flock crossing the open sky -->
        <div class="vb-or-gulls"></div>
        <!-- scenery layer (every sky except classic dusk, which stays the
             original scene verbatim): mountain ridges on the seam, a road
             riding the grid plane, and palm silhouettes framing the board -->
        <div v-if="outrunSceneryOn" class="vb-or-ridge-far"></div>
        <div class="vb-or-sun"></div>
        <template v-if="outrunSceneryOn">
          <div class="vb-or-ridge-near"></div>
          <div class="vb-or-palm vb-or-palm--sun"></div>
          <div class="vb-or-palm vb-or-palm--mini"></div>
        </template>
        <div class="vb-or-ground">
          <div class="vb-or-grid"></div>
          <div v-if="outrunSceneryOn" class="vb-or-road"></div>
        </div>
        <template v-if="outrunSceneryOn">
          <div class="vb-or-palm vb-or-palm--l"></div>
          <div class="vb-or-palm vb-or-palm--r"></div>
        </template>
      </div>
      <div class="vb-dp-frame">
        <header class="vb-dp-header">
          <div class="vb-dp-head-left">
            <div v-if="displayOperatorName" class="vb-dp-operator">{{ displayOperatorName }}</div>
            <h1 class="vb-dp-title">Vehicle Status</h1>
          </div>
          <div class="vb-dp-head-right">
            <div v-if="isSolariDisplay" class="vb-dp-clock vb-dp-clock--flap"><split_flap :value="sfPad(displayClock, 8)" :length="8"/></div>
            <div v-else class="vb-dp-clock">{{ displayClock }}</div>
            <div class="vb-dp-date">{{ displayClockDate }}<span v-if="summary && summary.timezone" class="vb-dp-tz"> &middot; {{ summary.timezone }}</span></div>
          </div>
        </header>

        <div ref="display_board" class="vb-dp-board">
          <!-- solari: true animated split-flap lines; rows are keyed by
               position (not vehicle) so page flips flutter in place -->
          <template v-if="isSolariDisplay">
            <div class="vb-dp-row--head vb-sf-head" aria-hidden="true">
              <span class="vb-sf-col vb-sf-col-name"><span class="vb-sf-head-label">Vehicle</span></span>
              <span class="vb-sf-col vb-sf-col-wash"><span class="vb-sf-head-label">Wash</span></span>
              <span class="vb-sf-col vb-sf-col-maint"><span class="vb-sf-head-label">Maint</span></span>
              <span class="vb-sf-col vb-sf-col-return"><span class="vb-sf-head-label">Returns</span></span>
              <span class="vb-sf-col vb-sf-col-departs"><span class="vb-sf-head-label">Departs</span></span>
              <span class="vb-sf-col vb-sf-col-in"><span class="vb-sf-head-label">In</span></span>
            </div>
            <div class="vb-dp-rows vb-dp-rows--solari">
              <div v-for="(r, idx) in solariDisplayRows" :key="'sfr-' + idx" class="vb-sf-row">
                <span class="vb-sf-col vb-sf-col-name"><split_flap :value="r.name === '-' ? '' : r.name" :length="14"/></span>
                <span class="vb-sf-col vb-sf-col-wash" :class="'vb-dp-tone--' + r.washTone"><split_flap :value="r.washShort" :length="6"/></span>
                <span class="vb-sf-col vb-sf-col-maint" :class="'vb-dp-tone--' + r.maintTone"><split_flap :value="r.maintShort" :length="6"/></span>
                <span class="vb-sf-col vb-sf-col-return" :class="'vb-sf-return--' + r.sfReturnTone"><split_flap :value="sfJustify(r.sfReturn, 12)" :length="12"/></span>
                <span class="vb-sf-col vb-sf-col-departs"><split_flap :value="sfJustify(r.sfDepart, 12)" :length="12"/></span>
                <span class="vb-sf-col vb-sf-col-in" :class="'vb-sf-in--' + (r.departUrgency || 'none')"><split_flap :value="sfPad(r.sfDepartIn, 9)" :length="9"/></span>
              </div>
              <div v-if="!displayRows.length && !loading" class="vb-dp-quiet vb-dp-quiet--solari">
                <div class="vb-dp-quiet-title">No vehicles to show</div>
                <div class="vb-dp-quiet-sub">Waiting for vehicle data.</div>
              </div>
            </div>
          </template>

          <!-- screen saver: no pages; the roster tickers upward one row at a
               time, the leaving row flipping up behind the list and the
               entering row flipping up into the bottom slot -->
          <template v-else-if="isScreensaverDisplay">
          <div class="vb-dp-row vb-dp-row--head" aria-hidden="true">
            <span>Vehicle</span>
            <span>Wash</span>
            <span>Maint</span>
            <span>Returns</span>
            <span class="vb-dp-cell-departs">Departs</span>
          </div>
          <transition-group name="vb-ss" tag="div" class="vb-dp-rows vb-dp-rows--ss">
            <div
              v-for="r in screensaverRows"
              :key="r.key"
              class="vb-dp-row"
            >
              <span class="vb-dp-cell-name">{{ r.name }}</span>
              <span class="vb-dp-cell-status"><span v-if="r.wash" class="vb-dp-chip" :class="['vb-dp-tone--' + r.washTone, 'vb-dp-st--' + r.washRaw]">{{ r.wash }}</span></span>
              <span class="vb-dp-cell-status"><span v-if="r.maint" class="vb-dp-chip" :class="['vb-dp-tone--' + r.maintTone, 'vb-dp-st--' + r.maintRaw]">{{ r.maint }}</span></span>
              <span class="vb-dp-cell-return">
                <template v-if="r.returnTime">
                  <span v-if="r.returnLabel" class="vb-dp-return-label">{{ r.returnLabel }}</span>
                  <span class="vb-dp-return-time" :class="{ 'vb-dp-return-time--late': r.returnState === 'late', 'vb-dp-return-time--dim': r.returnState === 'dim' }">{{ r.returnTime }}</span>
                  <span v-if="r.returnEta" class="vb-dp-return-eta">ETA {{ r.returnEta }}</span>
                </template>
              </span>
              <span class="vb-dp-cell-departs" :class="{ 'vb-dp-depart--distant': r.departDistant }">
                <template v-if="r.departTime">
                  <span class="vb-dp-depart-time">{{ r.departTime }}</span>
                  <span v-if="r.departIn" class="vb-dp-depart-in" :class="'vb-dp-depart-in--' + (r.departUrgency || 'none')">{{ r.departIn }}</span>
                </template>
              </span>
            </div>
            <div v-if="!displayRows.length && !loading" :key="'ss-quiet'" class="vb-dp-quiet">
              <div class="vb-dp-quiet-title">No vehicles to show</div>
              <div class="vb-dp-quiet-sub">Waiting for vehicle data.</div>
            </div>
          </transition-group>
          </template>

          <template v-else>
          <div class="vb-dp-row vb-dp-row--head" aria-hidden="true">
            <span>Vehicle</span>
            <span>Wash</span>
            <span>Maint</span>
            <span>Returns</span>
            <span class="vb-dp-cell-departs">Departs</span>
          </div>
          <!-- keyed by page + theme + phosphor + content signature: theme
               and phosphor switches remount so the entrance choreography
               (and any var-driven keyframes safari resolved at start)
               relands fresh; unchanged refreshes never remount -->
          <div :key="'page-' + display_page + '-' + display_theme + '-' + display_phosphor + '-' + displayPageSig" class="vb-dp-rows">
            <div
              v-for="(r, idx) in currentDisplayPageRows"
              :key="r.key"
              class="vb-dp-row"
              :style="{ animationDelay: displayRowDelay(idx) }"
            >
              <!-- the incoming face: display: contents everywhere except the
                   flip themes, where it becomes a face of the rolling prism
                   (same delay as the outgoing face so both turn together) -->
              <div class="vb-dp-rowface" :style="{ animationDelay: displayRowDelay(idx) }">
              <span class="vb-dp-cell-name">{{ r.name }}</span>
              <span class="vb-dp-cell-status"><span v-if="r.wash" class="vb-dp-chip" :class="['vb-dp-tone--' + r.washTone, 'vb-dp-st--' + r.washRaw]">{{ r.wash }}</span></span>
              <span class="vb-dp-cell-status"><span v-if="r.maint" class="vb-dp-chip" :class="['vb-dp-tone--' + r.maintTone, 'vb-dp-st--' + r.maintRaw]">{{ r.maint }}</span></span>
              <span class="vb-dp-cell-return">
                <template v-if="r.returnTime">
                  <span v-if="r.returnLabel" class="vb-dp-return-label">{{ r.returnLabel }}</span>
                  <span class="vb-dp-return-time" :class="{ 'vb-dp-return-time--late': r.returnState === 'late', 'vb-dp-return-time--dim': r.returnState === 'dim' }">{{ r.returnTime }}</span>
                  <span v-if="r.returnEta" class="vb-dp-return-eta">ETA {{ r.returnEta }}</span>
                </template>
              </span>
              <span class="vb-dp-cell-departs" :class="{ 'vb-dp-depart--distant': r.departDistant }">
                <template v-if="r.departTime">
                  <span class="vb-dp-depart-time">{{ r.departTime }}</span>
                  <span v-if="r.departIn" class="vb-dp-depart-in" :class="'vb-dp-depart-in--' + (r.departUrgency || 'none')">{{ r.departIn }}</span>
                  <!-- outrun only: the departure countdown as a draining
                       fuel bar (12h runway; empty and blinking = late) -->
                  <span
                    v-if="isOutrunDisplay && r.departFrac !== null"
                    class="vb-or-fuel"
                    :class="'vb-or-fuel--' + (r.departUrgency || 'ok')"
                  ><span class="vb-or-fuel-fill" :style="{ width: Math.round(r.departFrac * 100) + '%' }"></span></span>
                </template>
              </span>
              </div>
              <!-- flip-panel outgoing face: holds the previous reading flat
                   until this slot's stagger turn, then folds away just as
                   the incoming face folds in - the board never clears
                   (midnight/showroom only) -->
              <div
                v-for="p in (isFlipDisplay && display_prev_rows && display_prev_rows[idx] ? [display_prev_rows[idx]] : [])"
                :key="'back-' + p.key"
                class="vb-dp-rowback"
                :style="{ animationDelay: displayRowDelay(idx) }"
                aria-hidden="true"
              >
                <span class="vb-dp-cell-name">{{ p.name }}</span>
                <span class="vb-dp-cell-status"><span v-if="p.wash" class="vb-dp-chip" :class="['vb-dp-tone--' + p.washTone, 'vb-dp-st--' + p.washRaw]">{{ p.wash }}</span></span>
                <span class="vb-dp-cell-status"><span v-if="p.maint" class="vb-dp-chip" :class="['vb-dp-tone--' + p.maintTone, 'vb-dp-st--' + p.maintRaw]">{{ p.maint }}</span></span>
                <span class="vb-dp-cell-return">
                  <template v-if="p.returnTime">
                    <span v-if="p.returnLabel" class="vb-dp-return-label">{{ p.returnLabel }}</span>
                    <span class="vb-dp-return-time" :class="{ 'vb-dp-return-time--late': p.returnState === 'late', 'vb-dp-return-time--dim': p.returnState === 'dim' }">{{ p.returnTime }}</span>
                    <span v-if="p.returnEta" class="vb-dp-return-eta">ETA {{ p.returnEta }}</span>
                  </template>
                </span>
                <span class="vb-dp-cell-departs" :class="{ 'vb-dp-depart--distant': p.departDistant }">
                  <template v-if="p.departTime">
                    <span class="vb-dp-depart-time">{{ p.departTime }}</span>
                    <span v-if="p.departIn" class="vb-dp-depart-in" :class="'vb-dp-depart-in--' + (p.departUrgency || 'none')">{{ p.departIn }}</span>
                  </template>
                </span>
              </div>
            </div>
            <div v-if="!displayRows.length && !loading" class="vb-dp-quiet">
              <div class="vb-dp-quiet-title">No vehicles to show</div>
              <div class="vb-dp-quiet-sub">Waiting for vehicle data.</div>
            </div>
          </div>
          </template>
        </div>

        <!-- thin progress bar telegraphs the next page flip; the 12s
             animation matches the page timer interval -->
        <div v-if="displayPageCount > 1 && !isScreensaverDisplay" class="vb-dp-rotbar" aria-hidden="true">
          <div :key="'rot-' + display_page" class="vb-dp-rotbar-fill"></div>
        </div>

        <footer class="vb-dp-footer">
          <div v-if="displayPageCount > 1 && !isScreensaverDisplay" class="vb-dp-pages">
            <button
              v-for="p in displayPageCount"
              :key="'dot-' + p"
              type="button"
              class="vb-dp-page-dot"
              :class="{ 'vb-dp-page-dot--on': p - 1 === display_page }"
              :aria-label="'Page ' + p"
              :aria-current="p - 1 === display_page ? 'true' : 'false'"
              @click="setDisplayPage(p - 1)"
            ></button>
          </div>
          <div class="vb-dp-brand">
            <span class="vb-dp-live-dot" aria-hidden="true"></span>
            Fleet Integrations<span v-if="updatedAt" class="vb-dp-updated"> &middot; updated {{ updatedAt }}</span>
          </div>
        </footer>
      </div>

      <!-- control pill fades out while the board idles; any mouse movement
           brings it back, and it stays put while the pointer is over it -->
      <div
        class="vb-dp-controls"
        :class="{ 'vb-dp-controls--hidden': !controls_visible }"
        @mouseenter="pinControls"
        @mouseleave="wakeControls"
      >
        <span class="vb-dp-controls-label">Theme</span>
        <button
          v-for="t in displayThemes"
          :key="t.value"
          type="button"
          class="vb-dp-theme-dot"
          :class="['vb-dp-theme-dot--' + t.value, { 'vb-dp-theme-dot--on': display_theme === t.value }]"
          :title="t.label"
          :aria-label="'Theme: ' + t.label"
          :aria-pressed="display_theme === t.value ? 'true' : 'false'"
          @click="setDisplayTheme(t.value)"
        ></button>
        <!-- ember only: pick the tube's phosphor, p3 amber or p1 green -->
        <template v-if="display_theme === 'ember'">
          <span class="vb-dp-controls-sep"></span>
          <span class="vb-dp-controls-label">Phosphor</span>
          <button
            v-for="p in displayPhosphors"
            :key="'phos-' + p"
            type="button"
            class="vb-dp-theme-dot"
            :class="['vb-dp-phos-dot--' + p, { 'vb-dp-theme-dot--on': display_phosphor === p }]"
            :title="p === 'amber' ? 'Amber' : 'Green'"
            :aria-label="'Phosphor: ' + p"
            :aria-pressed="display_phosphor === p ? 'true' : 'false'"
            @click="setDisplayPhosphor(p)"
          ></button>
        </template>
        <!-- outrun only: pick the sky, from the classic dusk to the
             clock-following auto and the per-pass cycle -->
        <template v-if="display_theme === 'outrun'">
          <span class="vb-dp-controls-sep"></span>
          <span class="vb-dp-controls-label">Sky</span>
          <button
            v-for="s in displaySkies"
            :key="'sky-' + s"
            type="button"
            class="vb-dp-theme-dot"
            :class="['vb-dp-sky-dot--' + s, { 'vb-dp-theme-dot--on': display_sky === s }]"
            :title="displaySkyLabel(s)"
            :aria-label="'Sky: ' + displaySkyLabel(s)"
            :aria-pressed="display_sky === s ? 'true' : 'false'"
            @click="setDisplaySky(s)"
          ></button>
        </template>
        <span class="vb-dp-controls-sep"></span>
        <button type="button" class="vb-dp-exit" @click="exitDisplay">Exit display</button>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'
// moment-timezone (not bare moment) so formatStamp can render each stamp in
// the tenant's zone; the singleton is already augmented app-wide via main.js
import moment from 'moment-timezone'
import { get_vehicle_info, get_vehicle_info_fasttrak, update_vehicle_status, get_vehicle_info_history } from '../networking/apis'
import { get_tbn_vehicle_info, tbn_update_vehicle_status } from '../networking/tbn_apis'
import { tenantContextDiffers, tenantDisplayName } from '../utils/tenant_context'
import { statusLabel, statusOptions } from '@/utils/vehicle_status_labels'
import split_flap from './split_flap.vue'

// '-' placeholder for the plain-text columns rendered by b-table's default
// cell; arrays (multi-driver) join into one line
const dash = value => {
  if (value === null || value === undefined || value === '') return '-'
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-'
  return value
}

// column catalog: keys mirrored from vehicle_status_maint.vue's fields
// array with the redesign label fixes (sentence case, shorter wash/maint
// headers). only the columns the team actually scans are sortable.
const FIELDS = [
  { key: 'details', label: '', sortable: false },
  { key: 'name', label: 'Vehicle', sortable: true },
  { key: 'vehicle_status', label: 'Wash', sortable: true },
  { key: 'maintenance_status', label: 'Maint', sortable: true },
  { key: 'fleetio_status', label: 'Fleetio status', formatter: dash },
  { key: 'rta_status', label: 'RTA status', formatter: dash },
  { key: 'rta_work_order_status', label: 'RTA WO status', formatter: dash },
  { key: 'last_dirty_time', label: 'Dirty' },
  { key: 'last_cleaned_time', label: 'Cleaned' },
  { key: 'last_cleaned_number', label: 'Cleaned #', formatter: dash },
  { key: 'last_washed_time', label: 'Washed' },
  { key: 'last_washed_number', label: 'Washed #', formatter: dash },
  { key: 'last_returned_time', label: 'Last return' },
  { key: 'current_mvmt_finish_base', label: 'Current finish', formatter: dash },
  { key: 'current_mvmt_return_time', label: 'Current return' },
  // pre-formatted drive-time string from the api ("23 mins"), not a stamp;
  // sparse by design (only OUT vehicles inside the return window get one).
  // cardinal's config selects it - without a catalog entry it silently drops.
  { key: 'current_mvmt_return_eta', label: 'Return ETA', formatter: dash },
  { key: 'next_mvmt_return_time', label: 'Next return' },
  { key: 'next_mvmt_start_base', label: 'Next start', formatter: dash },
  { key: 'next_mvmt_driver', label: 'Next driver', formatter: dash },
  { key: 'next_mvmt_id', label: 'Next ID', formatter: dash },
  { key: 'next_mvmt_time', label: 'Next depart' },
  { key: 'next_mvmt_delta', label: 'Departs in', sortable: true },
  { key: 'wheelchair', label: 'WC' },
  { key: 'stayanddo', label: 'S&D' },
  { key: 'tote', label: 'Tote' },
  { key: 'notes', label: 'Notes' },
]

// fallback column set when the tenant has not configured visible_fields:
// the status columns the surface is named for, the next-movement scan
// set, and notes
const DEFAULT_VISIBLE_KEYS = [
  'name',
  'vehicle_status',
  'maintenance_status',
  'next_mvmt_id',
  'next_mvmt_driver',
  'next_mvmt_time',
  'next_mvmt_delta',
  'notes',
]

// chip color per wash status, matching the standard VSM's hue language:
// clean = cyan, washed = sea-green, dirty = brown, out = purple, ready = green
const WASH_CHIP_CLASSES = {
  READY: 'vb-chip--green',
  CLEANED: 'vb-chip--cyan',
  WASHED: 'vb-chip--teal',
  DIRTY: 'vb-chip--brown',
  OUT: 'vb-chip--purple',
}

const MAINT_CHIP_CLASSES = {
  READY: 'vb-chip--green',
  PM: 'vb-chip--blue',
  OOS: 'vb-chip--red',
  PENDING_DVIR: 'vb-chip--amber',
  UNSAFE_DVIR: 'vb-chip--red',
}

// one-line meaning per status for the info modal legend. order mirrors the
// wash/maint flow; the washed_enabled trim is applied in the computed.
const WASH_LEGEND = [
  { value: 'READY', desc: 'cleaned and washed, ready for service (set automatically)' },
  { value: 'CLEANED', desc: 'interior has been cleaned (set by the team)' },
  { value: 'WASHED', desc: 'exterior has been washed (set by the team)' },
  { value: 'DIRTY', desc: 'back at the yard and not yet cleaned (set automatically)' },
  { value: 'OUT', desc: 'out on a booking (set automatically)' },
]
const MAINT_LEGEND = [
  { value: 'READY', desc: 'DVIRs clear, good to roll (set automatically)' },
  { value: 'PENDING_DVIR', desc: 'a DVIR is open and awaiting review (set automatically)' },
  { value: 'UNSAFE_DVIR', desc: 'an unsafe DVIR was reported (set automatically)' },
  { value: 'PM', desc: 'planned or preventative maintenance (set by the team)' },
  { value: 'OOS', desc: 'out of service (set by the team)' },
]

// auto-refresh cadence, matching the legacy standalone vsm's 60s reload
const REFRESH_SECS = 60

// next_mvmt_ms is an absolute epoch in seconds; anything at/above this
// sentinel means "no scheduled movement" (the api sorts those last)
const MS_SENTINEL = 1e15
// a departure inside this window is "soon" — amber on the card rail. the
// delta string still carries the human-readable countdown; this only drives
// the at-a-glance triage color.
const SOON_SECS = 2 * 60 * 60

// compact captions for the card's aux row: the catalog labels ("Next
// Return", "Next driver") are sized for column headers and would eat the
// row. fields without an entry fall back to their catalog label. current
// return + return eta have no entries because they render in the schedule
// deck, never the aux row.
const AUX_SHORT_LABELS = {
  next_mvmt_return_time: 'Next Return',
  last_returned_time: 'Last Return',
  next_mvmt_driver: 'Driver',
}

// tv display themes; the value doubles as the css modifier and the
// ?display= query value a kiosk bookmarks. midnight is the product's own
// dark language, showroom is the marketing site's, solari and ember are
// the departure board's flap and crt archetypes on vsm data, and
// screensaver is the linkedin campaign language with burn-in protection
// (palette swaps + a row ticker instead of pages).
const DISPLAY_THEMES = [
  { value: 'midnight', label: 'Midnight' },
  { value: 'showroom', label: 'Showroom' },
  { value: 'solari', label: 'Solari' },
  { value: 'ember', label: 'Ember' },
  { value: 'screensaver', label: 'Screen Saver' },
  { value: 'outrun', label: 'Outrun' },
]

// outrun's fuel bars grade the runway to the next departure against this
// window; anything further out is calm enough to carry no bar at all
const OUTRUN_FUEL_WINDOW_SECS = 43200

// ember's phosphor chemistry: p3 amber (the default) or p1 green, the two
// classic monochrome tubes. remembered independently of the theme.
const DISPLAY_PHOSPHOR_KEY = 'vb_display_phosphor'
const DISPLAY_PHOSPHORS = ['amber', 'green']

// outrun's sky: the classic dusk plus night and dawn grades, an auto mode
// that follows the tenant clock, and a cycle mode that rolls to the next
// grade each full pass through the pages. remembered like the phosphor.
const DISPLAY_SKY_KEY = 'vb_display_sky'
// dusk is the preserved original scene (no scenery, ever); cruise is the same
// dusk sky carrying the road/ridge/palm scenery, as do dawn/day/night
const DISPLAY_SKIES = ['dawn', 'day', 'dusk', 'cruise', 'night', 'auto', 'cycle']
// the cycle runs a whole day in order, one grade per pass. its dusk slot is
// cruise (cycle already promises a new vista each wrap); classic dusk stays a
// direct pick, and auto keeps classic dusk for its evening hours.
const DISPLAY_SKY_CYCLE = ['dawn', 'day', 'cruise', 'night']
// the skies that render the scenery layer (everything except classic dusk)
const DISPLAY_SCENERY_SKIES = ['dawn', 'day', 'cruise', 'night']
const DISPLAY_SKY_LABELS = {
  dawn: 'Dawn',
  day: 'Day',
  dusk: 'Dusk',
  cruise: 'Cruise (dusk with scenery)',
  night: 'Night',
  auto: 'Auto (follows the local time)',
  cycle: 'Cycle (rolls each pass)',
}

// midnight/showroom prism reveal: one 550ms panel roll per row, rows
// staggered at ~70% of the roll so adjacent panels overlap slightly and
// the page reads as a cascade (never slower than the ~5s envelope on
// short pages). both prism faces share the row's delay and turn together
// - keep the css durations (vb-dp-prism-in 0.55s / vb-dp-prism-out
// 0.45s) in sync with this.
const DISPLAY_REVEAL_MS = 550
const DISPLAY_REVEAL_CASCADE_MS = 5000

// screen saver cadence: one row ticks up every step, and the light/dark
// palette flips at a pass boundary once at least the swap floor has
// elapsed (every pass or every 60s, whichever runs longer)
const SS_STEP_MS = 3000
const SS_SWAP_FLOOR_MS = 60000
const DISPLAY_THEME_KEY = 'vb_display_theme'
const isDisplayTheme = value => DISPLAY_THEMES.some(t => t.value === value)

// only the flap and crt themes use webfonts; midnight/showroom stay on
// system stacks so a kiosk boot never waits on fonts.googleapis.com (and a
// firewalled kiosk degrades the retro themes to their fallback stacks only)
const DISPLAY_FONT_THEMES = { solari: true, ember: true, outrun: true }
const VSM_DISPLAY_FONTS_URL = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700&family=IBM+Plex+Mono:wght@500;600&family=Share+Tech+Mono&family=Orbitron:wght@600;800&family=Rajdhani:wght@500;600;700&display=swap'

// flap-drum-safe short labels (the drum is a-z 0-9 :./-,&' and the flap
// component uppercases on its own)
const DISPLAY_SHORT_WASH = { READY: 'READY', CLEANED: 'CLEAN', WASHED: 'WASHED', DIRTY: 'DIRTY', OUT: 'OUT' }
const DISPLAY_SHORT_MAINT = { READY: 'READY', PM: 'PM', OOS: 'OOS', PENDING_DVIR: 'DVIR', UNSAFE_DVIR: 'UNSAFE' }

export default {
  name: 'vsm_board',
  components: { split_flap },
  data() {
    return {
      loading: false,
      refreshing: false,
      error: '',
      // true after a 404/empty load — the fleet has no vehicles to show
      // (typically no telematics feed), rendered as a calm fact not an error
      noData: false,
      // true under the mobile breakpoint; swaps the table for the card list
      mobileView: false,
      summary: null,
      items_full: [],
      selected_base: '',
      wash_filters: [],
      maint_filters: [],
      tag_filters: [],
      // free-text vehicle/driver find, applied on top of the chip filters
      search: '',

      // anchored status picker: which cell's options are open (one at a
      // time), plus the row/field it targets and the fixed-position style
      // computed from the trigger chip. rendered once at the root as a
      // floating popover, so opening it never reflows the sibling chip.
      editing_cell: null,
      picker_item: null,
      picker_field: null,
      picker_style: null,
      notes_original: null,

      // vehicle name whose notes input just saved, for the brief
      // confirmation flash
      notes_saved: '',
      notes_saved_timer: null,
      // vehicle name whose notes input is currently focused; auto-refresh
      // checks this so a soft reload never stomps a half-typed note
      notes_editing: '',

      // auto-refresh: a periodic soft reload for the wall-board use case.
      // on by default at the legacy cadence; the timer is paused per-tick
      // when it would waste a call or disrupt an edit (see autoRefresh)
      auto_refresh: true,
      refresh_secs: REFRESH_SECS,
      refresh_timer: null,
      // epoch ms of the last successful load, surfaced as "updated h:mm:ss"
      last_updated: null,
      // epoch ms stamped right before window.print(), for the print header
      printed_at: null,

      // the "About this board" legend/help modal
      show_info: false,

      // vehicle names with their history panel open
      expanded: [],
      // vehicle name whose detail sheet is open (null = closed); resolved to
      // a live row via sheetItem so auto-refresh keeps the sheet current
      sheet_name: null,

      // sequence guard so only the latest in-flight fetch lands
      fetch_seq: 0,
      // the html data-fi-theme value parked while a standalone mount forces
      // the kiosk surface light (restored in beforeDestroy)
      saved_fi_theme: '',

      // tv display state
      display_mode: false,
      display_theme: 'midnight',
      display_phosphor: 'amber',
      display_sky: 'dusk',
      display_sky_pos: 0,
      display_idle: false,
      controls_visible: true,
      display_page: 0,
      display_page_size: 8,
      display_clock_now: Date.now(),
      display_minute_tick: 0,
      did_request_fullscreen: false,
      display_clock_timer: null,
      display_minute_timer: null,
      display_page_timer: null,
      display_idle_timer: null,
      display_resize_timer: null,
      // the rows shown before the latest page/refresh render: the flip
      // panels' back faces, so a page turn reads as one page changing
      // into the other rather than a cleared board
      display_prev_rows: null,
      // screen saver theme: ticker position, current burn-in palette, and
      // the epoch of the last palette swap
      display_scroll_pos: 0,
      display_ss_palette: 'light',
      display_ss_swap_at: 0,
      display_ss_timer: null,
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    // chromeless no-login mode: App.vue renders this component directly for
    // any /vsm2* path, the same pre-router interception the legacy /vsm
    // standalone boards use, and the login guard's startsWith('/vsm') bypass
    // already covers it. there is no authenticated user in this mode, so the
    // tenant rides in ?tenant= (the vsm_standalone_uuid works: the api resolves
    // either id) and the backend source in ?src= (user.types does not exist
    // logged out and vsmSource would silently default a fasttrak tenant to cm).
    standalone() {
      return this.$route.path.startsWith('/vsm2')
    },
    // standalone access is a capability url: the per-tenant
    // vsm_standalone_uuid the api auto-provisions and the info modals embed.
    // the api would also resolve a raw tenant id, but tenant ids are not
    // secrets (they ride in ?tenant= all over the authed app), so this board
    // refuses anything that is not the uuid to keep the no-login surface
    // unguessable. legacy /vsm links keep their tenant-id fallback; vsm2 is
    // a new surface with no old links to grandfather.
    standaloneIdOk() {
      if (!this.standalone) return true
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(this.tenant || ''))
    },
    tenant() {
      return this.$route.query.tenant || (this.user && this.user.tenant)
    },
    // every booking source returns the same {summary, results} contract, but
    // the read/write endpoints differ. fasttrak has a parallel route on the cm
    // api; tbn lives in its own api module. dispatch on the tenant type the
    // navbar already exposes via user.types so deep-links resolve too; an
    // explicit ?src= wins so standalone links stay correct with no user at all.
    vsmSource() {
      const src = this.$route.query.src
      if (src === 'cm' || src === 'fasttrak' || src === 'tbn') return src
      const types = this.user && this.user.types
      const type = (types && this.tenant && types[this.tenant]) || ''
      if (type === 'FASTTRAK') return 'fasttrak'
      if (type === 'TBN') return 'tbn'
      return 'cm'
    },
    // admins deep-link with ?tenant=, so name the tenant whenever it
    // differs from the navbar selection. standalone has no navbar selection
    // to differ from; name the tenant from the summary instead.
    contextTenantName() {
      if (this.standalone) return (this.summary && this.summary.tenant_name) || ''
      if (!tenantContextDiffers(this.$route, this.user)) return ''
      return tenantDisplayName(this.user, this.tenant)
    },
    washedEnabled() {
      return !!(this.summary && this.summary.washed_enabled)
    },
    baseOptions() {
      const opts = (this.summary && this.summary.bases) || []
      // client-side label fix only: the server option list is shared with
      // the legacy page and its first entry arrives as 'All Bases'
      return opts.map((opt, idx) => {
        if (idx === 0 && opt && opt.text === 'All Bases') return { ...opt, text: 'All bases' }
        return opt
      })
    },
    filtersActive() {
      return this.wash_filters.length > 0 || this.maint_filters.length > 0 || this.tag_filters.length > 0
    },
    searchActive() {
      return !!(this.search && this.search.trim())
    },
    // available mirrors the legacy available_cnt: wash READY (or CLEANED
    // when the tenant has no washed step) plus maint READY
    countAvailable() {
      const washState = this.washedEnabled ? 'READY' : 'CLEANED'
      return this.items_full.filter(item => item.vehicle_status === washState && item.maintenance_status === 'READY').length
    },
    // available renders as its own standalone chip ahead of this group,
    // so the wash group label only covers true wash statuses
    washChips() {
      // color mirrors WASH_CHIP_CLASSES so each filter pill matches its
      // status chip on the rows
      const chips = []
      if (this.washedEnabled) chips.push({ value: 'ready', label: 'Ready', color: 'green', count: this.countWash('READY') })
      chips.push({ value: 'cleaned', label: 'Clean', color: 'cyan', count: this.countWash('CLEANED') })
      if (this.washedEnabled) chips.push({ value: 'washed', label: 'Washed', color: 'teal', count: this.countWash('WASHED') })
      chips.push({ value: 'dirty', label: 'Dirty', color: 'brown', count: this.countWash('DIRTY') })
      chips.push({ value: 'out', label: 'Out', color: 'purple', count: this.countWash('OUT') })
      return chips
    },
    maintChips() {
      // color mirrors MAINT_CHIP_CLASSES
      const chips = [
        { value: 'ready', label: 'Ready', color: 'green', count: this.countMaint('READY') },
        { value: 'pm', label: 'PM', color: 'blue', count: this.countMaint('PM') },
        { value: 'oos', label: 'OOS', color: 'red', count: this.countMaint('OOS') },
      ]
      // system-written dvir statuses are not in the manual dropdown but do
      // occur on rows; chip them only when present so the counts cover the
      // whole fleet without cluttering tenants that never see them
      const pending = this.countMaint('PENDING_DVIR')
      const unsafe = this.countMaint('UNSAFE_DVIR')
      if (pending > 0) chips.push({ value: 'pending_dvir', label: statusLabel('PENDING_DVIR'), color: 'amber', count: pending })
      if (unsafe > 0) chips.push({ value: 'unsafe_dvir', label: statusLabel('UNSAFE_DVIR'), color: 'red', count: unsafe })
      return chips
    },
    // distinct tag set across the loaded fleet, sorted, with stable per-tag
    // counts from the unfiltered set. derived from the rows (not summary) so
    // it follows whatever vehicle_tags the feed actually carries; empty on
    // feeds with no tags, which collapses the tags control to nothing.
    tagChips() {
      const seen = {}
      this.items_full.forEach(item => {
        this.itemTags(item).forEach(t => { seen[t] = (seen[t] || 0) + 1 })
      })
      return Object.keys(seen).sort().map(t => ({ value: t, label: t, count: seen[t] }))
    },
    // the two chip groups AND together; within a group membership is OR,
    // with 'available' special-cased like the legacy filter_items
    filteredItems() {
      let items = this.items_full
      if (this.wash_filters.length > 0) {
        const upper = this.wash_filters.map(s => s.toUpperCase())
        const wantAvailable = this.wash_filters.includes('available')
        const washState = this.washedEnabled ? 'READY' : 'CLEANED'
        items = items.filter(item => {
          if (wantAvailable && item.vehicle_status === washState && item.maintenance_status === 'READY') return true
          return upper.includes(item.vehicle_status)
        })
      }
      if (this.maint_filters.length > 0) {
        const upper = this.maint_filters.map(s => s.toUpperCase())
        items = items.filter(item => upper.includes(item.maintenance_status))
      }
      // tag group ands with wash/maint; within the group membership is or
      // (the row carries any one of the selected tags). exact-token match so
      // "oc" never matches "oceanside".
      if (this.tag_filters.length > 0) {
        items = items.filter(item => {
          const tags = this.itemTags(item)
          return this.tag_filters.some(t => tags.indexOf(t) !== -1)
        })
      }
      if (this.searchActive) {
        const q = this.search.trim().toLowerCase()
        items = items.filter(item => this.matchesSearch(item, q))
      }
      return items
    },
    // tenant-configured column list when present, the default scan set
    // otherwise; details + name always lead, unknown keys are dropped.
    // standalone prefers the tenant's dedicated standalone list (the tv
    // field set), matching the legacy standalone boards' cascade.
    visibleFields() {
      const byKey = {}
      FIELDS.forEach(field => { byKey[field.key] = field })

      const configured = this.standalone && this.summary && this.summary.visible_standalone_fields
        ? this.summary.visible_standalone_fields
        : (this.summary && this.summary.visible_fields)
      let keys
      if (configured) {
        keys = configured.split(',').map(k => k.trim()).filter(k => byKey[k])
      } else {
        keys = DEFAULT_VISIBLE_KEYS.filter(k => byKey[k])
      }

      // the board is built around wash + maint; always surface both status
      // columns (right after name) even when the tenant's column config
      // omits them
      const lead = { details: 1, name: 1, vehicle_status: 1, maintenance_status: 1 }
      keys = keys.filter(k => !lead[k])
      return [byKey['details'], byKey['name'], byKey['vehicle_status'], byKey['maintenance_status'], ...keys.map(k => byKey[k])]
    },
    // on the card layout the line shows name + the two status chips +
    // departs-in; everything else the tenant configured drops into the
    // expandable detail, notes excluded (it renders as its own field there)
    mobileSecondaryFields() {
      // next_mvmt_delta + next_mvmt_time render on the card line (the
      // countdown and its precise stamp), and current return + return eta
      // render in the schedule deck above it, so keep all four out of the
      // expandable detail to avoid showing the same time twice
      const skip = {
        details: 1, name: 1, vehicle_status: 1, maintenance_status: 1,
        next_mvmt_delta: 1, next_mvmt_time: 1,
        current_mvmt_return_time: 1, current_mvmt_return_eta: 1,
        notes: 1,
      }
      return this.visibleFields.filter(f => !skip[f.key])
    },
    // the live row behind the open detail sheet. resolved by name each
    // render so a soft reload's in-place merge flows straight into the open
    // sheet; null (sheet closed / vehicle gone) unmounts it.
    sheetItem() {
      if (!this.sheet_name) return null
      return this.items_full.find(i => i.name === this.sheet_name) || null
    },
    // edit gates mirror the legacy page's role checks. standalone is forced
    // read-only FIRST: logged out, state.profile.user is a truthy {name:null}
    // with no role, so the role inequalities alone would fail OPEN and hand
    // anonymous visitors editable pickers whose writes then 403 into the
    // localStorage-clearing login redirect (fatal on a wall-mounted tv).
    canEditWash() {
      if (this.standalone) return false
      const role = this.user && this.user.role
      return role !== 'dispatch' && role !== 'garage'
    },
    canEditMaint() {
      if (this.standalone) return false
      const role = this.user && this.user.role
      return role !== 'dispatch' && role !== 'wash-bay'
    },
    // built fresh per render so the washed_enabled trim never mutates a
    // shared array (the legacy page filters its options in place)
    washStatusOptions() {
      const opts = statusOptions(['READY', 'CLEANED', 'WASHED', 'DIRTY', 'OUT'])
      if (!this.washedEnabled) return opts.filter(o => o.value !== 'READY' && o.value !== 'WASHED')
      return opts
    },
    maintStatusOptions() {
      return statusOptions(['READY', 'PM', 'OOS'])
    },
    // the option set for whichever cell's picker is currently open
    pickerOptions() {
      return this.picker_field === 'maintenance_status' ? this.maintStatusOptions : this.washStatusOptions
    },

    // last-load stamp in the tenant's timezone, so it reads in the same zone
    // as every other time on the board (see formatStamp)
    updatedAt() {
      if (!this.last_updated) return ''
      const tz = (this.summary && this.summary.timezone) || ''
      const m = moment(this.last_updated)
      return (tz ? m.tz(tz) : m).format('h:mm:ss a')
    },
    autoRefreshTitle() {
      return this.auto_refresh
        ? 'Auto-refresh is on, every ' + this.refresh_secs + ' seconds. Click to pause.'
        : 'Auto-refresh is off. Click to resume.'
    },
    printedStamp() {
      return this.printed_at ? moment(this.printed_at).format('MMM D, YYYY h:mm a') : ''
    },
    // legend rows for the info modal. wash honors the washed_enabled trim so
    // the key matches the statuses this tenant actually uses
    washLegend() {
      if (this.washedEnabled) return WASH_LEGEND
      return WASH_LEGEND.filter(row => row.value !== 'READY' && row.value !== 'WASHED')
    },
    maintLegend() {
      return MAINT_LEGEND
    },

    // ---- tv display ----
    displayThemes() {
      return DISPLAY_THEMES
    },
    displayPhosphors() {
      return DISPLAY_PHOSPHORS
    },
    displaySkies() {
      return DISPLAY_SKIES
    },
    // the sky actually painted: auto follows the tenant-local hour (dawn
    // 4-10, day 10-16, dusk 16-20, night otherwise; the clock tick keeps
    // it honest) and cycle rolls one grade per full pass through the pages
    effectiveDisplaySky() {
      if (this.display_sky === 'auto') {
        const h = this.displayMoment(this.display_clock_now).hour()
        if (h >= 4 && h < 10) return 'dawn'
        if (h >= 10 && h < 16) return 'day'
        if (h >= 16 && h < 20) return 'dusk'
        return 'night'
      }
      if (this.display_sky === 'cycle') {
        return DISPLAY_SKY_CYCLE[this.display_sky_pos % DISPLAY_SKY_CYCLE.length]
      }
      return this.display_sky
    },
    // the scenery layer (road, ridges, palms) renders for every sky except
    // classic dusk, which is preserved exactly as originally shipped
    outrunSceneryOn() {
      return DISPLAY_SCENERY_SKIES.indexOf(this.effectiveDisplaySky) !== -1
    },
    isSolariDisplay() {
      return this.display_theme === 'solari'
    },
    isScreensaverDisplay() {
      return this.display_theme === 'screensaver'
    },
    isOutrunDisplay() {
      return this.display_theme === 'outrun'
    },
    // the two themes whose rows are two-face flip panels
    isFlipDisplay() {
      return this.display_theme === 'midnight' || this.display_theme === 'showroom'
    },
    // standalone names the tenant from the summary (no navbar context to
    // differ from); authed boards use the same resolver as the rail chip
    displayOperatorName() {
      if (this.standalone) return (this.summary && this.summary.tenant_name) || ''
      return tenantDisplayName(this.user, this.tenant) || ''
    },
    displayClock() {
      return this.displayMoment(this.display_clock_now).format('h:mm A')
    },
    displayClockDate() {
      return this.displayMoment(this.display_clock_now).format('dddd, MMMM D')
    },
    // one row per vehicle in the board's current filter scope and order: a
    // manager pre-filters then presents, a kiosk boot is a fresh mount so it
    // shows the full roster. the api order already parks no-movement rows
    // last, which is the operational order for a wall.
    displayRows() {
      void this.display_minute_tick // re-derive countdowns/tenses on the tick
      return this.filteredItems.map(item => this.displayRow(item))
    },
    displayPageCount() {
      return Math.max(1, Math.ceil(this.displayRows.length / Math.max(1, this.display_page_size)))
    },
    currentDisplayPageRows() {
      const start = this.display_page * this.display_page_size
      return this.displayRows.slice(start, start + this.display_page_size)
    },
    // hash of what the page visibly reads (vehicles, statuses, times; not
    // the live countdown/eta text). the rows container is keyed by it, so
    // the entrance choreography replays only when a reading actually
    // changed, instead of re-flipping identical panels on every refresh
    displayPageSig() {
      const s = this.displayRowsSig(this.currentDisplayPageRows)
      let h = 5381
      for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) | 0
      return h
    },
    // the screen saver's visible window: a wrapping slice starting at the
    // ticker position. rosters that fit on one screen never scroll (keys
    // must stay unique for the transition-group), so they render whole and
    // rely on the palette swap alone for burn-in protection.
    screensaverRows() {
      const rows = this.displayRows
      const size = Math.max(1, this.display_page_size)
      if (rows.length <= size) return rows
      const out = []
      for (let i = 0; i < size; i++) out.push(rows[(this.display_scroll_pos + i) % rows.length])
      return out
    },
    // the solari board keeps a constant line count: unused lines flip to
    // blanks like the real hardware, and rows are keyed by position so a
    // page change flutters cells from the old text to the new
    solariDisplayRows() {
      const rows = this.currentDisplayPageRows.slice()
      while (rows.length < this.display_page_size) {
        rows.push({
          key: 'sf-blank-' + rows.length,
          name: '', wash: '', maint: '', washShort: '', maintShort: '',
          washTone: 'blank', maintTone: 'blank', washRaw: 'none', maintRaw: 'none',
          returnLabel: '', returnTime: '', returnState: '', returnEta: '',
          departTime: '', departIn: '', departFrac: null, departUrgency: '', departDistant: false,
          sfReturn: '', sfDepart: '', sfDepartIn: '', sfReturnTone: 'none',
        })
      }
      return rows
    },
  },

  watch: {
    // the navbar switcher rewrites ?tenant= in place without remounting,
    // so reset the tenant-scoped state and refetch
    tenant() {
      this.selected_base = ''
      this.wash_filters = []
      this.maint_filters = []
      this.tag_filters = []
      this.search = ''
      this.summary = null
      this.items_full = []
      this.error = ''
      this.noData = false
      this.cancelEdit()
      this.load_data()
    },
    // keep the display page and ticker position in range when the roster
    // changes shape (refresh, filter change, tenant switch). deliberately
    // no re-measure here: the page size is measured per theme at entry,
    // theme switch, and resize, and stays STATIC across refreshes so the
    // board's row count never wobbles (blank-padded rows hold the frame)
    displayRows() {
      if (!this.display_mode) return
      if (this.display_page >= this.displayPageCount) this.display_page = 0
      if (this.display_scroll_pos >= this.displayRows.length) this.display_scroll_pos = 0
    },
    // snapshot the outgoing page for the flip panels' back faces; user
    // watchers run before the render watcher, so the new render sees both.
    // only snapshot when the visible reading actually changes: the rows
    // container re-keys on the same signature, so the back faces always
    // mount together with their incoming faces. snapshotting on every
    // identity change (refreshes and minute ticks rebuild the row objects
    // with identical content) mounted back faces into a settled board and
    // replayed phantom fold-outs over the same reading.
    currentDisplayPageRows(val, oldVal) {
      if (!this.display_mode) return
      // a page-size re-measure (first entry, window resize) re-slices the
      // page without the reading changing: reveal from blank, never fold
      // identical back faces over it
      if (this._display_resize) {
        this._display_resize = false
        this.display_prev_rows = null
        return
      }
      if (!oldVal || !oldVal.length) {
        this.display_prev_rows = null
        return
      }
      if (this.displayRowsSig(val) !== this.displayRowsSig(oldVal)) {
        this.display_prev_rows = oldVal
      }
    },
  },

  mounted() {
    this.setupMobileQuery()
    // print path hides the app chrome via this body class while the board
    // is mounted, mirroring the trip-sheet wall board
    document.body.classList.add('vsm-board-print')
    // kiosk/outbound surfaces stay light: the standalone board renders
    // chromeless (no .fi-hz-root), so a viewer's persisted midnight theme
    // only half-applies (dark rail, unreadable title). drop the html theme
    // attribute while mounted and restore whatever was there on the way out.
    if (this.standalone) {
      this.saved_fi_theme = document.documentElement.getAttribute('data-fi-theme')
      document.documentElement.removeAttribute('data-fi-theme')
    }
    document.addEventListener('visibilitychange', this.onVisibility)
    document.addEventListener('keydown', this.onSheetKeydown)
    window.addEventListener('resize', this.measureHistCols)
    this.load_data()
    this.startAutoRefresh()

    // tv display wiring: exit/measure hooks plus the kiosk query boot
    window.addEventListener('keydown', this.onDisplayKeydown)
    window.addEventListener('resize', this.onDisplayResize)
    document.addEventListener('fullscreenchange', this.onDisplayFullscreenChange)
    document.addEventListener('webkitfullscreenchange', this.onDisplayFullscreenChange)
    // kiosk bookmarks boot straight into display mode: ?display=<theme>,
    // or ?display=1 for the remembered theme
    const q = String(this.$route.query.display || '').toLowerCase()
    if (isDisplayTheme(q)) {
      this.enterDisplay(q, true)
    } else if (q === '1' || q === 'true') {
      this.enterDisplay(null, true)
    }
  },

  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocClick, true)
    window.removeEventListener('scroll', this.onWindowScroll, true)
    window.removeEventListener('resize', this.onWindowResize, true)
    document.removeEventListener('visibilitychange', this.onVisibility)
    document.removeEventListener('keydown', this.onSheetKeydown)
    window.removeEventListener('resize', this.measureHistCols)
    // if the sheet was open when the board unmounted, give scrolling back
    if (this.sheet_name) document.body.style.overflow = ''
    document.body.classList.remove('vsm-board-print')
    if (this.standalone && this.saved_fi_theme) {
      document.documentElement.setAttribute('data-fi-theme', this.saved_fi_theme)
    }
    if (this.notes_saved_timer) clearTimeout(this.notes_saved_timer)
    this.stopAutoRefresh()
    this.teardownMobileQuery()
    window.removeEventListener('keydown', this.onDisplayKeydown)
    window.removeEventListener('resize', this.onDisplayResize)
    document.removeEventListener('fullscreenchange', this.onDisplayFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.onDisplayFullscreenChange)
    this.stopDisplayTimers()
    document.body.classList.remove('vb-display-active')
  },

  // re-measure the history grid tracks after every re-render while a panel
  // is open; a css-var write on the card is not reactive, so this never
  // re-triggers itself
  updated() {
    this.measureHistCols()
  },

  methods: {
    // opts.soft = a background auto-refresh: merge rows in place so open
    // history panels and a focused note survive (see mergeResults). a hard
    // load (manual refresh, base/tenant change, first paint) replaces the
    // rows and resets those, since the user asked for fresh state.
    async load_data(opts = {}) {
      if (!this.tenant) return
      if (!this.standaloneIdOk) {
        // a raw tenant id in a standalone url: state it instead of loading.
        // retry re-lands here, which is fine - the link itself is wrong.
        this.error = 'This standalone link is not valid. Open the info panel on the Vehicle Status Manager and copy the current VSM2 standalone link.'
        this.loading = false
        return
      }
      const soft = !!(opts && opts.soft === true)
      const seq = ++this.fetch_seq
      this.refreshing = true
      if (!this.summary) this.loading = true
      try {
        const fetch_vehicle_info =
          this.vsmSource === 'tbn' ? get_tbn_vehicle_info
          : this.vsmSource === 'fasttrak' ? get_vehicle_info_fasttrak
          : get_vehicle_info
        // vsm_board is staff-only (/ops_vsm) with no standalone driver url, so the
        // driver-view future-hours override never applies here - only the legacy
        // standalone driver links (vehicle_status.vue / vehicle_status_maint.vue) set it
        const { value: { data = {} } = {} } = await fetch_vehicle_info(this.tenant, this.selected_base)
        if (seq !== this.fetch_seq) return
        this.summary = data.summary || null
        this.error = ''
        this.noData = false
        this.last_updated = Date.now()
        if (soft) {
          this.mergeResults(data.results || [])
        } else {
          this.items_full = data.results || []
          // a hard reload replaces the row objects, so open history panels
          // and any in-flight cell edit reset rather than point at stale rows
          this.expanded = []
          this.cancelEdit()
        }
      } catch (e) {
        if (seq === this.fetch_seq) {
          // the roster is sourced from the telematics feed, so a fleet with
          // no connected provider 404s. show that as the calm no-data fact
          // and reserve the retryable error banner for genuine failures.
          const status = e && e.response && e.response.status
          if (status === 404) {
            this.noData = true
            this.error = ''
            this.summary = null
            this.items_full = []
          } else {
            // a genuine failure on a tenant that previously 404'd must clear
            // noData, or the calm card and the error banner stack together
            this.noData = false
            this.error = 'The vehicle data did not load. Check your connection and retry.'
          }
        }
      } finally {
        if (seq === this.fetch_seq) {
          this.loading = false
          this.refreshing = false
        }
      }
    },

    // soft-merge a fresh roster into the current rows: reuse each existing
    // row object by name so an open history panel and a focused notes input
    // survive the refresh, while following the new ordering and field values.
    // rows that drop out of the feed fall away; new rows come in as-is.
    mergeResults(results) {
      const prev = {}
      this.items_full.forEach(row => { prev[row.name] = row })
      this.items_full = results.map(incoming => {
        const existing = prev[incoming.name]
        if (!existing) return incoming
        Object.keys(incoming).forEach(key => {
          // never stomp a note the user is actively typing
          if (key === 'notes' && this.notes_editing === existing.name) return
          this.$set(existing, key, incoming[key])
        })
        return existing
      })
      // close history panels for vehicles no longer in the feed
      const present = {}
      results.forEach(row => { present[row.name] = true })
      this.expanded = this.expanded.filter(name => present[name])
      // same for the detail sheet (sheetItem would go null anyway; this also
      // releases the scroll lock closeSheet owns)
      if (this.sheet_name && !present[this.sheet_name]) this.closeSheet()
    },

    // periodic background reload for the wall-board use case. the tv
    // display forces the cadence even when the rail toggle is off; the
    // toggle itself is never mutated (exit re-evaluates it).
    startAutoRefresh() {
      this.stopAutoRefresh()
      if ((this.auto_refresh || this.display_mode) && this.refresh_secs > 0) {
        this.refresh_timer = setInterval(this.autoRefresh, this.refresh_secs * 1000)
      }
    },
    stopAutoRefresh() {
      if (this.refresh_timer) {
        clearInterval(this.refresh_timer)
        this.refresh_timer = null
      }
    },
    // one tick: defer rather than disrupt. a hidden tab, an in-flight fetch,
    // an open status picker, or a note being edited all wait for the next
    // interval, so the refresh never fights the user or wastes a call.
    autoRefresh() {
      if (typeof document !== 'undefined' && document.hidden) return
      if (this.refreshing || this.loading) return
      // the user-facing deferrals don't apply while the tv overlay covers
      // the board (no picker or note can be open under it)
      if (!this.display_mode) {
        if (!this.auto_refresh) return
        if (this.editing_cell) return
        if (this.notes_editing) return
      }
      this.load_data({ soft: true })
    },
    toggleAutoRefresh() {
      this.auto_refresh = !this.auto_refresh
      if (this.auto_refresh) {
        this.startAutoRefresh()
        // resuming should feel immediate, not wait a full interval
        this.autoRefresh()
      } else {
        this.stopAutoRefresh()
      }
    },
    // catch a tab returning to the foreground so a board left in the
    // background snaps current the moment it is looked at again
    onVisibility() {
      if (typeof document !== 'undefined' && !document.hidden) this.autoRefresh()
    },

    printPage() {
      // stamp the print header, let it paint, then open the print dialog
      this.printed_at = Date.now()
      this.$nextTick(() => window.print())
    },

    countWash(status) {
      return this.items_full.filter(item => item.vehicle_status === status).length
    },
    countMaint(status) {
      return this.items_full.filter(item => item.maintenance_status === status).length
    },
    // vehicle_tags arrives as a comma-delimited string ("sd,charter"); split
    // to trimmed, non-empty tokens. absent on non-samsara feeds, so guard.
    itemTags(item) {
      if (!item.vehicle_tags) return []
      return String(item.vehicle_tags).split(',').map(t => t.trim()).filter(Boolean)
    },
    countTag(tag) {
      return this.items_full.filter(item => this.itemTags(item).indexOf(tag) !== -1).length
    },

    toggleWashFilter(value) {
      if (this.wash_filters.includes(value)) {
        this.wash_filters = this.wash_filters.filter(v => v !== value)
      } else {
        this.wash_filters = [...this.wash_filters, value]
      }
    },
    toggleMaintFilter(value) {
      if (this.maint_filters.includes(value)) {
        this.maint_filters = this.maint_filters.filter(v => v !== value)
      } else {
        this.maint_filters = [...this.maint_filters, value]
      }
    },
    toggleTagFilter(value) {
      if (this.tag_filters.includes(value)) {
        this.tag_filters = this.tag_filters.filter(v => v !== value)
      } else {
        this.tag_filters = [...this.tag_filters, value]
      }
    },
    clearFilters() {
      this.wash_filters = []
      this.maint_filters = []
      this.tag_filters = []
      this.search = ''
    },
    // clear search and keep focus in the field, so keyboard/AT users are not
    // dropped to <body> when the v-if'd clear button unmounts itself
    clearSearch() {
      this.search = ''
      this.$nextTick(() => {
        if (this.$refs.searchInput && this.$refs.searchInput.focus) this.$refs.searchInput.focus()
      })
    },

    // raw status value as searchable text: "PENDING_DVIR" -> "pending_dvir
    // pending dvir", so both the underscored value and the spaced words match
    statusSearchText(status) {
      if (!status) return ''
      return (String(status) + ' ' + String(status).replace(/_/g, ' ')).toLowerCase()
    },

    // free-text match across the fields the legacy board searches: vehicle
    // name, the next-movement driver(s), wash/maint status, bases, id, notes
    matchesSearch(item, q) {
      const hit = v => {
        if (v === null || v === undefined) return false
        if (Array.isArray(v)) return v.some(hit)
        return String(v).toLowerCase().indexOf(q) !== -1
      }
      if (hit(item.name)) return true
      if (hit(item.next_mvmt_driver)) return true
      if (hit(item.next_mvmt_start_base) || hit(item.start_bases)) return true
      if (hit(item.next_mvmt_id)) return true
      if (hit(item.notes)) return true
      if (hit(item.vehicle_tags)) return true
      if (hit(this.washChipLabel(item.vehicle_status)) || hit(this.maintChipLabel(item.maintenance_status))) return true
      // match the RAW status values too, not just their humanized labels. the
      // labels are display text and can drop tokens the user still searches by:
      // shortening PENDING_DVIR/UNSAFE_DVIR to "Pending"/"Unsafe" took the word
      // "dvir" out of the searchable text, so `dvir` matched nothing. the raw
      // value keeps it (underscores split so "pending dvir" matches as well).
      if (hit(this.statusSearchText(item.vehicle_status)) || hit(this.statusSearchText(item.maintenance_status))) return true
      // multi-movement rows carry additional drivers/ids in next_mvmt_list
      return (item.next_mvmt_list || []).some(nm => hit(nm.next_mvmt_driver) || hit(nm.next_mvmt_id))
    },

    // mobile breakpoint, reactive to rotation/resize so the layout is not
    // pinned at mount the way a user-agent sniff would be
    setupMobileQuery() {
      if (typeof window === 'undefined' || !window.matchMedia) return
      this._mql = window.matchMedia('(max-width: 640px)')
      this.mobileView = this._mql.matches
      this._mqlHandler = e => { this.mobileView = e.matches }
      if (this._mql.addEventListener) this._mql.addEventListener('change', this._mqlHandler)
      else this._mql.addListener(this._mqlHandler)
    },
    teardownMobileQuery() {
      if (!this._mql) return
      if (this._mql.removeEventListener) this._mql.removeEventListener('change', this._mqlHandler)
      else this._mql.removeListener(this._mqlHandler)
    },

    // card tap -> detail sheet, unless the tap landed on an interactive
    // child (chips, the vehicle link) that has its own behavior
    cardTap(item, ev) {
      if (ev.target.closest('button, a, input, select, textarea, label')) return
      this.openSheet(item)
    },
    openSheet(item) {
      this.sheet_name = item.name
      this.fetchHistory(item.name)
      // hold the page still behind the sheet; restored on close and (belt
      // and suspenders) in beforeDestroy
      document.body.style.overflow = 'hidden'
      this.$nextTick(() => { if (this.$refs.sheetClose) this.$refs.sheetClose.focus() })
    },
    closeSheet() {
      this.sheet_name = null
      document.body.style.overflow = ''
    },
    onSheetKeydown(e) {
      if (e.key === 'Escape' && this.sheet_name) this.closeSheet()
    },
    // aux-row promotion policy: the tenant's configured mobile fields that
    // are not already on the card face, in config order, empties skipped,
    // capped at 3 so the row never wraps into a second deck
    auxPairs(item) {
      const out = []
      for (const f of this.mobileSecondaryFields) {
        const value = this.cellDisplay(item, f.key)
        if (!value || value === '-') continue
        out.push({ key: f.key, label: AUX_SHORT_LABELS[f.key] || f.label, value })
        if (out.length === 3) break
      }
      return out
    },

    // one display value for a secondary field in the card detail, covering
    // the timestamp, boolean, and plain-text cases the table renders
    // through its per-column slots
    cellDisplay(item, key) {
      const stampKeys = {
        last_dirty_time: 1, last_cleaned_time: 1, last_washed_time: 1,
        last_returned_time: 1, current_mvmt_return_time: 1,
        next_mvmt_return_time: 1, next_mvmt_time: 1,
      }
      if (stampKeys[key]) {
        const guarded = key === 'last_dirty_time' || key === 'last_cleaned_time' || key === 'last_washed_time' || key === 'last_returned_time'
        if (guarded && !item.vehicle_info) return '-'
        return this.formatStamp(item[key])
      }
      if (key === 'wheelchair') return item.wheelchair ? 'Yes' : '-'
      if (key === 'stayanddo') return item.stayanddo ? 'Yes' : '-'
      if (key === 'tote') return item.tote_label || (item.tote ? 'Yes' : '-')
      return dash(item[key])
    },

    // shared status labels (also called directly by the history rows);
    // the chip wrappers keep the '-' placeholder for missing values
    statusLabel,
    washChipLabel(status) {
      return status ? statusLabel(status) : '-'
    },
    washChipClass(status) {
      return WASH_CHIP_CLASSES[status] || 'vb-chip--gray'
    },
    maintChipLabel(status) {
      return status ? statusLabel(status) : '-'
    },
    maintChipClass(status) {
      return MAINT_CHIP_CLASSES[status] || 'vb-chip--gray'
    },
    // chip tint for an option in the open picker (wash vs maint palette)
    pickerChipClass(value) {
      return this.picker_field === 'maintenance_status' ? this.maintChipClass(value) : this.washChipClass(value)
    },

    // at-a-glance triage that colors the card's left rail (and the table
    // row's left edge). most urgent wins. undefined maint is treated as
    // neutral, mirroring how countAvailable already ignores it, so a tenant
    // that does not track maintenance never lights up amber/red for it.
    rowState(item) {
      const maint = item.maintenance_status
      const wash = item.vehicle_status
      // out of service or already overdue → red
      if (maint === 'OOS' || maint === 'UNSAFE_DVIR') return 'late'
      if (this.isLate(item.next_mvmt_delta)) return 'late'
      const ms = item.next_mvmt_ms
      const scheduled = typeof ms === 'number' && ms < MS_SENTINEL
      if (scheduled) {
        const secs = ms - Date.now() / 1000
        if (secs < 0) return 'late'
        if (secs <= SOON_SECS) return 'attention'
      }
      // needs work before it can roll → amber
      if (maint === 'PM' || maint === 'PENDING_DVIR') return 'attention'
      if (wash === 'DIRTY') return 'attention'
      // ready to go (same definition as the Available chip) → green
      const washReady = this.washedEnabled ? 'READY' : 'CLEANED'
      if (wash === washReady && maint === 'READY') return 'ready'
      return 'none'
    },
    // b-table per-row class; only problems get a colored left edge so the
    // desktop table is not striped end to end. the details band opts out
    // (type 'row-details'): a rail there would just repeat the parent's
    // status, and the history rows inside it carry their own (histRowClass)
    rowClass(item, type) {
      if (!item || type !== 'row') return ''
      const state = this.rowState(item)
      return state === 'late' || state === 'attention' ? 'vb-row-' + state : ''
    },
    // per-entry triage for the history rails: the status half of rowState.
    // lateness is departure-based and means nothing for a past snapshot
    histState(h) {
      if (h.maintenance_status === 'OOS' || h.maintenance_status === 'UNSAFE_DVIR') return 'late'
      if (h.maintenance_status === 'PM' || h.maintenance_status === 'PENDING_DVIR' || h.vehicle_status === 'DIRTY') return 'attention'
      return ''
    },
    histRowClass(h) {
      const s = this.histState(h)
      return s ? 'vb-hist2-row--' + s : ''
    },
    histEntryClass(h) {
      const s = this.histState(h)
      return s ? 'vb-hist-entry--' + s : ''
    },
    // dim history values that match the previous (older) snapshot so both
    // history views read as "what changed when" instead of a wall of
    // repeats; the oldest entry has no baseline and stays at full strength
    histDim(history, idx, key) {
      const prev = history && history[idx + 1]
      if (!prev) return false
      return (history[idx][key] || '') === (prev[key] || '')
    },
    // an entry whose only change was deleting the note would otherwise
    // render as two dim chips and nothing else; name the event instead
    histNoteCleared(history, idx) {
      const prev = history && history[idx + 1]
      return !!(prev && prev.notes && !history[idx].notes)
    },

    cellKey(item, field) {
      return item.name + '::' + field
    },
    // tap the chip to open the colored status options as a floating popover
    // anchored to it; tapping one sets it in a single step. the popover is
    // position: fixed and rendered once at the root, so it escapes the
    // table's overflow scrollport (no clipping) and opening it never reflows
    // the neighboring chip. tapping the same chip again toggles it closed.
    startEdit(item, field, ev) {
      const key = this.cellKey(item, field)
      if (this.editing_cell === key) { this.cancelEdit(true); return }
      // anchor to the colored chip itself (not the whole button, whose
      // invisible hit-area extends past the chip) so the menu lines up over it
      const trigger = ev && (ev.currentTarget || ev.target)
      const chip = trigger && trigger.querySelector ? trigger.querySelector('.vb-status-chip') : null
      const anchor = chip || trigger
      this._trigger_rect = anchor && anchor.getBoundingClientRect ? anchor.getBoundingClientRect() : null
      this.picker_item = item
      this.picker_field = field
      this.editing_cell = key
      // render off-screen first so the unpositioned popover never flashes
      this.picker_style = { top: '-9999px', left: '-9999px' }
      this.$nextTick(() => {
        this.positionPicker()
        document.addEventListener('mousedown', this.onDocClick, true)
        // a fixed popover anchored to a scrolled chip would detach, so any
        // scroll (window or the table's inner scrollport) closes it
        window.addEventListener('scroll', this.onWindowScroll, true)
        window.addEventListener('resize', this.onWindowResize, true)
        // land keyboard focus on the current option
        const pop = this.$refs.pickerPop
        const el = pop && (pop.querySelector('.vb-status-opt--current') || pop.querySelector('.vb-status-opt'))
        if (el && el.focus) el.focus()
      })
    },
    // center the menu vertically on the trigger so the middle option lands
    // directly over the button (native-select feel), and line the option
    // chips up horizontally over the trigger chip; clamp to the viewport
    positionPicker() {
      const rect = this._trigger_rect
      const pop = this.$refs.pickerPop
      if (!rect || !pop) return
      const margin = 8
      const pw = pop.offsetWidth
      const ph = pop.offsetHeight
      let top = rect.top + rect.height / 2 - ph / 2
      if (top + ph > window.innerHeight - margin) top = window.innerHeight - margin - ph
      if (top < margin) top = margin
      // undo the menu's internal left padding so an option chip sits exactly
      // over the trigger chip
      const firstChip = pop.querySelector('.vb-status-chip')
      const chipOffset = firstChip ? firstChip.getBoundingClientRect().left - pop.getBoundingClientRect().left : 0
      let left = rect.left - chipOffset
      if (left + pw > window.innerWidth - margin) left = window.innerWidth - margin - pw
      if (left < margin) left = margin
      this.picker_style = { top: Math.round(top) + 'px', left: Math.round(left) + 'px' }
    },
    // restoreFocus returns focus to the chip that opened the picker so
    // keyboard/AT users keep their place; mouse outside-click closes pass
    // false and leave focus alone
    cancelEdit(restoreFocus = false) {
      const key = this.editing_cell
      this.editing_cell = null
      this.picker_item = null
      this.picker_field = null
      this.picker_style = null
      document.removeEventListener('mousedown', this.onDocClick, true)
      window.removeEventListener('scroll', this.onWindowScroll, true)
      window.removeEventListener('resize', this.onWindowResize, true)
      if (restoreFocus && key) {
        this.$nextTick(() => {
          const el = this.$el.querySelector('[data-status-trigger="' + key + '"]')
          if (el && el.focus) el.focus()
        })
      }
    },
    // close when the next pointer-down lands outside the popover and outside
    // any chip trigger (so tapping another row's chip just re-targets it)
    onDocClick(e) {
      if (e.target.closest && (e.target.closest('.vb-picker-pop') || e.target.closest('.vb-chip-btn'))) return
      this.cancelEdit()
    },
    onWindowScroll() { this.cancelEdit() },
    onWindowResize() { this.cancelEdit() },
    // one tap sets + saves: optimistic update, patch from the response,
    // revert + toast on failure
    async setStatus(item, field, value) {
      this.cancelEdit(true)
      if (!value || value === item[field]) return
      const previous = item[field]
      this.$set(item, field, value)
      try {
        const body = { name: item.name }
        body[field] = value
        const update = this.vsmSource === 'tbn' ? tbn_update_vehicle_status : update_vehicle_status
        const { value: { data } = {} } = await update(this.tenant, body)
        // patch the row from the response instead of refetching, so open
        // history panels and scroll position survive the save
        if (data && typeof data === 'object') this.patchRow(item, data)
      } catch (e) {
        this.$set(item, field, previous)
        this.toast('The status update did not save. Try again.')
      }
    },
    // merge the updated record the api returns into the row, the same way
    // the list fetch merges each vehicle_info record into its row
    patchRow(item, record) {
      Object.keys(record).forEach(key => this.$set(item, key, record[key]))
      this.$set(item, 'vehicle_info', record)
    },

    noteFocus(item) {
      this.notes_original = item.notes
      // pauses the auto-refresh merge from overwriting this cell mid-edit
      this.notes_editing = item.name
    },
    async saveNotes(item) {
      // editing ends on blur, so clear the auto-refresh guard right away
      this.notes_editing = ''
      // capture before any await so a focus change cannot swap originals
      const original = this.notes_original
      if (item.notes === original) return
      try {
        const update = this.vsmSource === 'tbn' ? tbn_update_vehicle_status : update_vehicle_status
        await update(this.tenant, { name: item.name, notes: item.notes })
        this.flashNotesSaved(item.name)
      } catch (e) {
        // revert so the cell never keeps a value the server did not take
        this.$set(item, 'notes', original)
        this.toast('The notes did not save. Try again.')
      }
    },
    flashNotesSaved(name) {
      if (this.notes_saved_timer) clearTimeout(this.notes_saved_timer)
      this.notes_saved = name
      this.notes_saved_timer = setTimeout(() => { this.notes_saved = '' }, 1600)
    },

    toast(message) {
      try {
        this.$bvToast.toast(message, { title: 'VSM2', variant: 'warning', solid: true, autoHideDelay: 4000 })
      } catch (_) { /* toast plugin may not be ready on first paint */ }
    },

    toggleHistory(row) {
      const name = row.item.name
      if (this.expanded.includes(name)) {
        this.expanded = this.expanded.filter(n => n !== name)
        row.toggleDetails()
        return
      }
      this.expanded = [...this.expanded, name]
      row.toggleDetails()
      this.fetchHistory(name)
    },
    async fetchHistory(name) {
      const target = this.items_full.filter(item => item.name === name)[0]
      if (target && target.history_error) {
        // a retry goes back to the loading note while the fetch runs
        target.history_error = false
        this.$forceUpdate()
      }
      try {
        const { value: { data = [] } = {} } = await get_vehicle_info_history(this.tenant, name)
        if (target) {
          // history is a late-attached key, so force the re-render the
          // same way the legacy page does
          target.history = data
          this.$forceUpdate()
        }
      } catch (e) {
        // failure renders the per-row error note with its retry link
        if (target) {
          target.history_error = true
          this.$forceUpdate()
        }
      }
    },
    // the details row bootstrap-vue renders is a single cell spanning every
    // column, so the history grid inside it cannot inherit the table's
    // column tracks. measure the header cells instead and hand the geometry
    // to the grid as css vars on the card. runs from the updated hook while
    // a panel is open (covering refresh merges, column config loads, and
    // content-driven column shifts) plus window resize; four rect reads, so
    // cheap enough to run unconditionally. deltas between rects taken in the
    // same frame stay correct under the responsive wrapper's horizontal
    // scroll.
    measureHistCols() {
      if (this.mobileView || !this.expanded.length || !this.$el || !this.$el.querySelector) return
      const card = this.$el.querySelector('.vb-card')
      const hist = card && card.querySelector('.vb-history')
      const ths = card ? card.querySelectorAll('thead th') : []
      if (!hist || ths.length < 4) return
      const left = hist.getBoundingClientRect().left
      const name = ths[1].getBoundingClientRect()
      const wash = ths[2].getBoundingClientRect()
      const maint = ths[3].getBoundingClientRect()
      const end = ths[4] ? ths[4].getBoundingClientRect().left : maint.right
      card.style.setProperty('--vbh-ind', (name.left - left) + 'px')
      card.style.setProperty('--vbh-c1', (wash.left - left) + 'px')
      card.style.setProperty('--vbh-c2', (maint.left - wash.left) + 'px')
      card.style.setProperty('--vbh-c3', (end - maint.left) + 'px')
    },

    isLate(value) {
      return !!value && String(value).toLowerCase().indexOf('late') === 0
    },
    // time-only urgency for the departs-in value: red once overdue, amber
    // inside the soon window, default ink otherwise. kept separate from
    // rowState so a dirty bus that does not leave for days never paints its
    // countdown amber.
    departUrgency(item) {
      if (this.isLate(item.next_mvmt_delta)) return 'late'
      const ms = item.next_mvmt_ms
      if (typeof ms === 'number' && ms < MS_SENTINEL) {
        const secs = ms - Date.now() / 1000
        if (secs < 0) return 'late'
        if (secs <= SOON_SECS) return 'soon'
      }
      return ''
    },
    // display-only trim of the api's readable delta: once a countdown is a
    // day or more out ("1d 4h 22m", "late 2d 0h 5m") the minute is noise, so
    // drop it and keep the deck narrow. same-day values keep full precision.
    // sort and search still use the raw value.
    formatDelta(value) {
      const s = value == null ? '' : String(value)
      if (!/\dd\b/.test(s)) return s
      return s.replace(/ \d+m$/, '')
    },
    // the formatted next-depart stamp for the card's depart deck, '' when there
    // is no scheduled movement so the sub-line simply doesn't render
    departStamp(item) {
      const s = this.formatStamp(item.next_mvmt_time)
      return s === '-' ? '' : s
    },
    // the band's stamp: today's returns drop the date ("6:00p"), so a date's
    // mere presence signals "not today" for free. tenant-configured display
    // formats are never trimmed (no way to know which tokens are the date).
    // '' when the vehicle has no current movement so the band doesn't render.
    returnBandStamp(item) {
      const m = this.parseStamp(item.current_mvmt_return_time)
      if (!m) return ''
      const tenantFormat = (this.summary && this.summary.datetime_format) || ''
      if (tenantFormat && tenantFormat !== 'MM/DD hh:mm A') return m.format(tenantFormat)
      const tz = (this.summary && this.summary.timezone) || ''
      const now = tz ? moment.tz(tz) : moment()
      const fmt = m.isSame(now, 'day') ? 'h:mma' : 'M/D h:mma'
      return m.format(fmt).replace(/(a|p)m$/, '$1')
    },
    // tense: "Returned" reads as a receipt once the bus is actually back
    // (stamp passed and the wash status is no longer OUT); anything else -
    // future, or past-but-still-out (overdue) - stays "Returns".
    returnBandLabel(item) {
      const m = this.parseStamp(item.current_mvmt_return_time)
      if (m && m.valueOf() < Date.now() && item.vehicle_status !== 'OUT') return 'Returned'
      return 'Returns'
    },
    // ink for the band stamp. 'late' = still OUT past the scheduled return
    // with no live eta: actually late coming home, so red is earned (the
    // stamp parse is tenant-tz-anchored, so the epoch comparison is safe).
    // 'dim' = quiet: already returned, or a day-plus-out multi-day charter
    // (same mute rule as distant departures). '' = a pending return inside
    // the day - the one the wash crew is staging for.
    returnBandState(item) {
      const m = this.parseStamp(item.current_mvmt_return_time)
      if (!m) return ''
      if (m.valueOf() < Date.now()) {
        if (item.vehicle_status === 'OUT' && !item.current_mvmt_return_eta) return 'late'
        return 'dim'
      }
      return m.valueOf() - Date.now() >= 86400 * 1000 ? 'dim' : ''
    },
    // day-plus-out departures paint quiet (normal weight + gray); only a
    // same-day countdown, or the late/soon states, earn the bold dark ink so
    // a wall of distant times stops out-shouting the vehicle names. prefers the
    // absolute stamp; falls back to a day token in the delta string for feeds
    // that carry no next_mvmt_ms.
    departIsDistant(item) {
      const u = this.departUrgency(item)
      if (u === 'late' || u === 'soon') return false
      const ms = item.next_mvmt_ms
      if (typeof ms === 'number' && ms < MS_SENTINEL) {
        return ms - Date.now() / 1000 >= 86400
      }
      return /\dd/.test(item.next_mvmt_delta || '')
    },

    // compact stamp by default ("6/11 7:59a"), with a tenant-configured
    // display format still winning. the api blends 'MM/DD hh:mm A' in as
    // its own fallback, so that exact value means "not configured". the
    // two-step parse mirrors the legacy formatDateTimeCustom filter,
    // including the non-iso input shape some rows carry.
    //
    // render in the tenant's timezone so a dispatcher in another zone still
    // reads the yard's wall-clock. moment.tz parses a zoneless value as
    // tenant-local (no shift) and converts an absolute (Z/offset) value into
    // tenant time. this matches the legacy boards' moment.tz.setDefault(tz)
    // without the global side effect that leaks into every other page. the
    // compact case formats off the zoned moment directly, since routing
    // through formatDateTimeCompact would re-parse and re-localize to the
    // viewer, undoing the conversion.
    // the tz-aware parse behind formatStamp, shared with returnIsDistant
    // (which needs the moment, not the display string); null when the value
    // is missing or unparseable
    parseStamp(value) {
      if (!value || typeof value !== 'string') return null
      const tz = (this.summary && this.summary.timezone) || ''
      const isIso = value.indexOf('T') !== -1
      const m = tz
        ? (isIso ? moment.tz(String(value), tz) : moment.tz(String(value), ['MM/DD hh:mm A'], tz))
        : (isIso ? moment(String(value)) : moment(String(value), ['MM/DD hh:mm A']))
      return m.isValid() ? m : null
    },
    formatStamp(value) {
      const tenantFormat = (this.summary && this.summary.datetime_format) || ''
      const m = this.parseStamp(value)
      if (!m) return '-'
      if (tenantFormat && tenantFormat !== 'MM/DD hh:mm A') return m.format(tenantFormat)
      // same compact shape as formatDateTimeCompact ("6/11 7:59a"), kept on
      // the zoned moment to preserve the tenant timezone
      return m.format('M/D h:mma').replace(/(a|p)m$/, '$1') || '-'
    },

    // the primary movement carries a driver array (co-drivers), while each
    // upcoming booking in next_mvmt_list carries a single driver string
    formatDrivers(value) {
      if (Array.isArray(value)) return value.length ? value.join(', ') : '-'
      return value || '-'
    },

    // departs-in sorts on the precomputed millisecond delta from the api,
    // the display string is not sortable text (mirrors the legacy page)
    tableSortCompare(itemA, itemB, key) {
      if (key !== 'next_mvmt_delta') return false
      return itemA['next_mvmt_ms'] - itemB['next_mvmt_ms']
    },

    // ---- tv display ----

    // a moment in the tenant zone when one is configured, browser-local
    // otherwise; the display clock routes through here
    displayMoment(value) {
      const m = moment(value)
      const tz = this.summary && this.summary.timezone
      if (tz) m.tz(tz)
      return m
    },

    // maps one vehicle row to the fixed tv row: vehicle, wash, maint,
    // returns (stamp + live eta), departs (stamp + countdown). one line per
    // vehicle - idle vehicles are content on this board, so never null.
    displayRow(item) {
      const wash = item.vehicle_status
      const maint = item.maintenance_status
      const washTone = (WASH_CHIP_CLASSES[wash] || 'vb-chip--gray').replace('vb-chip--', '')
      const maintTone = (MAINT_CHIP_CLASSES[maint] || 'vb-chip--gray').replace('vb-chip--', '')
      const returnTime = this.returnBandStamp(item)
      const departTime = this.departStamp(item)
      const departIn = departTime ? this.formatDelta(item.next_mvmt_delta) : ''
      const returnEta = item.current_mvmt_return_eta || ''
      const returnState = returnTime ? this.returnBandState(item) : ''
      // outrun's fuel gauge: fraction of the 12h runway left before the
      // next departure. null = nothing scheduled or comfortably beyond the
      // window (a full bar on a distant row would just shout); 0 = late.
      const nextMs = item.next_mvmt_ms
      let departFrac = null
      if (departTime && typeof nextMs === 'number' && nextMs < MS_SENTINEL) {
        const secs = nextMs - Date.now() / 1000
        if (secs <= 0) departFrac = 0
        else if (secs <= OUTRUN_FUEL_WINDOW_SECS) departFrac = secs / OUTRUN_FUEL_WINDOW_SECS
      }
      return {
        key: item.name,
        name: item.name || '-',
        wash: wash ? statusLabel(wash) : '',
        maint: maint ? statusLabel(maint) : '',
        washTone,
        maintTone,
        washRaw: String(wash || 'none').toLowerCase(),
        maintRaw: String(maint || 'none').toLowerCase(),
        washShort: wash ? (DISPLAY_SHORT_WASH[wash] || String(wash).slice(0, 6)) : '',
        maintShort: maint ? (DISPLAY_SHORT_MAINT[maint] || String(maint).slice(0, 6)) : '',
        // the column header already says returns, so pending/late rows are
        // just the time; only the receipt tense earns a word
        returnLabel: returnTime && this.returnBandLabel(item) === 'Returned' ? 'Back' : '',
        returnTime,
        returnState,
        returnEta,
        departTime,
        departIn,
        departFrac,
        departUrgency: this.departUrgency(item),
        departDistant: this.departIsDistant(item),
        // flap composites: the eta is the more urgent fact whenever it
        // exists, so it takes the return cell on the flap board (the other
        // themes show both)
        sfReturn: returnTime ? (returnEta ? 'ETA ' + returnEta : returnTime) : '',
        sfDepart: departTime,
        sfDepartIn: departIn,
        sfReturnTone: !returnTime ? 'none'
          : returnState === 'late' ? 'late'
          : returnEta ? 'eta'
          : returnState === 'dim' ? 'dim'
          : 'pending',
      }
    },

    // right-aligns a flap value: leading spaces render as blank tiles, so
    // times fill their column from the right edge (split_flap's normalize
    // never trims, it only truncates the tail)
    sfPad(value, len) {
      return String(value || '').padStart(len)
    },

    // date + time flap fields: the date anchors to the left tile and the
    // time to the right one, so every date starts flush with the header
    // and every meridiem lands on the last tile. single-part values (a
    // bare same-day time) right-align; the split is on the first space,
    // which also seats "ETA" left of a right-anchored countdown.
    sfJustify(value, len) {
      const s = String(value || '')
      const cut = s.indexOf(' ')
      if (cut === -1) return this.sfPad(s, len)
      const left = s.slice(0, cut)
      const right = s.slice(cut + 1)
      const fill = len - left.length - right.length
      if (fill < 1) return s
      return left + ' '.repeat(fill) + right
    },

    // what a display page visibly reads, as a comparable string: vehicle
    // set, statuses and times, but not the live countdown/eta text (those
    // update in place without re-running the entrance choreography)
    displayRowsSig(rows) {
      return (rows || [])
        .map(r => [r.key, r.washRaw, r.maintRaw, r.returnTime, r.returnState, r.departTime].join('|'))
        .join('~')
    },

    // entrance stagger for the standard board: midnight/showroom roll
    // prism panels top to bottom with each row starting while the one
    // above is ~70% through its turn, so the page reads as one cascade
    // rather than strictly one-by-one; ember keeps its quick crt sparkle
    // and outrun its racing cascade
    displayRowDelay(idx) {
      if (this.display_theme === 'ember') return (idx * 55) + 'ms'
      if (this.display_theme === 'outrun') return (idx * 90) + 'ms'
      const n = this.currentDisplayPageRows.length
      const step = n > 1
        ? Math.min(DISPLAY_REVEAL_MS * 0.7, (DISPLAY_REVEAL_CASCADE_MS - DISPLAY_REVEAL_MS) / (n - 1))
        : 0
      return Math.round(idx * step) + 'ms'
    },

    enterDisplay(theme, fromQuery) {
      const stored = localStorage.getItem(DISPLAY_THEME_KEY)
      this.display_theme = isDisplayTheme(theme) ? theme : (isDisplayTheme(stored) ? stored : 'midnight')
      const phosphor = localStorage.getItem(DISPLAY_PHOSPHOR_KEY)
      if (DISPLAY_PHOSPHORS.indexOf(phosphor) !== -1) this.display_phosphor = phosphor
      const sky = localStorage.getItem(DISPLAY_SKY_KEY)
      if (DISPLAY_SKIES.indexOf(sky) !== -1) this.display_sky = sky
      this.display_mode = true
      this.display_page = 0
      // a fresh session starts from a blank board that reveals row by row
      // (no back faces from a previous display session)
      this.display_prev_rows = null
      this.resetScreensaverCycle()
      // the overlay covers the picker and the sheet; close them so exit
      // never lands on a stale popover (the sheet also owns a scroll lock)
      this.cancelEdit()
      if (this.sheet_name) this.closeSheet()
      this.ensureDisplayFonts()
      document.body.classList.add('vb-display-active')
      this.startDisplayTimers()
      this.startAutoRefresh()
      this.autoRefresh()
      this.wakeControls()
      this.syncDisplayQuery()
      this.$nextTick(() => {
        this.measureDisplayPageSize()
        // the fullscreen api needs a user gesture, so kiosk boots via
        // query skip it (use the browser's own kiosk/fullscreen instead)
        if (!fromQuery) this.requestDisplayFullscreen()
      })
    },

    exitDisplay() {
      this.display_mode = false
      this.display_idle = false
      // park the pager so a later re-entry starts at page 0 without the
      // page-change watcher snapshotting the old page as back faces
      // (display_mode is already false here, so the watcher stays quiet)
      this.display_page = 0
      this.stopDisplayTimers()
      document.body.classList.remove('vb-display-active')
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {})
      }
      this.did_request_fullscreen = false
      this.syncDisplayQuery()
      // re-arms or stops per the user's rail toggle
      this.startAutoRefresh()
    },

    setDisplayPhosphor(value) {
      if (DISPLAY_PHOSPHORS.indexOf(value) === -1) return
      this.display_phosphor = value
      localStorage.setItem(DISPLAY_PHOSPHOR_KEY, value)
      this.wakeControls()
    },

    displaySkyLabel(value) {
      return DISPLAY_SKY_LABELS[value] || value
    },

    setDisplaySky(value) {
      if (DISPLAY_SKIES.indexOf(value) === -1) return
      this.display_sky = value
      localStorage.setItem(DISPLAY_SKY_KEY, value)
      this.wakeControls()
    },

    setDisplayTheme(value) {
      if (!isDisplayTheme(value)) return
      this.display_theme = value
      // a theme switch re-lands as a fresh reveal, not a re-flip of the
      // same reading over itself
      this.display_prev_rows = null
      // themes have different row heights and board insets (outrun's
      // panel fits one less row than midnight), so each theme measures
      // its own page size
      this.$nextTick(this.measureDisplayPageSize)
      if (value === 'screensaver') this.resetScreensaverCycle()
      localStorage.setItem(DISPLAY_THEME_KEY, value)
      this.ensureDisplayFonts()
      this.syncDisplayQuery()
      this.wakeControls()
    },

    // clicking a footer dot jumps straight to that page and restarts the
    // cycle, so the landed-on page gets its full dwell and the rotbar
    // (keyed by page) restarts in sync
    setDisplayPage(idx) {
      if (!this.display_mode || this.isScreensaverDisplay) return
      const page = Math.max(0, Math.min(idx, this.displayPageCount - 1))
      if (page !== this.display_page) this.display_page = page
      this.armDisplayPageTimer()
      this.wakeControls()
    },

    // mirrors display state into ?display= so the running configuration is
    // bookmarkable for the kiosk machine. only the display key is touched,
    // so ?tenant= and ?src= survive. safe on /vsm2 as well: a replace with
    // only a query resolves against the current (unmatched) path in
    // vue-router 3, so the pre-router branch keeps rendering this component
    // (a logged-in customer/driver role could never have reached /vsm2, the
    // guard redirects the initial navigation).
    syncDisplayQuery() {
      const current = String(this.$route.query.display || '')
      const desired = this.display_mode ? this.display_theme : ''
      if (current === desired) return
      const query = Object.assign({}, this.$route.query)
      if (desired) query.display = desired
      else delete query.display
      const nav = this.$router.replace({ query })
      if (nav && nav.catch) nav.catch(() => {})
    },

    // lazy webfont link, and only for the themes that want one at all
    ensureDisplayFonts() {
      if (!DISPLAY_FONT_THEMES[this.display_theme]) return
      if (document.getElementById('vb-display-fonts')) return
      const link = document.createElement('link')
      link.id = 'vb-display-fonts'
      link.rel = 'stylesheet'
      link.href = VSM_DISPLAY_FONTS_URL
      document.head.appendChild(link)
    },

    requestDisplayFullscreen() {
      const el = this.$refs.display_root
      if (!el) return
      const request = el.requestFullscreen || el.webkitRequestFullscreen
      if (!request) return
      try {
        const p = request.call(el)
        if (p && p.catch) p.catch(() => {})
        this.did_request_fullscreen = true
      } catch (e) {
        // fullscreen is best effort; the overlay already covers the page
      }
    },

    onDisplayFullscreenChange() {
      const active = document.fullscreenElement || document.webkitFullscreenElement
      if (this.display_mode && this.did_request_fullscreen && !active) {
        this.did_request_fullscreen = false
        this.exitDisplay()
      }
    },

    onDisplayKeydown(e) {
      if (!this.display_mode) return
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault()
        this.exitDisplay()
      }
    },

    wakeControls() {
      this.controls_visible = true
      this.display_idle = false
      clearTimeout(this.display_idle_timer)
      this.display_idle_timer = setTimeout(() => {
        this.controls_visible = false
        this.display_idle = true
      }, 4000)
    },

    // while the pointer rests on the pill the idle timer is suspended, so
    // it cannot fade out under a hovering cursor
    pinControls() {
      this.controls_visible = true
      this.display_idle = false
      clearTimeout(this.display_idle_timer)
    },

    startDisplayTimers() {
      this.stopDisplayTimers()
      this.display_clock_now = Date.now()
      this.display_clock_timer = setInterval(() => { this.display_clock_now = Date.now() }, 1000)
      this.display_minute_timer = setInterval(() => { this.display_minute_tick += 1 }, 30000)
      this.armDisplayPageTimer()
      this.display_ss_timer = setInterval(this.advanceScreensaver, SS_STEP_MS)
    },

    // the page cycle is its own arm so a dot click can restart the dwell
    armDisplayPageTimer() {
      if (this.display_page_timer) clearInterval(this.display_page_timer)
      this.display_page_timer = setInterval(() => {
        if (this.isScreensaverDisplay) return
        const paging = this.displayPageCount > 1
        let wrapped = !paging
        if (paging) {
          const next = (this.display_page + 1) % this.displayPageCount
          wrapped = next === 0
          this.display_page = next
        }
        // cycle sky: one grade per full pass through the pages; rosters
        // that fit a single page roll on a once-a-minute floor instead
        if (wrapped && this.display_theme === 'outrun' && this.display_sky === 'cycle') {
          if (paging || Date.now() - (this._sky_cycle_at || 0) >= 60000) {
            this.display_sky_pos = (this.display_sky_pos + 1) % DISPLAY_SKY_CYCLE.length
            this._sky_cycle_at = Date.now()
          }
        }
      }, 12000)
    },

    // screen saver tick: advance the row ticker, and flip the light/dark
    // palette at a pass boundary (or on the plain clock when the roster
    // fits on one screen) once the swap floor has elapsed
    advanceScreensaver() {
      if (!this.display_mode || !this.isScreensaverDisplay) return
      const n = this.displayRows.length
      const scrolling = n > this.display_page_size
      if (scrolling) this.display_scroll_pos = (this.display_scroll_pos + 1) % n
      const atBoundary = scrolling ? this.display_scroll_pos === 0 : true
      if (atBoundary && Date.now() - this.display_ss_swap_at >= SS_SWAP_FLOOR_MS) {
        this.display_ss_palette = this.display_ss_palette === 'light' ? 'dark' : 'light'
        this.display_ss_swap_at = Date.now()
      }
    },

    resetScreensaverCycle() {
      this.display_scroll_pos = 0
      this.display_ss_palette = 'light'
      this.display_ss_swap_at = Date.now()
    },

    stopDisplayTimers() {
      if (this.display_clock_timer) clearInterval(this.display_clock_timer)
      if (this.display_minute_timer) clearInterval(this.display_minute_timer)
      if (this.display_page_timer) clearInterval(this.display_page_timer)
      if (this.display_ss_timer) clearInterval(this.display_ss_timer)
      clearTimeout(this.display_idle_timer)
      clearTimeout(this.display_resize_timer)
      this.display_clock_timer = this.display_minute_timer = this.display_page_timer = this.display_ss_timer = null
    },

    // rows per page from the real rendered row height so any screen,
    // 720p signage to a 4k lobby panel, fills without clipping
    measureDisplayPageSize() {
      if (!this.display_mode) return
      const board = this.$refs.display_board
      if (!board) return
      const head = board.querySelector('.vb-dp-row--head')
      // measure the rows container itself where one exists: clientHeight on
      // the board counts its own padding (outrun's panel) as usable space
      // and over-fits by a row
      const rowsBox = board.querySelector('.vb-dp-rows')
      const avail = rowsBox ? rowsBox.clientHeight : (board.clientHeight - (head ? head.offsetHeight : 0))
      // fractional heights, and the TALLEST rendered row: probing only the
      // first row under-measured pages whose later rows stack a countdown
      // under the stamp, which over-fit the page and cut off the bottom row
      let rowH = 0
      board.querySelectorAll('.vb-dp-rows .vb-dp-row, .vb-dp-rows--solari .vb-sf-row').forEach(el => {
        const margin = parseFloat(getComputedStyle(el).marginBottom) || 0
        rowH = Math.max(rowH, el.getBoundingClientRect().height + margin)
      })
      if (!rowH) rowH = Math.max(48, window.innerHeight * 0.085)
      // no minimum beyond 1: forcing 3 rows into a viewport that fits 2
      // clips the bottom row (the old departures-inherited floor)
      const size = Math.max(1, Math.floor(avail / Math.max(1, rowH)))
      if (size !== this.display_page_size) {
        // a re-measure is a layout change, not a reading change: the rows
        // watcher consumes this flag and reveals the re-laid-out page from
        // blank instead of folding identical back faces over it
        this._display_resize = true
        this.display_page_size = size
        if (this.display_page >= this.displayPageCount) this.display_page = 0
      }
      // belt and braces after the rows render: if they still overflow the
      // box (theme-specific insets, sub-pixel accumulation), drop one row
      // rather than let the bottom row draw cut off. the screensaver is
      // exempt: its ticker's absolutely-positioned leave rows inflate
      // scrollHeight while mid-flight.
      this.$nextTick(() => {
        if (!this.display_mode || this.isScreensaverDisplay) return
        const boardNow = this.$refs.display_board
        const box = boardNow && boardNow.querySelector('.vb-dp-rows')
        if (box && box.scrollHeight > box.clientHeight + 1 && this.display_page_size > 1) {
          this._display_resize = true
          this.display_page_size -= 1
        }
      })
    },

    onDisplayResize() {
      if (!this.display_mode) return
      clearTimeout(this.display_resize_timer)
      this.display_resize_timer = setTimeout(this.measureDisplayPageSize, 150)
    },
  },
}
</script>

<style scoped>
.vb-root {
  /* tight 10px side gutters to match the support-ticket list spacing
     (.stl-root padding:0 + .stl-table-inner margin:0 10px); vertical kept */
  padding: 12px 10px 32px 10px;
  text-align: left;
  font-size: 14px;
  color: #1d1d1f;
}
@media (max-width: 720px) {
  .vb-root { padding: 12px 10px 24px 10px; }
}

/* header rail, matching the ops board / erm rail language */
.vb-rail {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  background: var(--fi-ops-rail-bg);
  border-left: 3px solid var(--fi-rail-accent);
  /* square rail edge, soft right side (support-ticket rail treatment) */
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
  margin-bottom: 8px;
}
.vb-rail-text {
  display: flex; flex-direction: column; justify-content: center;
  min-height: 32px;
  /* grow to push the controls to the right edge on one line; the min-width
     is what makes the control cluster wrap to its own row (rather than the
     title squeezing) once the header gets tight. content-driven, so it holds
     at any width and survives the wider title the tenant-context chip adds,
     where a fixed breakpoint would leave an awkward band. */
  flex: 1 1 auto;
  min-width: 240px;
}
/* solo page title (no eyebrow above it), so it sits a step larger than
   the eyebrow + title rails elsewhere in the ops family */
.vb-rail-title {
  margin: 0; padding: 0;
  font-size: 22px; line-height: 1.2;
  font-weight: 700; color: #1d1d1f;
  letter-spacing: -0.1px;
  display: flex; align-items: center; gap: 8px;
}
/* built entirely from theme-resolved tokens, so it needs no midnight.css rule:
   surface / line-strong / text-muted all re-resolve dark, and an outline pill
   stays quiet next to a 22px bold title in every theme. */
.vb-rail-preview-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  border: 1px solid var(--fi-line-strong);
  background: var(--fi-surface);
  color: var(--fi-text-muted);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  white-space: nowrap;
}
/* admin tenant-context line: names whose data the page renders when the
   route tenant differs from the navbar selection. eyebrow-style text in
   the old eyebrow slot (the ops rail language), so it left-aligns with
   the title and never pushes the action cluster to a second line. */
.vb-rail-tenant-eyebrow {
  font-size: 11px; line-height: 1.4;
  text-transform: uppercase; letter-spacing: 0.6px;
  color: var(--fi-ops-eyebrow); font-weight: 600;
  margin-bottom: 1px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
/* right cluster: the control buttons with the freshness stamp tucked beneath
   them, so "updated" reads next to the refresh/auto controls it belongs with.
   no margin-left:auto — rail-text's grow pushes this to the right edge on one
   line, and it left-aligns cleanly when it wraps to its own row. */
.vb-rail-actions-col {
  display: flex; flex-direction: column; align-items: flex-end; gap: 4px;
  flex: 0 0 auto;
}
.vb-rail-actions {
  display: inline-flex; align-items: center; gap: 6px;
  flex-shrink: 0;
}
.vb-base-select { width: 170px; }

@media (max-width: 600px) {
  /* keep the title and controls on ONE row (controls pinned right) rather than
     stacking the cluster onto its own full-width row -- saves vertical space on
     phones. the title shrinks and wraps in place; the cluster is compact now
     that the manual-refresh button is gone. */
  .vb-rail { padding: 10px 12px; gap: 8px; }
  .vb-rail-text { flex: 1 1 auto; min-width: 0; }
  .vb-rail-title { font-size: 18px; }
  /* right-pinned control cluster; buttons wrap among themselves only if a wide
     base-select forces it, and stay right-aligned when they do */
  .vb-rail-actions-col { align-items: flex-end; }
  .vb-rail-actions { flex-wrap: wrap; justify-content: flex-end; }
  /* the freshness stamp is secondary; drop it on a tight header */
  .vb-rail-updated { display: none; }
  /* compact base select so the control row fits one line */
  .vb-base-select { width: 108px; }
}

/* inline load error, same look as the ops board banner */
.vb-error {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; flex-wrap: wrap;
  background: #fff8ef;
  border: 1px solid #f0d9b5;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #7a5400;
}

/* filter chips */
.vb-chip-filters {
  display: flex; flex-wrap: wrap; gap: 14px;
  align-items: center;
  margin-bottom: 12px;
}
/* the pill cluster (available + both groups); inline-wraps on desktop, one
   swipeable row on mobile */
.vb-chip-scroll {
  display: flex; flex-wrap: wrap; gap: 14px;
  align-items: center;
  /* size to content (do not grow) so the tags trigger packs in right after the
     maint group; .vb-search keeps margin-left:auto to hold the right edge */
  flex: 0 1 auto; min-width: 0;
}
.vb-chip-group {
  display: flex; flex-wrap: wrap; gap: 6px;
  align-items: center;
}
.vb-chip-group-label {
  font-size: 10.5px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.4px;
  color: #6b7280;
}
.vb-chip {
  position: relative;
  background: #f8fafc; border: 1px solid #d6dde6; border-radius: 999px;
  padding: 4px 12px; font-size: 12.5px; font-weight: 600; color: #4b5563;
  cursor: pointer; transition: background .12s, border-color .12s, color .12s, filter .12s;
  white-space: nowrap;
}
/* invisible hit-area extension to ~38px; the painted pill stays the same */
.vb-chip::before {
  content: '';
  position: absolute;
  top: -6px; bottom: -6px; left: 0; right: 0;
}
.vb-chip:not([class*="vb-fc-"]):hover { background: #eef4fb; border-color: #b8c8da; }
.vb-chip--active {
  background: var(--fi-ops-accent); border-color: var(--fi-ops-accent); color: #fff;
}
.vb-chip--active:hover { background: var(--fi-ops-accent-strong); border-color: var(--fi-ops-accent-strong); }

/* filter pills echo the row status-chip palette: a subtle tint when idle,
   the chip's own ink as a solid fill when active. colors mirror
   WASH_CHIP_CLASSES / MAINT_CHIP_CLASSES (.vb-chip--green etc.). */
/* idle pills carry their status color clearly — a medium tint of the bold
   hue plus the full bold-hue border, so the row reads as the new palette;
   selected pills fill solid (the exact chip color, below) */
.vb-fc-green  { background: #fff; border-color: #1fab42; color: #146230; }
.vb-fc-cyan   { background: #fff; border-color: #1099bf; color: #095f78; }
.vb-fc-teal   { background: #fff; border-color: #1f9a7c; color: #105748; }
.vb-fc-blue   { background: #fff; border-color: #1668d6; color: #11468f; }
.vb-fc-brown  { background: #fff; border-color: #a85e22; color: #76411a; }
.vb-fc-purple { background: #fff; border-color: #8a3fb5; color: #5f2780; }
.vb-fc-amber  { background: #fff; border-color: #d39410; color: #7e5407; }
.vb-fc-red    { background: #fff; border-color: #d62f2f; color: #b22525; }
.vb-fc-gray   { background: #fff; border-color: #6b7280; color: #4b5563; }
.vb-chip[class*="vb-fc-"] { border-width: 1.5px; }
/* idle outline pills get a neutral wash on hover; brightness() would dim the
   white fill to near-invisible. scoped to idle so the active solid fill keeps
   its own hover. */
.vb-chip[class*="vb-fc-"]:not(.vb-chip--active):hover { background: #f4f7fa; }
.vb-fc-green.vb-chip--active  { background: #1fab42; border-color: #1fab42; color: #15202b; }
.vb-fc-cyan.vb-chip--active   { background: #1099bf; border-color: #1099bf; color: #15202b; }
.vb-fc-teal.vb-chip--active   { background: #1f9a7c; border-color: #1f9a7c; color: #15202b; }
.vb-fc-amber.vb-chip--active  { background: #d39410; border-color: #d39410; color: #15202b; }
.vb-fc-blue.vb-chip--active   { background: #1668d6; border-color: #1668d6; color: #fff; }
.vb-fc-brown.vb-chip--active  { background: #a85e22; border-color: #a85e22; color: #fff; }
.vb-fc-purple.vb-chip--active { background: #8a3fb5; border-color: #8a3fb5; color: #fff; }
.vb-fc-red.vb-chip--active    { background: #d62f2f; border-color: #d62f2f; color: #fff; }
.vb-fc-gray.vb-chip--active   { background: #5f6772; border-color: #5f6772; color: #fff; }

/* tags: compact menu trigger sitting in the filter row next to the chip
   groups; outside .vb-chip-scroll so its menu isn't clipped by that row's
   overflow. */
.vb-tag-dd { flex: 0 0 auto; }
/* transparent on desktop: tag-dd + search lay out inline in .vb-chip-filters
   exactly as before (search keeps its margin-left:auto right pull). becomes a
   real flex row at mobile widths -- see the max-width:640px block. */
.vb-tag-search-row { display: contents; }
.vb-tag-dd >>> .dropdown-toggle {
  border-radius: 999px; font-size: 12.5px; font-weight: 600;
  padding: 4px 12px;
}
.vb-tag-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 16px; height: 16px; margin-left: 6px; padding: 0 4px;
  border-radius: 999px; background: var(--fi-ops-accent); color: #fff;
  font-size: 10.5px; line-height: 1;
}
.vb-tag-opt {
  display: flex; align-items: center; gap: 8px;
  margin: 0; padding: 3px 2px; font-size: 13px; cursor: pointer;
  white-space: nowrap;
}
.vb-tag-opt-count { margin-left: auto; color: #6b7280; font-size: 11.5px; }
.vb-tag-form { max-height: 320px; overflow-y: auto; }

/* find-vehicle/driver search, right-aligned in the filter row */
.vb-search {
  margin-left: auto;
  position: relative;
  display: inline-flex; align-items: center;
  flex: 0 1 240px;
}
.vb-search-icon {
  position: absolute; left: 11px;
  color: #9aa3ad; font-size: 12px; pointer-events: none;
}
.vb-search-input {
  width: 100%;
  padding: 5px 26px 5px 28px;
  font-size: 13px;
  border: 1px solid #d6dde6; border-radius: 999px;
  background: #fff; color: #1d1d1f;
  transition: border-color .12s, box-shadow .12s;
}
.vb-search-input::placeholder { color: #9aa3ad; }
.vb-search-input:focus {
  outline: none;
  border-color: var(--fi-ops-accent);
  box-shadow: 0 0 0 2px rgba(58, 110, 153, 0.2);
}
.vb-search-clear {
  position: absolute; right: 6px;
  background: none; border: none; padding: 2px 5px;
  color: #9aa3ad; cursor: pointer; font-size: 12px; line-height: 1;
}
/* invisible hit-area extension to ~32px, matching the .vb-chip-btn convention */
.vb-search-clear::before {
  content: '';
  position: absolute;
  top: -9px; bottom: -9px; left: -7px; right: -7px;
}
.vb-search-clear:hover { color: #4b5563; }
@media (max-width: 640px) {
  /* the filter block pins to the top of the scroll so search + pills stay
     reachable down a long card list. the navbar is in normal flow (not
     fixed), so it scrolls off and top:0 lands at the viewport edge. the
     negative side margins bleed the white backing to the screen edges so
     cards do not show through the gutters when stuck. */
  .vb-chip-filters {
    position: sticky; top: 0; z-index: 20;
    flex-direction: column; align-items: stretch; gap: 8px;
    background: #fff;
    margin: 0 -16px 10px -16px;
    padding: 8px 16px;
    border-bottom: 1px solid #eef0f3;
  }
  /* tags + search share one row below the status pills (order:1 places that
     row after the default-order chip scroller). tags lead at their natural
     width, search fills the rest. 16px input dodges iOS focus-zoom. */
  .vb-tag-search-row {
    display: flex; align-items: center; gap: 8px; order: 1;
  }
  .vb-search { margin-left: 0; flex: 1 1 auto; width: auto; min-width: 0; }
  .vb-search-input { font-size: 16px; }
  /* one swipeable pill row instead of three stacked groups. the scrollbar
     chrome read as janky, so it's hidden; a soft right-edge fade signals
     there's more to swipe without a visible track. */
  .vb-chip-scroll {
    flex-wrap: nowrap; overflow-x: auto; gap: 10px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    /* as an overflow-x scroll container this flex child won't honor the
       column parent's stretch, so pin the width and bleed past the bar's
       right padding to the screen edge — that puts the fade where it's
       actually visible instead of in the clipped-off region. */
    width: calc(100% + 16px);
    margin-right: -16px;
    -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 22px), transparent);
    mask-image: linear-gradient(to right, #000 calc(100% - 22px), transparent);
  }
  .vb-chip-scroll::-webkit-scrollbar { display: none; }
  /* trailing scroll space (a flex spacer, more reliable than end padding on
     iOS Safari) so the last pill can swipe clear of the right-edge fade and
     the -16px bleed instead of staying stuck under them. without it a wide
     last chip like "Available (33)" can never be read at the end of the row.
     sized past the 22px mask so the final chip lands fully in the clear.
     (the old worst case named here was "Unsafe DVIR"; the maint labels are
     now "Pending"/"Unsafe", so measure against the counted filter pills.) */
  .vb-chip-scroll::after {
    content: '';
    flex: 0 0 28px;
  }
  /* pin the group wrappers too — the rule below only pins the chips, so the
     groups kept flex-shrink:1 and collapsed under the no-wrap row, letting
     their chips spill out and overlap. */
  .vb-chip-group { flex-wrap: nowrap; flex: 0 0 auto; }
  /* tags trigger sits at its natural width at the head of the tag/search row */
  .vb-tag-dd { align-self: auto; flex: 0 0 auto; }
  .vb-chip-scroll > .vb-chip,
  .vb-chip-group > .vb-chip { flex: 0 0 auto; }
}

/* a mouse can't thumb-swipe: on fine-pointer devices the pill row wraps
   instead of scrolling sideways. the swipe strip hides its scrollbar by
   design, so when a 2-D wheel (magic mouse, free-spin wheels) pans it
   horizontally the filters end up stranded mid-scroll with no visible way
   back. real phones (coarse pointer) keep the one-row swipe strip. */
@media (max-width: 640px) and (hover: hover) and (pointer: fine) {
  .vb-chip-scroll {
    flex-wrap: wrap;
    overflow-x: visible;
    width: 100%;
    margin-right: 0;
    -webkit-mask-image: none;
    mask-image: none;
  }
  .vb-chip-scroll::after { content: none; }
}

/* table card */
.vb-card {
  background: #fff;
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  padding: 0;
  /* overflow visible so the status picker (a root-level position:fixed popover)
     and chip hover rings are never clipped by the card. the table's horizontal
     scroll lives on the inner .table-responsive wrapper, not here. */
  overflow: visible;
}

/* quiet fact card for tenants without a telematics feed */
.vb-no-telematics {
  background: #fff;
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  padding: 44px 24px;
  text-align: center;
  color: #6b7280;
}
.vb-no-telematics .fa {
  font-size: 26px;
  color: #b9c2cc;
  margin-bottom: 10px;
}
.vb-no-telematics-title {
  font-size: 15px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 4px;
}
.vb-no-telematics-sub {
  font-size: 13px;
  max-width: 52ch;
  margin: 0 auto;
  line-height: 1.5;
}
.vb-card >>> .table.b-table { margin-bottom: 0; }

/* keep bootstrap-vue's responsive wrapper as the horizontal scroll container at
   all widths so a fleet with more columns than the viewport is wide stays
   swipeable instead of bleeding off the right edge and getting clipped (this is
   how the Support Ticket list behaves). the status picker is a root-level
   position:fixed popover, so this overflow never clips it. overflow-y is pinned
   to hidden rather than left to compute to auto from overflow-x:auto, so classic
   (non-overlay) scrollbars in Safari/Firefox can't spawn a phantom vertical
   scrollbar; the wrapper is content-height (no max-height) so a tall fleet is
   never clipped and the page scrolls to reveal every row. trade-off: the thead
   anchors to this scroll container rather than page-pinning, same as the Support
   Ticket list's header. */
.vb-card >>> .table-responsive { overflow-x: auto; overflow-y: hidden; }

/* the canonical dense table pattern; deep selectors because bootstrap-vue
   renders the inner table */
.vb-root >>> .table.b-table { font-size: 13px; border-collapse: collapse; }
.vb-root >>> .table.b-table th,
.vb-root >>> .table.b-table td {
  border-left: none !important; border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  padding: 3px 8px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
.vb-root >>> .table.b-table th:first-child,
.vb-root >>> .table.b-table td:first-child { padding-left: 12px !important; }
.vb-root >>> .table.b-table th:last-child,
.vb-root >>> .table.b-table td:last-child   { padding-right: 12px !important; }
.vb-root >>> .table.b-table thead th {
  background: #f8f9fa;
  font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; color: #555;
  position: sticky; top: 0; z-index: 2;
  box-shadow: inset 0 -2px 0 0 #ccc;
}
/* the details band opts out of the row hover wash: it reads as an extension
   of the parent row, not a hoverable row of its own */
.vb-root >>> .table.b-table tbody tr:not(.b-table-details):hover td { background-color: #f0f5ff; }
/* problem rows get a left edge that matches the card rails (late = red,
   attention = amber); ready/idle rows stay clean so the eye finds problems */
.vb-root >>> .table.b-table tr.vb-row-late td:first-child { box-shadow: inset 3px 0 0 0 #d64545; }
.vb-root >>> .table.b-table tr.vb-row-attention td:first-child { box-shadow: inset 3px 0 0 0 #e0a32e; }

.vb-root >>> th[aria-sort] { background-image: none !important; padding-right: 0.75rem !important; white-space: nowrap; }
.vb-root >>> th[aria-sort]::before { display: none !important; }
.vb-root >>> th[aria-sort]::after  { display: inline-block !important; margin-left: 4px; font-size: 10px; color: #999; vertical-align: middle; float: none !important; width: auto !important; height: auto !important; opacity: 1 !important; }
.vb-root >>> th[aria-sort="none"]::after       { content: "\2195" !important; }
.vb-root >>> th[aria-sort="ascending"]::after  { content: "\25B2" !important; color: var(--fi-rail-accent); }
.vb-root >>> th[aria-sort="descending"]::after { content: "\25BC" !important; color: var(--fi-rail-accent); }

/* busy / empty states */
.vb-busy {
  padding: 24px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
}
.vb-empty {
  padding: 14px 12px;
  font-size: 13px;
  color: #6b7280;
}
.vb-clear-btn {
  background: none; border: none; padding: 0; margin-left: 6px;
  font-size: 13px; color: var(--fi-ops-accent-strong); text-decoration: underline;
  cursor: pointer;
}
.vb-clear-btn:hover { color: var(--fi-ops-accent-deep); }

/* expand (history) button */
.vb-expand-btn {
  position: relative;
  width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  background: #fff; border: 1px solid #d6dde6; border-radius: 6px;
  color: #4b5563; cursor: pointer; padding: 0;
}
/* invisible hit-area extension to ~40px around the 28px button */
.vb-expand-btn::before {
  content: '';
  position: absolute;
  top: -6px; bottom: -6px; left: -6px; right: -6px;
}
.vb-expand-btn:hover { background: #eef4fb; border-color: #b8c8da; }
.vb-expand-btn .fa { font-size: 11px; transition: transform .15s ease; }
.vb-expand-btn--open .fa { transform: rotate(90deg); }

/* vehicle name link */
.vb-vehicle-link { font-weight: 600; white-space: nowrap; }

/* status chips: a quiet two-tier tag. set statuses render as a soft tinted
   tag (hue text on a light wash + hairline); the alarm states (out, oos,
   unsafe) keep a solid fill so they stay loud in a field of quiet tags; unset
   is a ghost. shape stays the support-ticket priority-tag rectangle so the
   wash/maint chips still column up regardless of label. */
.vb-status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  /* fixed width so wash/maint buttons line up regardless of label; the rare
     system-set DVIR labels can still grow past this */
  min-width: 88px;
  padding: 3px 8px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border: 1px solid transparent;
  color: #fff;
  white-space: nowrap;
}
/* set statuses: soft tinted tags. hue text on a light wash with a matching
   hairline — quiet enough that a wall of them never out-shouts the departure
   columns, but the hue still encodes status at a glance. text colors verified
   AA (>=4.5:1) on their tint. */
.vb-chip--green  { background: #e8f6ec; border-color: #bfe3cb; color: #14792f; }  /* READY */
.vb-chip--cyan   { background: #e3f3f9; border-color: #b2deec; color: #0a6c88; }  /* CLEANED */
.vb-chip--teal   { background: #e6f4ef; border-color: #b6e0d3; color: #15705a; }  /* WASHED */
.vb-chip--amber  { background: #fbf0d6; border-color: #f0dba4; color: #8a5a06; }  /* PENDING_DVIR */
.vb-chip--blue   { background: #e7f0fc; border-color: #bdd5f3; color: #1255b0; }  /* PM */
.vb-chip--brown  { background: #f5ece2; border-color: #e6d1bc; color: #884b1a; }  /* DIRTY */
/* alarm states stay loud: a fully filled chip is reserved for the statuses
   that warrant an alarm in a field of quiet tags */
.vb-chip--purple { background: #8a3fb5; border-color: #8a3fb5; color: #fff; }  /* OUT */
.vb-chip--red    { background: #d62f2f; border-color: #d62f2f; color: #fff; }  /* OOS / UNSAFE */
/* unset / unknown: a ghost — no fill, faint hairline, muted dash. the most
   common cell on a real fleet, so it stays the quietest thing on the row */
.vb-chip--gray   { background: #f7f9fb; border-color: #dbe1e8; color: #6b7280; }  /* unset */
/* change-dimmed history chip: a value carried over unchanged from the
   previous snapshot. hue stays reserved for the value that changed, so a
   history list scans as a series of events instead of repeated state */
.vb-status-chip.vb-status-chip--dim { background: transparent; border-color: #dbe1e8; color: #6b7280; }
/* caret tucked inside the trigger chip so wash/maint read as dropdown
   buttons; read-only chips (history, non-editable role) omit it */
.vb-status-caret { margin-left: 6px; font-size: 10px; opacity: 0.9; }
/* the trigger pushes its caret to the right edge of the fixed-width chip
   (dropdown-button look); the picker options pin to the same width so the
   menu lands square over the button; history chips stay content-sized */
.vb-status-chip--trigger { justify-content: space-between; }
.vb-picker-pop .vb-status-opt .vb-status-chip { width: 88px; }
.vb-hist-line .vb-status-chip { min-width: 0; justify-self: start; }

/* clickable chip wrapper for the editable cells */
.vb-chip-btn {
  position: relative;
  background: none; border: none; padding: 0;
  display: inline-flex; align-items: center;
  cursor: pointer;
  border-radius: 5px;
}
/* invisible hit-area extension to ~36px over the short chip */
.vb-chip-btn::before {
  content: '';
  position: absolute;
  top: -9px; bottom: -9px; left: -4px; right: -4px;
}
.vb-chip-btn:hover .vb-status-chip { filter: brightness(1.1); box-shadow: 0 0 0 2px rgba(70, 130, 180, 0.25); }
/* the trigger whose picker is open reads as held: ringed chip + flipped caret */
.vb-chip-btn--active .vb-status-chip { box-shadow: 0 0 0 2px rgba(58, 110, 153, 0.55); }
.vb-chip-btn--active .vb-status-caret { transform: rotate(180deg); }

/* anchored status picker: a single floating popover (position: fixed, placed
   in JS from the trigger's rect) shared by the whole board. opening it never
   moves the neighboring chip, and fixed positioning escapes the table's
   overflow scrollport so it is never clipped. */
.vb-picker-pop {
  position: fixed;
  z-index: 1080;
  display: flex; flex-direction: column; align-items: stretch; gap: 2px;
  min-width: 150px; max-width: 240px;
  background: #fff;
  border: 1px solid #d6dde6;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(20, 40, 60, 0.16);
  padding: 5px;
}
/* one full-width menu row per option: the solid status chip on the left, a
   check on the current one. comfortable row height so a mis-tap onto an
   adjacent destructive status is hard */
.vb-status-opt {
  background: none; border: none; margin: 0;
  cursor: pointer; border-radius: 6px; line-height: 1;
  width: 100%; min-height: 40px; padding: 4px 8px;
  display: inline-flex; align-items: center; justify-content: space-between; gap: 10px;
}
.vb-status-opt:hover { background: #f2f5f8; }
.vb-status-opt--current { background: #eef4fb; }
.vb-status-opt-check { color: var(--fi-ops-accent); font-size: 12px; }
.vb-status-opt:focus-visible { outline: 2px solid var(--fi-ops-accent); outline-offset: -2px; }
@media (max-width: 640px) {
  /* roomier menu + 44px rows on phones */
  .vb-picker-pop { min-width: 180px; max-width: calc(100vw - 24px); padding: 6px; }
  .vb-status-opt { min-height: 44px; }
  .vb-status-opt .vb-status-chip { font-size: 12px; }
}

/* family focus standard */
.vb-chip:focus-visible,
.vb-chip-btn:focus-visible,
.vb-expand-btn:focus-visible,
.vb-clear-btn:focus-visible {
  outline: 2px solid var(--fi-ops-accent);
  outline-offset: 2px;
}

/* notes */
.vb-notes-input { min-width: 180px; transition: border-color .25s, background-color .25s; }
/* brief confirmation after a successful blur-save; a timer clears it */
.vb-notes-input--saved { border-color: #b5e3c5; background-color: #e7f8ed; }

/* departs-in urgency; on-time keeps default ink, soon is amber, late is red */
.vb-late { color: #b91c1c; font-weight: 600; }
.vb-soon { color: #b45309; font-weight: 600; }
.vb-stack-line { display: block; }

/* status history detail panel: grid rows aligned under the parent table's
   columns via the measured css vars (see measureHistCols). the -12px bleed
   undoes the details cell's first-child padding so each entry's rail lands
   on the table's left edge, the same x as the parent rows' rails; the
   width:0/min-width:100% pair keeps long notes from feeding back into the
   auto table layout's column widths (a zero-width block contributes nothing
   to intrinsic sizing but still fills the cell at layout time). */
.vb-history { margin-left: -12px; width: 0; min-width: 100%; padding: 1px 0 6px; }
.vb-hist2-row {
  display: grid;
  grid-template-columns: var(--vbh-c1, 260px) var(--vbh-c2, 120px) var(--vbh-c3, 120px) minmax(0, 1fr);
  align-items: center;
  padding: 3px 0;
  font-size: 12px;
}
.vb-hist2-row + .vb-hist2-row { border-top: 1px solid #eef0f3; }
.vb-hist2-row--late { box-shadow: inset 3px 0 0 0 #d64545; }
.vb-hist2-row--attention { box-shadow: inset 3px 0 0 0 #e0a32e; }
/* stamp + author share the vehicle column's track; the author wraps under
   the stamp (then ellipsizes) rather than pushing into the wash chips */
.vb-hist2-when-cell {
  display: flex; flex-wrap: wrap; align-items: baseline; column-gap: 6px;
  min-width: 0;
  padding-left: calc(var(--vbh-ind, 40px) + 8px);
}
.vb-hist2-cell { padding-left: 8px; min-width: 0; }
.vb-hist2-note { color: #4b5563; }
.vb-hist-note-icon { margin-right: 6px; color: #9aa3ad; font-size: 11px; }
.vb-history-empty { font-size: 12px; color: #6b7280; font-style: italic; }
.vb-history-error { font-size: 12px; color: #6b7280; }
/* the measured indent applies to the desktop expansion only; the sheet's
   copies of these states sit flush in its own padding */
.vb-history .vb-history-empty, .vb-history .vb-history-error { padding: 2px 0 2px calc(var(--vbh-ind, 40px) + 8px); }
.vb-history-error .vb-clear-btn { font-size: 12px; }

/* mobile card layout (max-width: 640px branch) */
/* bottom room so the last card clears the floating "Ask Fleet Assistant"
   launcher (fixed, bottom-right) instead of tucking under it */
.vb-card-list { display: flex; flex-direction: column; gap: 12px; padding-bottom: 84px; }
.vb-vcard {
  position: relative;
  background: #fff;
  border: 1px solid #e0e6ec;
  border-left: 5px solid #e0e6ec;
  /* square the railed (left) edge so the colored bar reads as a rectangle,
     not a rounded lozenge — mirrors the support-ticket admin-rail trick
     (border-radius 0 on the railed side) */
  border-radius: 0 10px 10px 0;
  padding: 9px 10px;
}
/* readiness rail: red = late / out of service, amber = soon / needs work,
   green = ready, neutral otherwise. differentiates the cards at a glance and
   makes a late or out-of-service bus pop. */
.vb-vcard--late { border-left-color: #d64545; }
.vb-vcard--attention { border-left-color: #e0a32e; }
.vb-vcard--ready { border-left-color: #46a36b; }
.vb-vcard--none { border-left-color: #e0e6ec; }
/* visually-hidden helper: screen-reader-only text */
.vb-sr {
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0);
  white-space: nowrap; border: 0;
}
/* two columns: expander | body (name over chips) | departs. align-items
   flex-start so the expander and the deck sit against the name's line rather
   than centering on the taller two-row body. */
.vb-vcard-line { display: flex; align-items: flex-start; gap: 10px; min-width: 0; }
.vb-vcard-line .vb-expand-btn,
.vb-vcard-line .vb-chip-btn,
.vb-vcard-chiprow > .vb-status-chip { flex-shrink: 0; }
/* the body takes the slack; the chip row below it has width to spare, which is
   what pays for the larger type on the name and the countdown */
/* the 14px gap is load-bearing: the chip buttons' -10px top margin spends 10px
   of it on their invisible tap overflow, leaving 4px of real clearance between
   the name link and the chip's tap box. keep gap > 10px or the chips start
   eating taps meant for the name. */
.vb-vcard-body {
  display: flex; flex-direction: column; align-items: flex-start; gap: 14px;
  min-width: 0; flex: 1 1 auto;
}
/* row-gap is NOT cosmetic. a flex line's cross size comes from the MARGIN box
   (44 - 10 - 14 = 20px), but the button still paints and hit-tests 44px tall,
   so it hangs 10px above and 14px below its own line. two wrapped lines
   therefore overlap by (44 - 20 - row-gap); at the old 8px that was a 16px
   overlap, and since the maint button is later in the DOM and positioned, it
   painted on top and STOLE taps aimed at the wash chip above it - one tap on
   "Dirty" opened the maintenance picker instead. row-gap must be >= 24px
   (border box - margin box) to separate them; 26px leaves 2px of slack.
   the row wraps below ~325px of card width, and as high as ~380px for tenants
   whose summary.datetime_format is long (formatStamp honors it verbatim and
   the depart deck is flex-shrink:0), so this is reachable on a real iPhone SE,
   not just a 320px edge case. */
.vb-vcard-chiprow { display: flex; column-gap: 8px; row-gap: 26px; flex-wrap: wrap; min-width: 0; }
/* the name owns its row now, so it no longer needs a min-width floor to fight
   the chips for space; it still ellipsises rather than wrap if a tenant runs
   genuinely long vehicle names */
.vb-vcard-line .vb-vehicle-link {
  font-size: 17px; font-weight: 700; line-height: 1.15;
  min-width: 0; max-width: 100%;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
/* shrunken chips: the table's fixed 88px boxes scale down but stay fixed so
   wash/maint still column up card-to-card */
.vb-vcard-line .vb-status-chip {
  min-width: 64px;
  font-size: 11.5px; padding: 4px 8px;
}
.vb-vcard-line .vb-status-caret { font-size: 10px; }
/* ghost placeholder for an unset editable status: no fill, dashed hairline,
   a muted "Set" that reads as "tap to set" rather than a real gray status */
.vb-vcard-line .vb-status-chip--set {
  background: transparent; border-style: dashed;
  border-color: #c3ccd6; color: #8a93a0;
}
/* ios safari turns a <button> whose content overflows into a touch-scroll port
   and clips it. the old ::before hit-extenders were absolutely positioned past
   the button edges, so scrollHeight exceeded clientHeight and the pills scrolled
   under a finger + clipped at the bottom. take the 44px tap height from the pill
   button's own box instead (invisible: the button has no fill/border, only the
   inner chip shows) and pull the footprint back with negative margin so the row
   height is unchanged; drop the overflowing pseudo. the expander keeps a slightly
   larger visible box rather than an overflowing hit-area.
   the margins are ASYMMETRIC on purpose: the chip row sits directly under the
   vehicle-name link, so a symmetric -12/-12 would push the invisible 44px box
   6px up into the name and steal taps meant for it (the button paints later, so
   it would win). -10 sits inside the body's 14px gap, leaving 4px of clearance
   under the name, and the extra -14 sends the remainder down through the card's
   9px padding into the 12px inter-card gap, where nothing is tappable.
   THREE COUPLED INVARIANTS - change one, re-check the others:
     1. .vb-vcard-body gap (14px) MUST exceed this top margin (10px), or the
        chips eat the name's taps.
     2. .vb-vcard-chiprow row-gap (26px) MUST be >= 44 - 20 = 24px, or two
        WRAPPED chip rows overlap each other's tap boxes.
     3. the card's bottom padding + the .vb-card-list gap MUST absorb the 14px
        bottom overhang, or the last chip reaches into the next card. */
.vb-vcard-line .vb-chip-btn {
  min-height: 44px;
  margin-top: -10px; margin-bottom: -14px;
}
.vb-vcard-line .vb-chip-btn::before { content: none; }
/* the 44px pill button is taller than the ~24px chip, so a :focus-visible
   outline on the button draws an oversized ring — ring the visible chip. */
.vb-vcard-line .vb-chip-btn:focus-visible { outline: none; }
.vb-vcard-line .vb-chip-btn:focus-visible .vb-status-chip {
  outline: 2px solid var(--fi-ops-accent); outline-offset: 2px;
}
/* expander loses its overflowing hit-extender (same ios reason); its visible
   box IS the tap target and midnight.css recolors this element, so the box
   can't move to the glyph — size it up to 36px for a comfortable target. */
.vb-vcard-line .vb-expand-btn { width: 36px; height: 36px; }
.vb-vcard-line .vb-expand-btn::before { content: none; }
/* depart deck rides the right edge: countdown on the line, the precise stamp
   as a quiet sub-line beneath it */
.vb-vcard-depart { display: flex; flex-direction: column; align-items: flex-end; gap: 1px; text-align: right; flex-shrink: 0; margin-left: auto; padding-top: 1px; }
.vb-vcard-depart-main { display: flex; flex-direction: row; align-items: baseline; gap: 5px; }
.vb-vcard-depart-label {
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; color: #6b7280;
}
.vb-vcard-depart-val { font-size: 17px; font-weight: 600; color: #1d1d1f; line-height: 1.2; white-space: nowrap; }
/* the caption goes visually-hidden (not display:none) below 520px so screen
   readers still announce the value as a departure time; the precise stamp
   under it keeps the countdown legible for sighted users. re-measured
   2026-07-22: with the countdown at 17px an inline caption DOES cost real
   width at 375 ("departs in 17h 24m" squeezes the chip row into wrapping
   towers via the 26px wrap gap), so this stays load-bearing. */
@media (max-width: 520px) {
  .vb-vcard-depart-label {
    position: absolute;
    width: 1px; height: 1px;
    padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0, 0, 0, 0);
    white-space: nowrap; border: 0;
  }
}
/* a day-plus out reads quiet: normal weight, medium gray. only same-day
   countdowns (and the late/soon states below) keep the bold dark ink. */
.vb-vcard-depart-val--distant { font-weight: 400; color: #6b7280; }
/* precise next-depart stamp under the countdown */
.vb-vcard-depart-when { font-size: 12px; color: #6b7280; line-height: 1.2; }
/* return band (ticket 1566): full-width strip under the main row on a
   whisper of tint - the card above it stays exactly the shipped layout.
   negative margins cancel the card's 9px/10px padding so the hairline +
   tint run edge to edge; the bottom-right radius nests inside the card's
   10px (the railed left edge is already square). 12px type only - the
   depart deck keeps exclusive rights to 17px, so the card holds a single
   focal point. */
.vb-vcard-return-band {
  display: flex; align-items: baseline; justify-content: space-between; gap: 12px;
  margin: 8px -10px -9px -10px;
  padding: 7px 10px 8px 10px;
  border-top: 1px solid rgba(17, 24, 39, 0.06);
  background: rgba(17, 24, 39, 0.025);
  border-radius: 0 0 9px 0;
}
.vb-band-pair { display: inline-flex; align-items: baseline; gap: 6px; min-width: 0; }
/* eta rides the right edge (and holds it if the schedule stamp is absent) */
.vb-band-pair--eta { margin-left: auto; }
.vb-band-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; color: #6b7280; white-space: nowrap; }
.vb-band-time { font-size: 12px; font-weight: 500; color: #1d1d1f; white-space: nowrap; }
.vb-band-time--dim { font-weight: 400; color: #6b7280; }
/* still out past the scheduled return with no live eta: actually late,
   so this is inside red's "actual lateness" reservation */
.vb-band-time--late { color: #b91c1c; }
/* live inbound eta - soon-amber + 600: its presence means the bus is
   actively on its way in (the api only populates it inside the return
   window), the hottest fact on the wall */
.vb-band-eta { font-size: 12px; font-weight: 600; color: #b45309; white-space: nowrap; }
/* urgency wins over the depart-val default ink so the card time matches the
   list's late/almost-late coloring (same single-class specificity otherwise
   lets the later default rule win, leaving the card time stuck dark) */
.vb-vcard-depart-val.vb-late { color: #b91c1c; }
.vb-vcard-depart-val.vb-soon { color: #b45309; }
.vb-chip-btn:hover .vb-status-chip--set {
  border-color: var(--fi-ops-accent); color: var(--fi-ops-accent-strong);
}
/* aux row (card face): compact LABEL value pairs for the tenant's promoted
   secondary fields. plain text - taps here fall through to cardTap. the 4px
   top margin stacks on the body's 14px gap so the chips' 44px tap boxes
   (14px overhang) clear this row by 4px, mirroring the name-side invariant. */
.vb-vcard-auxrow {
  display: flex; flex-wrap: wrap; column-gap: 14px; row-gap: 2px;
  margin-top: 4px; min-width: 0;
}
.vb-aux-pair { display: inline-flex; align-items: baseline; gap: 5px; min-width: 0; }
.vb-aux-label {
  font-size: 10px; font-weight: 700; letter-spacing: .4px;
  text-transform: uppercase; color: #9aa3ad; white-space: nowrap;
}
.vb-aux-val { font-size: 12.5px; color: #374151; white-space: nowrap; }

/* detail bottom sheet (replaces the old in-card expansion): scrim wrapper +
   a bottom-pinned panel. z-index above the sticky toolbar (20) and the
   status picker (1080). the inner detail classes (.vb-vcard-row, history)
   are shared with the old expansion so their rules apply unchanged. */
.vb-sheet-wrap {
  position: fixed; inset: 0; z-index: 1090;
  background: rgba(16, 24, 34, 0.45);
  display: flex; align-items: flex-end;
}
.vb-sheet {
  width: 100%; max-height: 82vh; overflow-y: auto;
  background: #fff;
  border-radius: 14px 14px 0 0;
  padding: 6px 16px calc(16px + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -8px 28px rgba(16, 24, 34, 0.25);
}
.vb-sheet-grab { width: 36px; height: 4px; border-radius: 2px; background: #cdd6e0; margin: 4px auto 8px; }
.vb-sheet-head {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  margin-bottom: 6px;
}
.vb-sheet-title { font-size: 17px; font-weight: 700; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.vb-sheet-close {
  flex-shrink: 0; width: 44px; height: 44px; margin: -6px -10px -6px 0;
  border: none; background: none; border-radius: 8px;
  color: #6b7280; font-size: 16px; cursor: pointer;
}
.vb-sheet-close:hover { background: #eef4fb; color: #374151; }
/* slide-up: the wrapper fades, the panel translates. vue 2 transition class
   names (-enter, not -enter-from). */
.vb-sheet-enter-active, .vb-sheet-leave-active { transition: opacity .18s ease; }
.vb-sheet-enter-active .vb-sheet, .vb-sheet-leave-active .vb-sheet { transition: transform .22s ease; }
.vb-sheet-enter, .vb-sheet-leave-to { opacity: 0; }
.vb-sheet-enter .vb-sheet, .vb-sheet-leave-to .vb-sheet { transform: translateY(100%); }
@media (prefers-reduced-motion: reduce) {
  .vb-sheet-enter-active, .vb-sheet-leave-active,
  .vb-sheet-enter-active .vb-sheet, .vb-sheet-leave-active .vb-sheet { transition: none; }
}

.vb-vcard-row { display: flex; justify-content: space-between; gap: 14px; font-size: 13px; padding: 3px 0; }
.vb-vcard-row-label { color: #6b7280; }
.vb-vcard-row-val { color: #374151; text-align: right; }
.vb-vcard-row-val--empty { color: #9aa3ad; }
.vb-vcard-notes { align-items: center; }
.vb-vcard-notes .vb-notes-input { min-width: 0; width: 160px; }
/* status history as stacked entries; the desktop table would overflow the
   card on a phone (the clipped "Notes" column) */
.vb-vcard-history { padding: 10px 0 0 0; }
.vb-hist-head {
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.4px;
  color: #6b7280;
  padding: 10px 0 4px;
  border-top: 1px solid #e6eaef;
}
/* entries bleed to the sheet edge so their rails sit flush against it,
   the same way the cards wear theirs */
.vb-hist-entry { margin: 0 -16px; padding: 7px 16px; border-bottom: 1px solid #f0f2f5; }
.vb-hist-entry:last-child { border-bottom: none; }
/* the same per-snapshot triage rails as the desktop expansion */
.vb-hist-entry--late { box-shadow: inset 3px 0 0 0 #d64545; }
.vb-hist-entry--attention { box-shadow: inset 3px 0 0 0 #e0a32e; }
/* stamp | wash | maint | author on one line, like the desktop expansion.
   the chip tracks are fixed so the chips read as columns down the drawer
   (the stamp track fits the compact house format; a tenant with a long
   datetime_format wraps inside its track rather than shoving the columns) */
.vb-hist-line {
  display: grid;
  grid-template-columns: 84px 74px 74px minmax(0, 1fr);
  gap: 0 8px;
  align-items: center;
  font-size: 12px;
}
.vb-hist-when { color: #374151; font-weight: 600; white-space: nowrap; }
/* inside the drawer's fixed track, a long tenant datetime_format wraps
   rather than shoving the chip columns; the desktop cell keeps nowrap */
.vb-hist-line .vb-hist-when { white-space: normal; }
.vb-hist-by { color: #9aa3ad; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: right; }
.vb-hist-note { margin-top: 5px; font-size: 12px; color: #4b5563; }
.vb-hist-note--dim { color: #9aa3ad; }
.vb-hist-note--cleared { font-style: italic; }

/* keep iOS Safari from zooming the viewport on focus: any form control
   under 16px triggers the auto-zoom, which never reflows back cleanly */
@media (max-width: 640px) {
  .vb-card-list .vb-notes-input,
  .vb-card-list input,
  .vb-sheet .vb-notes-input,
  .vb-sheet input { font-size: 16px; }
}

/* header: auto-refresh toggle + the last-updated note */
.vb-rail-updated { font-size: 11px; color: #9aa3ad; white-space: nowrap; line-height: 1.2; }

/* result count + tz caption, sitting directly above the list so the count
   reads beside the filtered rows it describes */
.vb-results-meta {
  display: flex; align-items: baseline; gap: 10px;
  margin: 2px 2px 6px 2px;
  font-size: 12px;
}
.vb-results-count { font-weight: 600; color: #4b5563; }
/* push the tz to the right edge so it sits over the time columns / the card's
   right-aligned departs block, near the times it labels */
.vb-results-tz { color: #6b7280; margin-left: auto; }
.vb-auto-btn { display: inline-flex; align-items: center; gap: 6px; }
.vb-auto-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: #c2cad3; flex-shrink: 0;
}
/* a calm green pulse signals the board is live without shouting */
.vb-auto-dot--live { background: #46a36b; animation: vb-pulse 2.4s ease-out infinite; }
.vb-auto-btn--on { border-color: #bfe3cb; color: #14792f; }
@keyframes vb-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(70, 163, 107, 0.45); }
  70%  { box-shadow: 0 0 0 6px rgba(70, 163, 107, 0); }
  100% { box-shadow: 0 0 0 0 rgba(70, 163, 107, 0); }
}
@media (prefers-reduced-motion: reduce) {
  .vb-auto-dot--live { animation: none; }
}

/* print-only header; hidden on screen, revealed by the print stylesheet */
.vb-print-meta { display: none; }

/* info modal: legend rows drawn with the board's own chips + triage colors,
   so the key always matches what is on screen */
.vb-info { font-size: 13.5px; color: var(--fi-text); }
.vb-info-sec { margin-bottom: 18px; }
.vb-info-sec:last-child { margin-bottom: 0; }
.vb-info-h {
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.5px;
  color: var(--fi-ops-eyebrow); margin: 0 0 8px 0;
}
.vb-info-p { margin: 0 0 6px 0; line-height: 1.45; }
.vb-info-p:last-child { margin-bottom: 0; }
.vb-info-legend, .vb-info-chips { list-style: none; margin: 0; padding: 0; }
.vb-info-legend li {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 4px 0; line-height: 1.4;
}
.vb-info-dot {
  width: 10px; height: 10px; border-radius: 3px;
  margin-top: 4px; flex-shrink: 0;
}
.vb-info-dot--late { background: #d64545; }
.vb-info-dot--attention { background: #e0a32e; }
.vb-info-dot--ready { background: #46a36b; }
.vb-info-chips li { display: flex; align-items: center; gap: 10px; padding: 4px 0; }
.vb-info-chips .vb-status-chip { flex-shrink: 0; }
.vb-info-desc { color: var(--fi-text-muted); line-height: 1.4; }

/* ============================================================
   tv display
   a fixed overlay sized for tvs: vh-driven type so 720p signage and
   4k lobby panels both fill, themes swap css variables only (mirrors
   the departure board's .db-display architecture)
   ============================================================ */
.vb-display {
  position: fixed;
  top: 0; right: 0; bottom: 0; left: 0;
  z-index: 4000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--vp-bg);
  color: var(--vp-ink);
  font-family: var(--vp-body);
}
.vb-display--idle { cursor: none; }

.vb-dp-frame {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: clamp(18px, 3vh, 42px) clamp(22px, 3.4vw, 64px) clamp(14px, 2.2vh, 30px);
}

/* header: operator + title left, station clock right */
.vb-dp-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  padding-bottom: clamp(10px, 1.8vh, 22px);
  border-bottom: var(--vp-head-rule);
}
.vb-dp-operator {
  font-size: clamp(12px, 2vh, 24px);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--vp-accent);
  margin-bottom: clamp(2px, 0.6vh, 8px);
}
.vb-dp-title {
  margin: 0;
  font-family: var(--vp-display);
  font-size: clamp(30px, 6.4vh, 86px);
  font-weight: 700;
  line-height: 1;
  letter-spacing: var(--vp-title-tracking);
  text-transform: uppercase;
}
.vb-dp-head-right { text-align: right; flex-shrink: 0; }
.vb-dp-clock {
  font-family: var(--vp-mono);
  font-variant-numeric: tabular-nums;
  font-size: clamp(28px, 5.8vh, 78px);
  font-weight: 600;
  line-height: 1;
}
.vb-dp-date {
  font-size: clamp(12px, 1.9vh, 24px);
  color: var(--vp-muted);
  margin-top: clamp(2px, 0.6vh, 8px);
}
.vb-dp-tz { letter-spacing: 0.04em; }

/* board grid: vehicle / wash / maint / returns / departs */
.vb-dp-board {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: clamp(8px, 1.4vh, 18px);
}
.vb-dp-row {
  position: relative;
  display: grid;
  grid-template-columns: minmax(120px, 1.1fr) minmax(118px, 0.85fr) minmax(118px, 0.85fr) 1.2fr 1.3fr;
  gap: clamp(10px, 1.3vw, 28px);
  align-items: center;
  min-height: clamp(46px, 8.4vh, 104px);
  padding: 0 clamp(6px, 0.8vw, 18px);
  font-size: var(--vp-row-font);
}
.vb-dp-row > span,
.vb-dp-rowface > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* the face wrapper is invisible to the grid everywhere except the flip
   themes, which turn it into the panel that folds in */
.vb-dp-rowface { display: contents; }
.vb-dp-row--head {
  min-height: 0;
  padding-top: 0;
  padding-bottom: clamp(5px, 1vh, 12px);
  font-size: clamp(11px, 1.7vh, 20px);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--vp-muted);
  border-bottom: var(--vp-rule);
  background: none;
}
.vb-dp-rows { flex: 1; min-height: 0; overflow: hidden; }
.vb-dp-rows .vb-dp-row {
  border-bottom: var(--vp-rule);
  animation: var(--vp-row-anim);
  animation-fill-mode: both;
  /* the ticker's fold entrances hinge on the row's top edge */
  transform-origin: 50% 0;
}
/* midnight/showroom rows are faces of a rolling four-sided prism, like a
   trivision sign: both faces share the turn, so the outgoing reading is
   still visible while the incoming one rolls down over it. safari-safe
   because the faces rotate independently (no preserve-3d, no backface)
   and neither crosses 90deg. the outgoing face (.vb-dp-rowback) holds the
   previous reading flat until its stagger turn, then tips toward the
   viewer around its bottom hinge and folds under; the incoming face
   (.vb-dp-rowface) swings down from its top hinge and lands with the
   mechanical rebound. the row itself stays static, so its rule line and
   stripe never tilt. durations pair with DISPLAY_REVEAL_MS. */
.vb-display--midnight .vb-dp-rows .vb-dp-row,
.vb-display--showroom .vb-dp-rows .vb-dp-row {
  display: block;
  /* the 2d prism faces pin their outer edges to the row box by design
     (see the vb-dp-prism-* keyframes), so this clip only catches the
     0.8% settle dip at the end of the in roll */
  overflow: hidden;
}
.vb-display--midnight .vb-dp-rows .vb-dp-rowface,
.vb-display--showroom .vb-dp-rows .vb-dp-rowface {
  display: grid;
  grid-template-columns: inherit;
  gap: inherit;
  align-items: inherit;
  min-height: inherit;
  transform-origin: 50% 50%;
  animation: vb-dp-prism-in 0.55s linear both;
}
.vb-dp-rowback {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: inherit;
  gap: inherit;
  align-items: inherit;
  padding: inherit;
  transform-origin: 50% 50%;
  animation: vb-dp-prism-out 0.45s linear both;
}
.vb-dp-rowback > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vb-dp-cell-name {
  font-family: var(--vp-display);
  font-size: var(--vp-veh-font);
  font-weight: 700;
}
.vb-dp-chip {
  display: inline-block;
  max-width: 100%;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: clamp(12px, 2.1vh, 27px);
  font-weight: 600;
  padding: 0.22em 0.75em;
  letter-spacing: 0.03em;
  border: 1px solid currentColor;
  border-radius: var(--vp-chip-radius);
}
.vb-dp-cell-return { line-height: 1.25; }
.vb-dp-return-label {
  font-size: 0.52em;
  font-weight: 600;
  color: var(--vp-muted);
  text-transform: uppercase;
  letter-spacing: 0.14em;
  margin-right: 0.5em;
}
.vb-dp-return-time {
  font-family: var(--vp-mono);
  font-variant-numeric: tabular-nums;
  font-size: var(--vp-time-font);
  font-weight: 600;
}
.vb-dp-return-time--late { color: var(--vp-now); font-weight: 700; }
.vb-dp-return-time--dim { color: var(--vp-faint); font-weight: 400; }
.vb-dp-return-eta {
  display: block;
  font-size: clamp(12px, 2.1vh, 27px);
  font-weight: 700;
  color: var(--vp-soon);
}
.vb-dp-cell-departs { text-align: right; line-height: 1.25; }
.vb-dp-depart-time {
  font-family: var(--vp-mono);
  font-variant-numeric: tabular-nums;
  font-size: var(--vp-time-font);
  font-weight: 600;
}
.vb-dp-depart-in {
  display: block;
  font-size: clamp(12px, 2.1vh, 27px);
  font-weight: 600;
}
.vb-dp-depart-in--late { color: var(--vp-now); font-weight: 700; }
.vb-dp-depart-in--soon { color: var(--vp-soon); font-weight: 700; }
.vb-dp-depart--distant .vb-dp-depart-time,
.vb-dp-depart--distant .vb-dp-depart-in {
  color: var(--vp-faint);
  font-weight: 400;
}

/* quiet board: nothing to show (empty filter scope or data still loading) */
.vb-dp-quiet {
  padding: 14vh 24px;
  text-align: center;
}
.vb-dp-quiet-title {
  font-family: var(--vp-display);
  font-size: clamp(24px, 5vh, 64px);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--vp-title-tracking);
}
.vb-dp-quiet-sub {
  margin-top: clamp(6px, 1.4vh, 16px);
  font-size: clamp(14px, 2.4vh, 30px);
  color: var(--vp-muted);
}

/* footer: page dots left, brand + freshness right */
.vb-dp-footer {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-top: clamp(8px, 1.5vh, 18px);
  border-top: var(--vp-rule);
}
.vb-dp-pages { display: inline-flex; gap: 8px; }
.vb-dp-page-dot {
  width: clamp(8px, 1.2vh, 14px);
  height: clamp(8px, 1.2vh, 14px);
  border-radius: 50%;
  border: 1px solid var(--vp-muted);
  opacity: 0.55;
  padding: 0;
  background: transparent;
  appearance: none;
  cursor: pointer;
  transition: transform 0.15s, opacity 0.15s;
}
.vb-dp-page-dot:hover { opacity: 0.9; transform: scale(1.25); }
.vb-dp-page-dot:focus-visible { outline: 2px solid var(--vp-accent); outline-offset: 2px; }
.vb-dp-page-dot--on { background: var(--vp-accent); border-color: var(--vp-accent); opacity: 1; }
.vb-dp-brand {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: clamp(10px, 1.6vh, 18px);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--vp-muted);
}
.vb-dp-updated { letter-spacing: 0.08em; text-transform: none; }
.vb-dp-live-dot {
  width: clamp(6px, 1vh, 11px);
  height: clamp(6px, 1vh, 11px);
  border-radius: 50%;
  background: var(--vp-accent);
  animation: vb-dp-pulse 2.6s ease-in-out infinite;
}

/* page-rotation progress bar; keep in sync with the 12s page timer */
.vb-dp-rotbar {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: 3px;
  z-index: 5;
}
.vb-dp-rotbar-fill {
  height: 100%;
  width: 0;
  background: linear-gradient(90deg, var(--vp-accent), var(--vp-now));
  animation: vb-dp-rotate-progress 12s linear forwards;
}

/* auto-hiding control pill */
.vb-dp-controls {
  position: absolute;
  left: 50%;
  bottom: clamp(20px, 4vh, 44px);
  transform: translateX(-50%);
  z-index: 6;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 999px;
  background: rgba(9, 12, 17, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(10px);
  color: #fff;
  transition: opacity 0.35s ease;
}
.vb-dp-controls--hidden { opacity: 0; pointer-events: none; }
.vb-dp-controls-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(255, 255, 255, 0.65);
}
.vb-dp-theme-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  border: 2px solid transparent;
  transition: box-shadow 0.15s, transform 0.15s;
}
.vb-dp-theme-dot:hover { transform: scale(1.12); }
.vb-dp-theme-dot--midnight { background: #0f1a28; border-color: #6fb1e8; }
.vb-dp-theme-dot--showroom { background: #f8f9fb; border-color: #1976d2; }
.vb-dp-theme-dot--solari { background: #101115; border-color: #f4f1e8; }
.vb-dp-theme-dot--ember { background: #060402; border-color: #ffb000; }
/* ember's phosphor picker dots share the theme-dot chrome */
.vb-dp-phos-dot--amber { background: #ffb000; border-color: #ffb000; }
.vb-dp-phos-dot--green { background: #40ff6a; border-color: #40ff6a; }
/* outrun's sky picker dots: each a swatch of its grade; auto shows the
   three grades it moves between, cycle wears the neon wheel */
.vb-dp-sky-dot--day { background: linear-gradient(180deg, #1265cd, #a5ecfa); border-color: #ffd75e; }
.vb-dp-sky-dot--dusk { background: linear-gradient(180deg, #171043, #c22b6e); border-color: #ff2d78; }
/* cruise: the dusk sunset over a dark scenery band */
.vb-dp-sky-dot--cruise { background: linear-gradient(180deg, #171043 0%, #c22b6e 60%, #14092e 62%, #14092e 100%); border-color: #ffb84d; }
.vb-dp-sky-dot--night { background: linear-gradient(180deg, #05050f, #141033); border-color: #22d3ee; }
.vb-dp-sky-dot--dawn { background: linear-gradient(180deg, #55246b, #ffb37a); border-color: #ffb84d; }
.vb-dp-sky-dot--auto { background: conic-gradient(from 180deg, #141033 0 33%, #c22b6e 33% 66%, #ffb37a 66% 100%); border-color: #eaf6ff; }
.vb-dp-sky-dot--cycle { background: conic-gradient(#22d3ee, #b721ff, #ff2d78, #ffb84d, #22d3ee); border-color: #b721ff; }
/* half light, half dark: the swap is the theme's signature */
.vb-dp-theme-dot--screensaver { background: linear-gradient(135deg, #f7fafd 0%, #f7fafd 49%, #0e1d2f 51%, #0e1d2f 100%); border-color: #007ac7; }
.vb-dp-theme-dot--outrun { background: linear-gradient(180deg, #2b1a5e 0%, #ff2d78 100%); border-color: #22d3ee; }
.vb-dp-theme-dot--on { box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.55); }
.vb-dp-theme-dot:focus-visible,
.vb-dp-exit:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
.vb-dp-controls-sep {
  width: 1px;
  height: 18px;
  background: rgba(255, 255, 255, 0.25);
}
.vb-dp-exit {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 999px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 14px;
  cursor: pointer;
  transition: background 0.15s;
}
.vb-dp-exit:hover { background: rgba(255, 255, 255, 0.14); }

/* ---- theme: midnight (the product's dark language on a tv) ---- */
.vb-display--midnight {
  --vp-display: 'Avenir Next', Avenir, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
  --vp-body: 'Avenir Next', Avenir, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif;
  --vp-mono: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  --vp-bg: radial-gradient(120% 90% at 50% 0%, #142132 0%, #0f1a28 58%, #0c1520 100%);
  --vp-ink: #d7e1ea;
  --vp-muted: #93a5b7;
  --vp-faint: #67788a;
  --vp-accent: #6fb1e8;
  --vp-now: #f28b8b;
  --vp-soon: #ecc994;
  --vp-rule: 1px solid rgba(255, 255, 255, 0.09);
  --vp-head-rule: 2px solid rgba(255, 255, 255, 0.16);
  --vp-title-tracking: -0.02em;
  --vp-row-font: clamp(17px, 3vh, 40px);
  --vp-time-font: clamp(19px, 3.3vh, 44px);
  --vp-veh-font: clamp(20px, 3.8vh, 52px);
  --vp-chip-radius: 6px;
  --vp-row-anim: none; /* the flip lives on the row faces, not the row */
}
.vb-display--midnight .vb-dp-title { text-transform: none; }
/* a whisper of azure at the top; the only atmosphere */
.vb-display--midnight::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: radial-gradient(90% 38% at 50% 0%, rgba(111, 177, 232, 0.06), transparent 70%);
}
.vb-display--midnight .vb-dp-rows .vb-dp-row:nth-child(even) { background: rgba(255, 255, 255, 0.03); }
.vb-display--midnight .vb-dp-rotbar-fill { background: linear-gradient(90deg, #6fb1e8, #007ac7); }
/* chips: the midnight tint-bg + readable-ink idiom, hues from the dark vsm
   status vocabulary */
.vb-display--midnight .vb-dp-chip.vb-dp-tone--green { background: rgba(74, 222, 128, 0.14); border-color: rgba(74, 222, 128, 0.38); color: #86efac; }
.vb-display--midnight .vb-dp-chip.vb-dp-tone--cyan { background: rgba(56, 189, 248, 0.14); border-color: rgba(56, 189, 248, 0.38); color: #7dd3fc; }
.vb-display--midnight .vb-dp-chip.vb-dp-tone--teal { background: rgba(45, 212, 191, 0.14); border-color: rgba(45, 212, 191, 0.38); color: #5eead4; }
.vb-display--midnight .vb-dp-chip.vb-dp-tone--brown { background: rgba(214, 160, 101, 0.14); border-color: rgba(214, 160, 101, 0.38); color: #e6bd8f; }
.vb-display--midnight .vb-dp-chip.vb-dp-tone--purple { background: rgba(167, 139, 250, 0.16); border-color: rgba(167, 139, 250, 0.40); color: #c4b5fd; }
.vb-display--midnight .vb-dp-chip.vb-dp-tone--blue { background: rgba(111, 177, 232, 0.14); border-color: rgba(111, 177, 232, 0.40); color: #9cc7ec; }
.vb-display--midnight .vb-dp-chip.vb-dp-tone--amber { background: rgba(240, 173, 78, 0.12); border-color: rgba(240, 173, 78, 0.35); color: #ecc994; }
.vb-display--midnight .vb-dp-chip.vb-dp-tone--red { background: rgba(248, 113, 113, 0.18); border-color: rgba(248, 113, 113, 0.50); color: #fca5a5; }
.vb-display--midnight .vb-dp-chip.vb-dp-tone--gray { background: rgba(255, 255, 255, 0.06); border-color: rgba(255, 255, 255, 0.20); color: #93a5b7; }

/* ---- theme: showroom (the marketing site's light language) ---- */
.vb-display--showroom {
  --vp-display: -apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, 'Helvetica Neue', Arial, sans-serif;
  --vp-body: -apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, 'Helvetica Neue', Arial, sans-serif;
  --vp-mono: -apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, 'Helvetica Neue', Arial, sans-serif;
  --vp-bg:
    radial-gradient(1200px 480px at 18% -8%, rgba(66, 165, 245, 0.18), transparent 60%),
    radial-gradient(900px 420px at 94% 2%, rgba(25, 118, 210, 0.10), transparent 60%),
    linear-gradient(180deg, #ffffff 0%, #f8f9fb 100%);
  --vp-ink: #0a0d14;
  --vp-muted: rgba(10, 13, 20, 0.54);
  --vp-faint: rgba(10, 13, 20, 0.40);
  --vp-accent: #1976d2;
  --vp-now: #b91c1c;
  --vp-soon: #b45309;
  --vp-rule: 1px solid rgba(10, 13, 20, 0.09);
  --vp-head-rule: 2px solid rgba(10, 13, 20, 0.12);
  --vp-title-tracking: -0.02em;
  --vp-row-font: clamp(17px, 3vh, 40px);
  --vp-time-font: clamp(19px, 3.3vh, 44px);
  --vp-veh-font: clamp(20px, 3.8vh, 52px);
  --vp-chip-radius: 999px;
  --vp-row-anim: none; /* the flip lives on the row faces, not the row */
}
.vb-display--showroom .vb-dp-title { text-transform: none; }
.vb-display--showroom .vb-dp-operator { letter-spacing: 0.09em; }
/* the site's cta-gradient signature lives in the rotation bar */
.vb-display--showroom .vb-dp-rotbar-fill { background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%); }
/* blue is for actionable times only; the distant mute still wins */
.vb-display--showroom .vb-dp-depart-time { color: #1976d2; }
.vb-display--showroom .vb-dp-depart--distant .vb-dp-depart-time { color: var(--vp-faint); }
.vb-display--showroom .vb-dp-chip { font-weight: 700; }
.vb-display--showroom .vb-dp-chip.vb-dp-tone--green { background: #d4f4d6; border-color: rgba(43, 138, 62, 0.25); color: #2b8a3e; }
.vb-display--showroom .vb-dp-chip.vb-dp-tone--cyan { background: #d2efff; border-color: rgba(13, 110, 138, 0.25); color: #0d6e8a; }
.vb-display--showroom .vb-dp-chip.vb-dp-tone--teal { background: #ccf4ee; border-color: rgba(12, 129, 116, 0.25); color: #0c8174; }
.vb-display--showroom .vb-dp-chip.vb-dp-tone--brown { background: #fee4d1; border-color: rgba(184, 90, 30, 0.25); color: #b85a1e; }
.vb-display--showroom .vb-dp-chip.vb-dp-tone--purple { background: #f0e4f9; border-color: rgba(138, 63, 181, 0.28); color: #8a3fb5; }
.vb-display--showroom .vb-dp-chip.vb-dp-tone--blue { background: rgba(25, 118, 210, 0.10); border-color: rgba(25, 118, 210, 0.18); color: #1976d2; }
.vb-display--showroom .vb-dp-chip.vb-dp-tone--amber { background: rgba(245, 158, 11, 0.14); border-color: rgba(245, 158, 11, 0.30); color: #8a5a06; }
/* the app's solid-red = severe convention; the only solid fill on the board */
.vb-display--showroom .vb-dp-chip.vb-dp-tone--red { background: #d62f2f; border-color: #d62f2f; color: #ffffff; }
.vb-display--showroom .vb-dp-chip.vb-dp-tone--gray { background: #f1efe8; border-color: rgba(10, 13, 20, 0.12); color: rgba(10, 13, 20, 0.54); }

/* ---- theme: solari (true animated split-flap on vsm data) ---- */
.vb-display--solari {
  --vp-display: 'Barlow Condensed', 'Arial Narrow', sans-serif;
  --vp-body: 'Barlow Condensed', 'Arial Narrow', sans-serif;
  --vp-mono: 'IBM Plex Mono', Menlo, Consolas, monospace;
  --vp-bg: radial-gradient(110% 85% at 50% 8%, #17181d 0%, #0b0c10 60%, #07080b 100%);
  --vp-ink: #f4f1e8;
  --vp-muted: #79808c;
  --vp-faint: #79808c;
  --vp-accent: #f5b942;
  --vp-now: #ff6a55;
  --vp-soon: #f5b942;
  --vp-rule: 1px solid rgba(244, 241, 232, 0.10);
  --vp-head-rule: 2px solid rgba(244, 241, 232, 0.20);
  --vp-title-tracking: 0.14em;
  --vp-row-font: clamp(15px, 2.6vh, 36px);
  --vp-time-font: clamp(15px, 2.6vh, 36px);
  --vp-veh-font: inherit; /* flap cells share one em box */
  --vp-chip-radius: 0; /* statuses are flap words, not chips */
  --vp-row-anim: none; /* the flaps are the animation */
}
.vb-display--solari .vb-dp-clock--flap {
  font-size: clamp(20px, 4.4vh, 60px);
  font-family: var(--vp-display);
}
/* flap line layout: fixed-width columns so every row and the printed
   header align; the em widths mirror split_flap's cell metrics
   (0.66em cell + 0.06em gap) - keep in sync */
.vb-sf-head,
.vb-sf-row {
  display: flex;
  align-items: center;
  gap: clamp(12px, 1.6vw, 34px);
  font-size: var(--vp-row-font);
  padding: clamp(4px, 0.9vh, 12px) clamp(6px, 0.8vw, 18px);
  position: relative;
}
.vb-sf-head { padding-top: 0; }
.vb-sf-col { display: inline-flex; }
/* 14 tiles: full sprinter/tbn names ("1454 SPRINTER") instead of an
   8-tile truncation; the board's horizontal slack pays for it */
.vb-sf-col-name { min-width: calc(14 * 0.66em + 13 * 0.06em); font-size: 1.2em; }
.vb-sf-col-wash,
.vb-sf-col-maint { min-width: calc(6 * 0.66em + 5 * 0.06em); }
/* 12 tiles fit the worst-case stamp "12/25 12:45P" (two-digit date and
   hour) so the trailing a/p never truncates */
.vb-sf-col-return { min-width: calc(12 * 0.66em + 11 * 0.06em); }
.vb-sf-col-departs { min-width: calc(12 * 0.66em + 11 * 0.06em); }
.vb-sf-col-in { min-width: calc(9 * 0.66em + 8 * 0.06em); margin-left: auto; justify-content: flex-end; }
.vb-sf-head-label {
  font-size: clamp(11px, 1.7vh, 20px);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--vp-muted);
}
/* the name column's larger em box would inflate its head label too; the
   1.2 multiplier keeps this col as wide as the rows' 1.2em flap group so
   every downstream header sits exactly over its column */
.vb-sf-head .vb-sf-col-name {
  font-size: 1em;
  min-width: calc(1.2 * (14 * 0.66em + 13 * 0.06em));
}
.vb-sf-row { border-bottom: var(--vp-rule); }
/* status color rides the column (flap glyphs inherit currentColor):
   the restrained ivory/amber/red vocabulary of real boards */
.vb-sf-col.vb-dp-tone--green { color: #f4f1e8; }
.vb-sf-col.vb-dp-tone--cyan,
.vb-sf-col.vb-dp-tone--teal,
.vb-sf-col.vb-dp-tone--blue { color: #d8d4c8; }
.vb-sf-col.vb-dp-tone--brown,
.vb-sf-col.vb-dp-tone--amber { color: #f5b942; }
.vb-sf-col.vb-dp-tone--purple { color: #79808c; }
.vb-sf-col.vb-dp-tone--red { color: #ff6a55; }
.vb-sf-col.vb-dp-tone--gray,
.vb-sf-col.vb-dp-tone--blank { color: #79808c; }
.vb-sf-return--pending { color: #d8d4c8; }
.vb-sf-return--late { color: #ff6a55; }
.vb-sf-return--dim { color: #79808c; }
.vb-sf-return--eta { color: #f5b942; }
.vb-sf-return--none { color: #79808c; }
.vb-sf-col-in { color: #d8d4c8; }
.vb-sf-in--late { color: #ff6a55; }
.vb-sf-in--soon { color: #f5b942; }
.vb-sf-in--none { color: #79808c; }
.vb-dp-rows--solari { position: relative; }
.vb-dp-quiet--solari {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  padding: 0 24px;
}

/* ---- theme: ember (crt amber phosphor, monochrome) ---- */
.vb-display--ember {
  /* the phosphor family: every amber in the theme composes from these,
     so the green-phosphor toggle swaps four custom properties instead of
     duplicating the block. hot = the brighter grade the tube spends on
     "soon" urgency; late/unsafe red stays red on both phosphors (the
     alarm hue is semantic, not decorative). */
  --vp-phos-rgb: 255, 176, 0;
  --vp-phos: #ffb000;
  --vp-phos-hot-rgb: 255, 211, 77;
  --vp-phos-hot: #ffd34d;
  --vp-phos-ink-inverse: #0a0603;
  --vp-display: 'Share Tech Mono', Menlo, monospace;
  --vp-body: 'Share Tech Mono', Menlo, monospace;
  --vp-mono: 'Share Tech Mono', Menlo, monospace;
  --vp-bg: radial-gradient(110% 80% at 50% 12%, #181006 0%, #0a0603 52%, #050302 100%);
  --vp-ink: var(--vp-phos);
  --vp-muted: rgba(var(--vp-phos-rgb), 0.48);
  --vp-faint: rgba(var(--vp-phos-rgb), 0.35);
  --vp-accent: var(--vp-phos);
  --vp-now: #ff3b30;
  --vp-soon: var(--vp-phos-hot);
  --vp-rule: 1px solid rgba(var(--vp-phos-rgb), 0.18);
  --vp-head-rule: 2px solid rgba(var(--vp-phos-rgb), 0.32);
  --vp-title-tracking: 0.18em;
  --vp-row-font: clamp(16px, 2.9vh, 38px);
  --vp-time-font: clamp(17px, 3vh, 40px);
  /* the vehicle number is the anchor a yard reads from across the room;
     share tech mono is narrow, so it can run big without crowding */
  --vp-veh-font: clamp(22px, 4.2vh, 56px);
  --vp-chip-radius: 2px;
  --vp-row-anim: vb-dp-flicker 0.5s ease-out;
}
/* green phosphor (the p1 tube to amber's p3): same board, same glass,
   different chemistry. only the phosphor family and the tube's warm-black
   bed change. */
.vb-display--ember.vb-display--phos-green {
  --vp-phos-rgb: 64, 255, 106;
  --vp-phos: #40ff6a;
  --vp-phos-hot-rgb: 195, 255, 205;
  --vp-phos-hot: #c3ffcd;
  --vp-phos-ink-inverse: #04120a;
  --vp-bg: radial-gradient(110% 80% at 50% 12%, #07180c 0%, #030a05 52%, #020503 100%);
}
/* scanlines above the content, like glass in front of the phosphor */
.vb-display--ember::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: repeating-linear-gradient(0deg,
    rgba(0, 0, 0, 0.26) 0px, rgba(0, 0, 0, 0.26) 2px, transparent 2px, transparent 5px);
}
/* a soft interference band rolls down the tube every few seconds, then
   the raster settles; sits under the scanline glass */
.vb-display--ember::after {
  content: '';
  position: absolute;
  left: 0; right: 0;
  top: 0;
  height: 14%;
  z-index: 2;
  pointer-events: none;
  background: linear-gradient(180deg,
    transparent 0%, rgba(var(--vp-phos-rgb), 0.04) 30%, rgba(var(--vp-phos-rgb), 0.09) 50%,
    rgba(var(--vp-phos-rgb), 0.04) 70%, transparent 100%);
  transform: translateY(-120%);
  animation: vb-dp-crt-roll 9s linear infinite;
}
/* faint whole-raster brightness jitter, the tube never sitting perfectly
   still (kept tiny and infrequent) */
.vb-display--ember .vb-dp-frame { animation: vb-dp-crt-jitter 7s steps(1, end) infinite; }
.vb-display--ember .vb-dp-row span,
.vb-display--ember .vb-dp-title,
.vb-display--ember .vb-dp-clock { text-shadow: 0 0 14px rgba(var(--vp-phos-rgb), 0.4); }
.vb-display--ember .vb-dp-row { text-transform: uppercase; }
/* terminal grid: rows lose their inset so the columns share the frame
   rails with the clock and footer, and the two time columns reserve
   worst-case mono width ("12/25 12:45p") so a two-digit hour or date
   never clips the trailing a/p */
.vb-display--ember .vb-dp-row {
  padding-left: 0;
  padding-right: 0;
  /* the vehicle column soaks up the board's slack (1.8fr) so the two
     right-aligned time columns pack toward the right rail instead of
     leaving a dead zone between returns and departs */
  grid-template-columns:
    minmax(160px, 1.8fr) minmax(96px, 0.7fr) minmax(96px, 0.7fr)
    minmax(calc(var(--vp-time-font) * 6.9), 1fr) minmax(calc(var(--vp-time-font) * 6.9), 1fr);
}
/* monochrome status hierarchy: brightness, inverse video, and blink carry
   the semantics a crt cannot spend hue on. the box's inset is pulled back
   out so the status glyphs sit on the text grid under the column header;
   the cell must let the pulled-back box overflow or its left edge (and an
   inverse chip's fill) gets sheared at the column line - status words are
   short fixed labels, so the cell's ellipsis clip isn't needed here. */
.vb-display--ember .vb-dp-cell-status { overflow: visible; }
.vb-display--ember .vb-dp-chip {
  background: transparent;
  padding-left: 0.4em;
  padding-right: 0.4em;
  margin-left: calc(-0.4em - 1px);
}
.vb-display--ember .vb-dp-chip.vb-dp-tone--green { color: var(--vp-phos); border-color: rgba(var(--vp-phos-rgb), 0.5); text-shadow: 0 0 14px rgba(var(--vp-phos-rgb), 0.4); }
.vb-display--ember .vb-dp-chip.vb-dp-tone--cyan,
.vb-display--ember .vb-dp-chip.vb-dp-tone--teal,
.vb-display--ember .vb-dp-chip.vb-dp-tone--blue { color: rgba(var(--vp-phos-rgb), 0.75); border-color: rgba(var(--vp-phos-rgb), 0.3); text-shadow: none; }
.vb-display--ember .vb-dp-chip.vb-dp-tone--brown { color: rgba(var(--vp-phos-rgb), 0.55); border-color: rgba(var(--vp-phos-rgb), 0.22); text-shadow: none; }
.vb-display--ember .vb-dp-chip.vb-dp-tone--purple { color: rgba(var(--vp-phos-rgb), 0.48); border: 1px dashed rgba(var(--vp-phos-rgb), 0.25); text-shadow: none; }
.vb-display--ember .vb-dp-chip.vb-dp-tone--amber { color: var(--vp-phos-hot); border-color: rgba(var(--vp-phos-hot-rgb), 0.5); text-shadow: 0 0 14px rgba(var(--vp-phos-hot-rgb), 0.55); }
.vb-display--ember .vb-dp-chip.vb-dp-tone--gray { color: rgba(var(--vp-phos-rgb), 0.48); border-color: rgba(var(--vp-phos-rgb), 0.22); text-shadow: none; }
/* oos: inverse-video phosphor block breathing its glow; unsafe dvir:
   inverse red, blinking */
.vb-display--ember .vb-dp-chip.vb-dp-st--oos { background: var(--vp-phos); border-color: var(--vp-phos); color: var(--vp-phos-ink-inverse); font-weight: 700; text-shadow: none; box-shadow: 0 0 18px rgba(var(--vp-phos-rgb), 0.45); animation: vb-dp-ember-breathe 3s ease-in-out infinite; }
.vb-display--ember .vb-dp-chip.vb-dp-st--unsafe_dvir { background: #ff3b30; border-color: #ff3b30; color: #050302; font-weight: 700; text-shadow: none; box-shadow: 0 0 18px rgba(255, 59, 48, 0.5); animation: vb-dp-led-blink 1.1s steps(2, jump-none) infinite; }
.vb-display--ember .vb-dp-depart-in--late { text-shadow: 0 0 16px rgba(255, 59, 48, 0.65); animation: vb-dp-led-blink 1.1s steps(2, jump-none) infinite; }
.vb-display--ember .vb-dp-depart-in--soon { text-shadow: 0 0 14px rgba(var(--vp-phos-hot-rgb), 0.55); }
/* a late return smolders rather than blinks - departures own the blink */
.vb-display--ember .vb-dp-return-time--late { text-shadow: 0 0 16px rgba(255, 59, 48, 0.65); animation: vb-dp-ember-glow 2.2s ease-in-out infinite; }
/* the live eta is the fact the yard is waiting on; let it pulse gently */
.vb-display--ember .vb-dp-return-eta { animation: vb-dp-ember-glow 2.8s ease-in-out infinite; }

/* ---- theme: screen saver (the linkedin campaign language) ----
   built to be left on all day: the light/dark palette swap and the row
   ticker keep every pixel moving, and the brand orbs drift behind the
   board. light is the base; .vb-display--ss-dark carries the inversion. */
.vb-display--screensaver {
  --vp-display: -apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, 'Helvetica Neue', Arial, sans-serif;
  --vp-body: -apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, 'Helvetica Neue', Arial, sans-serif;
  --vp-mono: -apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, 'Helvetica Neue', Arial, sans-serif;
  --vp-bg: linear-gradient(180deg, #f7fafd 0%, #edf3fa 100%);
  --vp-ink: #15324d;
  --vp-muted: rgba(21, 50, 77, 0.56);
  --vp-faint: rgba(21, 50, 77, 0.38);
  --vp-accent: #007ac7;
  --vp-now: #b91c1c;
  --vp-soon: #b45309;
  --vp-rule: 1px solid rgba(21, 50, 77, 0.10);
  --vp-head-rule: 2px solid rgba(21, 50, 77, 0.14);
  --vp-title-tracking: -0.02em;
  --vp-row-font: clamp(17px, 3vh, 40px);
  --vp-time-font: clamp(19px, 3.3vh, 44px);
  --vp-veh-font: clamp(20px, 3.8vh, 52px);
  --vp-chip-radius: 999px;
  --vp-row-anim: none; /* the ticker transitions carry the motion */
  --vp-orb-a: rgba(0, 122, 199, 0.16);
  --vp-orb-b: rgba(108, 177, 222, 0.20);
  --vp-orb-c: rgba(240, 173, 78, 0.13);
}
.vb-display--screensaver.vb-display--ss-dark {
  --vp-bg: radial-gradient(120% 90% at 50% 0%, #14273c 0%, #0e1d2f 55%, #0a1624 100%);
  --vp-ink: #e2edf7;
  --vp-muted: #9db3c9;
  --vp-faint: #6d8299;
  --vp-accent: #6cb1de;
  --vp-now: #f28b8b;
  --vp-soon: #ecc994;
  --vp-rule: 1px solid rgba(255, 255, 255, 0.10);
  --vp-head-rule: 2px solid rgba(255, 255, 255, 0.16);
  --vp-orb-a: rgba(0, 122, 199, 0.30);
  --vp-orb-b: rgba(108, 177, 222, 0.20);
  --vp-orb-c: rgba(240, 173, 78, 0.14);
}
.vb-display--screensaver .vb-dp-title { text-transform: none; }
.vb-display--screensaver .vb-dp-operator { letter-spacing: 0.09em; }
/* azure is for actionable times only; the distant mute still wins */
.vb-display--screensaver .vb-dp-depart-time { color: var(--vp-accent); }
.vb-display--screensaver .vb-dp-depart--distant .vb-dp-depart-time { color: var(--vp-faint); }
.vb-display--screensaver .vb-dp-chip { font-weight: 700; }
/* light-palette chips share the showroom vocabulary */
.vb-display--screensaver .vb-dp-chip.vb-dp-tone--green { background: #d4f4d6; border-color: rgba(43, 138, 62, 0.25); color: #2b8a3e; }
.vb-display--screensaver .vb-dp-chip.vb-dp-tone--cyan { background: #d2efff; border-color: rgba(13, 110, 138, 0.25); color: #0d6e8a; }
.vb-display--screensaver .vb-dp-chip.vb-dp-tone--teal { background: #ccf4ee; border-color: rgba(12, 129, 116, 0.25); color: #0c8174; }
.vb-display--screensaver .vb-dp-chip.vb-dp-tone--brown { background: #fee4d1; border-color: rgba(184, 90, 30, 0.25); color: #b85a1e; }
.vb-display--screensaver .vb-dp-chip.vb-dp-tone--purple { background: #f0e4f9; border-color: rgba(138, 63, 181, 0.28); color: #8a3fb5; }
.vb-display--screensaver .vb-dp-chip.vb-dp-tone--blue { background: rgba(0, 122, 199, 0.10); border-color: rgba(0, 122, 199, 0.18); color: #007ac7; }
.vb-display--screensaver .vb-dp-chip.vb-dp-tone--amber { background: rgba(245, 158, 11, 0.14); border-color: rgba(245, 158, 11, 0.30); color: #8a5a06; }
.vb-display--screensaver .vb-dp-chip.vb-dp-tone--red { background: #d62f2f; border-color: #d62f2f; color: #ffffff; }
.vb-display--screensaver .vb-dp-chip.vb-dp-tone--gray { background: #eef2f7; border-color: rgba(21, 50, 77, 0.12); color: rgba(21, 50, 77, 0.56); }
/* dark-palette chips share the midnight vocabulary */
.vb-display--ss-dark .vb-dp-chip.vb-dp-tone--green { background: rgba(74, 222, 128, 0.14); border-color: rgba(74, 222, 128, 0.38); color: #86efac; }
.vb-display--ss-dark .vb-dp-chip.vb-dp-tone--cyan { background: rgba(56, 189, 248, 0.14); border-color: rgba(56, 189, 248, 0.38); color: #7dd3fc; }
.vb-display--ss-dark .vb-dp-chip.vb-dp-tone--teal { background: rgba(45, 212, 191, 0.14); border-color: rgba(45, 212, 191, 0.38); color: #5eead4; }
.vb-display--ss-dark .vb-dp-chip.vb-dp-tone--brown { background: rgba(214, 160, 101, 0.14); border-color: rgba(214, 160, 101, 0.38); color: #e6bd8f; }
.vb-display--ss-dark .vb-dp-chip.vb-dp-tone--purple { background: rgba(167, 139, 250, 0.16); border-color: rgba(167, 139, 250, 0.40); color: #c4b5fd; }
.vb-display--ss-dark .vb-dp-chip.vb-dp-tone--blue { background: rgba(111, 177, 232, 0.14); border-color: rgba(111, 177, 232, 0.40); color: #9cc7ec; }
.vb-display--ss-dark .vb-dp-chip.vb-dp-tone--amber { background: rgba(240, 173, 78, 0.12); border-color: rgba(240, 173, 78, 0.35); color: #ecc994; }
.vb-display--ss-dark .vb-dp-chip.vb-dp-tone--red { background: rgba(248, 113, 113, 0.18); border-color: rgba(248, 113, 113, 0.50); color: #fca5a5; }
.vb-display--ss-dark .vb-dp-chip.vb-dp-tone--gray { background: rgba(255, 255, 255, 0.06); border-color: rgba(255, 255, 255, 0.20); color: #9db3c9; }

/* the drifting brand orbs; palettes recolor them through the vars */
.vb-ss-orbs {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}
.vb-ss-orb {
  position: absolute;
  border-radius: 50%;
  will-change: transform;
}
.vb-ss-orb--1 { width: 52vh; height: 52vh; left: -10vh; top: -14vh; background: radial-gradient(circle at 35% 35%, var(--vp-orb-a), transparent 70%); animation: vb-ss-drift-a 53s ease-in-out infinite; }
.vb-ss-orb--2 { width: 64vh; height: 64vh; right: -18vh; top: 4vh; background: radial-gradient(circle at 40% 40%, var(--vp-orb-b), transparent 70%); animation: vb-ss-drift-b 67s ease-in-out infinite; }
.vb-ss-orb--3 { width: 44vh; height: 44vh; left: 22vw; bottom: -16vh; background: radial-gradient(circle at 45% 40%, var(--vp-orb-c), transparent 70%); animation: vb-ss-drift-c 47s ease-in-out infinite; }
.vb-ss-orb--4 { width: 34vh; height: 34vh; right: 24vw; bottom: 8vh; background: radial-gradient(circle at 40% 40%, var(--vp-orb-a), transparent 70%); animation: vb-ss-drift-b 59s ease-in-out infinite reverse; }
.vb-ss-orb--5 { width: 26vh; height: 26vh; left: 42vw; top: 6vh; background: radial-gradient(circle at 40% 40%, var(--vp-orb-b), transparent 70%); animation: vb-ss-drift-c 73s ease-in-out infinite reverse; }

/* the ticker: the leaving row flips up behind the list, survivors slide
   up one slot, and the entering row flips up into the bottom slot */
.vb-dp-rows--ss { position: relative; }
.vb-dp-rows--ss .vb-dp-row { z-index: 1; }
.vb-ss-move { transition: transform 0.55s cubic-bezier(0.25, 0.65, 0.3, 1); }
/* the enter/leave selectors must outweigh the base row rule
   (.vb-dp-rows .vb-dp-row), whose animation: var(--vp-row-anim) is none
   here and would silently suppress the flips */
.vb-dp-rows--ss .vb-dp-row.vb-ss-leave-active {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 0;
  /* hinged at its bottom edge so the departing row folds backwards, top
     edge tipping away over the list (overriding the rows' top-hinge
     origin, which the entrances still use). the 0.55s duration matches
     the .vb-ss-move slide so the folding sliver rides up with the list
     instead of getting overlapped by the row arriving into its slot. */
  transform-origin: 50% 100%;
  animation: vb-ss-flip-out 0.55s cubic-bezier(0.25, 0.65, 0.3, 1) both;
}
/* the entrance waits a beat so the slide clears the bottom slot first */
.vb-dp-rows--ss .vb-dp-row.vb-ss-enter-active { animation: vb-dp-flip-down 0.6s ease 0.12s both; }

/* ---- theme: outrun (synthwave arcade; the fleet on a scoreboard) ----
   sunset sky over a neon horizon, a scrolling perspective grid floor, and
   the departure countdown rendered as a draining fuel bar. rows race in
   from the left. orbitron/rajdhani ride the shared lazy fonts link. */
.vb-display--outrun {
  --vp-display: 'Orbitron', 'Avenir Next', sans-serif;
  --vp-body: 'Rajdhani', 'Avenir Next', 'Arial Narrow', sans-serif;
  --vp-mono: 'Rajdhani', Menlo, sans-serif;
  /* the sky family: the scene composes from these so the dusk/night/dawn
     grades (vb-display--sky-*) swap a handful of custom properties. the
     arcade chrome (chips, title, neons) stays constant across skies. */
  --or-sun-grad: linear-gradient(180deg, #ffd75e 0%, #ff9a3d 34%, #ff2d78 68%, #b721ff 100%);
  --or-sun-glow: rgba(255, 45, 120, 0.35);
  --or-sun-opacity: 0.85;
  --or-star-opacity: 1;
  --or-grid-rgb: 34, 211, 238;
  --or-horizon: #ff2d78;
  --or-horizon-glow: rgba(255, 45, 120, 0.8);
  --or-haze: rgba(26, 11, 46, 0.95);
  /* scene geometry. the scenery skies raise the horizon and tip the
     ground plane more upright - the classic driving-game uphill raster
     trick (the whole horizon reads as the road climbing). classic dusk
     keeps these defaults, so its scene is untouched. */
  --or-seam: 62%;
  --or-ground-h: 38%;
  --or-tilt: 62deg;
  /* scenery family (road, ridges, palms). these color defaults ARE the
     cruise grade (cruise = the dusk palette + scenery); classic dusk
     never mounts the scenery nodes, so they are inert there. */
  --or-road-rgb: 22, 16, 46;
  --or-dash-rgb: 255, 240, 210;
  --or-ridge-far: rgba(108, 66, 160, 0.55);
  --or-ridge-rgb: 63, 32, 102;
  --or-ridge-a: 0.8;
  --or-palm-rgb: 20, 10, 46;
  /* one reusable palm silhouette; instances tint via --or-palm-rgb through
     the mask, with a baked-fill fallback for engines without mask support */
  --or-palm-svg: url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%20120%20200'%3E%3Cpath%20d='M45,200C48,156,53,110,63,56L70,58C61,112,57,156,58,200Z'/%3E%3Cpath%20d='M66,56C64,34,58,16,46,4C58,18,64,38,70,56Z'/%3E%3Cpath%20d='M66,56C76,36,88,24,104,20C90,30,79,44,71,58Z'/%3E%3Cpath%20d='M67,57C82,50,98,52,110,62C96,58,82,60,70,64Z'/%3E%3Cpath%20d='M67,58C80,64,90,76,93,90C84,78,74,68,65,64Z'/%3E%3Cpath%20d='M66,56C56,38,44,28,28,24C42,34,54,46,62,58Z'/%3E%3Cpath%20d='M65,57C50,50,34,52,22,62C36,58,50,60,62,64Z'/%3E%3Cpath%20d='M65,58C52,64,42,76,39,90C48,78,58,68,67,64Z'/%3E%3Ccircle%20cx='60'%20cy='62'%20r='5'/%3E%3Ccircle%20cx='70'%20cy='64'%20r='5'/%3E%3C/svg%3E");
  --or-palm-svg-baked: url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%20120%20200'%3E%3Cg%20fill='%2314092e'%3E%3Cpath%20d='M45,200C48,156,53,110,63,56L70,58C61,112,57,156,58,200Z'/%3E%3Cpath%20d='M66,56C64,34,58,16,46,4C58,18,64,38,70,56Z'/%3E%3Cpath%20d='M66,56C76,36,88,24,104,20C90,30,79,44,71,58Z'/%3E%3Cpath%20d='M67,57C82,50,98,52,110,62C96,58,82,60,70,64Z'/%3E%3Cpath%20d='M67,58C80,64,90,76,93,90C84,78,74,68,65,64Z'/%3E%3Cpath%20d='M66,56C56,38,44,28,28,24C42,34,54,46,62,58Z'/%3E%3Cpath%20d='M65,57C50,50,34,52,22,62C36,58,50,60,62,64Z'/%3E%3Cpath%20d='M65,58C52,64,42,76,39,90C48,78,58,68,67,64Z'/%3E%3Ccircle%20cx='60'%20cy='62'%20r='5'/%3E%3Ccircle%20cx='70'%20cy='64'%20r='5'/%3E%3C/g%3E%3C/svg%3E");
  --vp-bg: linear-gradient(180deg,
    #070718 0%, #171043 30%, #3d1663 52%, #8a1e63 58%, #c22b6e 61.5%,
    #1a0b2e 62%, #0a0618 100%);
  --vp-ink: #eaf6ff;
  --vp-muted: #a99cd6;
  --vp-faint: #6f639c;
  --vp-accent: #22d3ee;
  --vp-now: #ff2d78;
  --vp-soon: #ffb84d;
  --vp-rule: 1px solid rgba(34, 211, 238, 0.14);
  --vp-head-rule: 2px solid rgba(34, 211, 238, 0.28);
  --vp-title-tracking: 0.08em;
  --vp-row-font: clamp(18px, 3.1vh, 42px);
  --vp-time-font: clamp(20px, 3.4vh, 46px);
  --vp-veh-font: clamp(20px, 3.7vh, 50px);
  --vp-chip-radius: 4px;
  --vp-row-anim: vb-or-race-in 0.5s cubic-bezier(0.2, 0.8, 0.3, 1);
}
/* night: the run after the sun is gone. pale moon, brighter stars, the
   grid gone deep blue, a cyan horizon. */
.vb-display--outrun.vb-display--sky-night {
  --or-sun-grad: linear-gradient(180deg, #eaf6ff 0%, #b6d8ff 40%, #7d8cff 75%, #4a55c4 100%);
  --or-sun-glow: rgba(155, 200, 255, 0.3);
  --or-sun-opacity: 0.5;
  --or-star-opacity: 1;
  --or-grid-rgb: 96, 140, 255;
  --or-horizon: #22d3ee;
  --or-horizon-glow: rgba(34, 211, 238, 0.7);
  --or-haze: rgba(6, 8, 22, 0.95);
  --or-road-rgb: 8, 6, 20;
  --or-dash-rgb: 140, 240, 255;
  --or-ridge-far: rgba(34, 22, 66, 0.6);
  --or-ridge-rgb: 10, 8, 28;
  --or-ridge-a: 0.85;
  --or-palm-rgb: 5, 3, 15;
  --or-seam: 50%;
  --or-ground-h: 50%;
  --or-tilt: 53deg;
  --or-ground-grad: linear-gradient(180deg, #070714, #04040c);
  --vp-bg: linear-gradient(180deg,
    #030310 0%, #0a0a26 27%, #131038 43.5%, #1b1445 49.5%,
    #070714 50%, #04040c 100%);
}
/* day: the trump card - the original 1986 outrun was a SUNNY game.
   coconut beach: saturated cerulean sky, white-hot striped sun, a gold
   sea-glint horizon, and the grid flipped to the theme's own hot pink so
   the neon identity survives full daylight. parallax clouds drift on two
   layers like the stage select. */
.vb-display--outrun.vb-display--sky-day {
  --or-sun-grad: linear-gradient(180deg, #ffffff 0%, #fff3c4 30%, #ffd75e 62%, #ff9a3d 100%);
  --or-sun-glow: rgba(255, 244, 214, 0.5);
  --or-sun-opacity: 0.95;
  --or-star-opacity: 0;
  --or-grid-rgb: 255, 45, 120;
  --or-horizon: #ffd75e;
  --or-horizon-glow: rgba(255, 215, 94, 0.8);
  --or-haze: rgba(11, 36, 56, 0.93);
  /* sun-gold dashes tie the lane divider to the gold sun and horizon */
  --or-road-rgb: 24, 20, 52;
  --or-dash-rgb: 255, 206, 66;
  --or-ridge-far: rgba(96, 116, 196, 0.5);
  --or-ridge-rgb: 44, 52, 120;
  --or-ridge-a: 0.72;
  --or-palm-rgb: 10, 22, 48;
  --or-seam: 50%;
  --or-ground-h: 50%;
  --or-tilt: 53deg;
  --or-ground-grad: linear-gradient(180deg, #0b2438, #071522);
  --vp-bg: linear-gradient(180deg,
    #0b3f9e 0%, #1265cd 24%, #2f9ce6 42%, #63ccf2 47%, #a5ecfa 49.5%,
    #0b2438 50%, #071522 100%);
}
/* the bright sky pushes the panel a step more opaque (navy instead of
   purple) and backs the chips the same way, so the arcade chrome reads
   at the dusk baseline from tv distance */
.vb-display--outrun.vb-display--sky-day .vb-dp-board {
  background: linear-gradient(180deg, rgba(6, 10, 32, 0.6), rgba(6, 10, 32, 0.84));
  border-color: rgba(34, 211, 238, 0.22);
}
.vb-display--outrun.vb-display--sky-day .vb-dp-chip { background: rgba(6, 10, 32, 0.62); }
/* dawn: the same road at first light. warm gold sun, stars fading, a
   teal grid under an amber horizon. */
.vb-display--outrun.vb-display--sky-dawn {
  --or-sun-grad: linear-gradient(180deg, #fff3c4 0%, #ffd75e 38%, #ff9a3d 70%, #ff6a8a 100%);
  --or-sun-glow: rgba(255, 184, 77, 0.4);
  --or-sun-opacity: 0.9;
  --or-star-opacity: 0.25;
  --or-grid-rgb: 45, 212, 191;
  --or-horizon: #ffb84d;
  --or-horizon-glow: rgba(255, 184, 77, 0.75);
  --or-haze: rgba(40, 20, 46, 0.92);
  --or-road-rgb: 30, 18, 44;
  --or-dash-rgb: 255, 214, 170;
  --or-ridge-far: rgba(150, 80, 140, 0.5);
  --or-ridge-rgb: 80, 36, 90;
  --or-ridge-a: 0.75;
  --or-palm-rgb: 36, 14, 50;
  --or-seam: 50%;
  --or-ground-h: 50%;
  --or-tilt: 53deg;
  --or-ground-grad: linear-gradient(180deg, #1a0b2e, #0a0618);
  --vp-bg: linear-gradient(180deg,
    #241238 0%, #55246b 29%, #c85a78 43.5%, #ffb37a 48.5%, #ffd8a8 49.5%,
    #1a0b2e 50%, #0a0618 100%);
}
/* cruise: the classic dusk palette verbatim, but with the uphill scene
   geometry - the sky stops re-mapped to the risen 50% horizon. every
   color here is copied from the base dusk block above; only the stop
   positions and the geometry vars differ. */
.vb-display--outrun.vb-display--sky-cruise {
  --or-seam: 50%;
  --or-ground-h: 50%;
  --or-tilt: 53deg;
  --or-ground-grad: linear-gradient(180deg, #1a0b2e, #0a0618);
  --vp-bg: linear-gradient(180deg,
    #070718 0%, #171043 24%, #3d1663 42%, #8a1e63 47%, #c22b6e 49.5%,
    #1a0b2e 50%, #0a0618 100%);
}
/* chrome-gradient arcade title */
.vb-display--outrun .vb-dp-title {
  background: linear-gradient(180deg, #eaf6ff 0%, #9be8ff 44%, #22d3ee 56%, #ff2d78 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 2px 14px rgba(34, 211, 238, 0.35));
}
.vb-display--outrun .vb-dp-clock { color: #9be8ff; text-shadow: 0 0 18px rgba(34, 211, 238, 0.55); }
/* rows sit on a translucent panel so the scene glows through readably */
.vb-display--outrun .vb-dp-board {
  background: linear-gradient(180deg, rgba(10, 7, 31, 0.42), rgba(10, 7, 31, 0.68));
  border: 1px solid rgba(34, 211, 238, 0.14);
  border-radius: 10px;
  padding: clamp(6px, 1vh, 14px) clamp(8px, 1vw, 20px);
  margin-top: clamp(6px, 1vh, 14px);
}
.vb-display--outrun .vb-dp-rows .vb-dp-row:nth-child(even) { background: rgba(34, 211, 238, 0.04); }
/* the board panel's side padding narrows the tracks vs other themes, so
   the name column takes a wider share (long sprinter names must fit) */
.vb-display--outrun .vb-dp-row {
  grid-template-columns: minmax(132px, 1.3fr) minmax(104px, 0.72fr) minmax(104px, 0.72fr) 1.1fr 1.3fr;
}
/* chromatic-aberration glow on the vehicle names */
.vb-display--outrun .vb-dp-cell-name {
  text-shadow: 2px 0 0 rgba(255, 45, 120, 0.5), -2px 0 0 rgba(34, 211, 238, 0.5);
}
.vb-display--outrun .vb-dp-rotbar-fill { background: linear-gradient(90deg, #22d3ee, #b721ff, #ff2d78); }
/* neon-outline chips; severity keeps the app's semantic hues, neon-ized */
.vb-display--outrun .vb-dp-chip { background: rgba(7, 7, 24, 0.5); font-weight: 700; text-transform: uppercase; }
.vb-display--outrun .vb-dp-chip.vb-dp-tone--green { border-color: rgba(57, 255, 142, 0.6); color: #39ff8e; text-shadow: 0 0 12px rgba(57, 255, 142, 0.5); }
.vb-display--outrun .vb-dp-chip.vb-dp-tone--cyan { border-color: rgba(34, 211, 238, 0.6); color: #5fe6ff; text-shadow: 0 0 12px rgba(34, 211, 238, 0.5); }
.vb-display--outrun .vb-dp-chip.vb-dp-tone--teal { border-color: rgba(45, 212, 191, 0.6); color: #55f0d3; text-shadow: 0 0 12px rgba(45, 212, 191, 0.5); }
.vb-display--outrun .vb-dp-chip.vb-dp-tone--brown { border-color: rgba(255, 138, 61, 0.55); color: #ff9d5c; text-shadow: 0 0 12px rgba(255, 138, 61, 0.4); }
.vb-display--outrun .vb-dp-chip.vb-dp-tone--purple { border-color: rgba(185, 138, 255, 0.55); color: #c9a4ff; text-shadow: 0 0 12px rgba(185, 138, 255, 0.4); }
.vb-display--outrun .vb-dp-chip.vb-dp-tone--blue { border-color: rgba(95, 176, 255, 0.55); color: #8cc6ff; text-shadow: 0 0 12px rgba(95, 176, 255, 0.4); }
.vb-display--outrun .vb-dp-chip.vb-dp-tone--amber { border-color: rgba(255, 184, 77, 0.6); color: #ffce7a; text-shadow: 0 0 12px rgba(255, 184, 77, 0.5); }
.vb-display--outrun .vb-dp-chip.vb-dp-tone--gray { border-color: rgba(169, 156, 214, 0.35); color: #a99cd6; }
.vb-display--outrun .vb-dp-chip.vb-dp-tone--red { border-color: #ff2d78; color: #ff7aa8; text-shadow: 0 0 12px rgba(255, 45, 120, 0.55); }
/* game-over states: solid fills, and unsafe blinks like a boss warning */
.vb-display--outrun .vb-dp-chip.vb-dp-st--oos { background: #ff2d78; border-color: #ff2d78; color: #12041c; text-shadow: none; box-shadow: 0 0 18px rgba(255, 45, 120, 0.55); }
.vb-display--outrun .vb-dp-chip.vb-dp-st--unsafe_dvir { background: #ff2d78; border-color: #ff2d78; color: #12041c; text-shadow: none; box-shadow: 0 0 22px rgba(255, 45, 120, 0.7); animation: vb-dp-led-blink 1.1s steps(2, jump-none) infinite; }
.vb-display--outrun .vb-dp-depart-time { color: #9be8ff; }
.vb-display--outrun .vb-dp-depart--distant .vb-dp-depart-time { color: var(--vp-faint); }
.vb-display--outrun .vb-dp-depart-in--late { text-shadow: 0 0 14px rgba(255, 45, 120, 0.6); }
.vb-display--outrun .vb-dp-return-time--late { text-shadow: 0 0 14px rgba(255, 45, 120, 0.6); }

/* the fuel gauge: a hud bar under the departure time */
.vb-or-fuel {
  display: block;
  margin-left: auto;
  margin-top: 0.3em;
  width: clamp(90px, 10vw, 230px);
  height: clamp(4px, 0.9vh, 9px);
  border: 1px solid rgba(34, 211, 238, 0.4);
  border-radius: 99px;
  background: rgba(34, 211, 238, 0.08);
  overflow: hidden;
}
.vb-or-fuel-fill {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #22d3ee, #b721ff);
  transition: width 1s linear;
}
.vb-or-fuel--soon { border-color: rgba(255, 184, 77, 0.6); }
.vb-or-fuel--soon .vb-or-fuel-fill { background: linear-gradient(90deg, #ffb84d, #ff8a3d); }
.vb-or-fuel--late { border-color: rgba(255, 45, 120, 0.75); background: rgba(255, 45, 120, 0.14); animation: vb-dp-led-blink 1.1s steps(2, jump-none) infinite; }
/* the muted distant rows never carry a bar, so no distant styling needed */

/* the scene: stars, striped sun, neon horizon, rolling grid floor */
.vb-or-scene {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}
.vb-or-stars { position: absolute; left: 0; right: 0; top: 0; height: 55%; opacity: var(--or-star-opacity, 1); }
.vb-or-stars::before,
.vb-or-stars::after {
  content: '';
  position: absolute;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #eaf6ff;
  box-shadow:
    8vw 6vh 0 rgba(234, 246, 255, 0.9), 22vw 12vh 0 rgba(155, 232, 255, 0.7),
    35vw 4vh 0 rgba(234, 246, 255, 0.8), 47vw 15vh 0 rgba(255, 214, 236, 0.6),
    58vw 7vh 0 rgba(234, 246, 255, 0.9), 71vw 11vh 0 rgba(155, 232, 255, 0.7),
    83vw 5vh 0 rgba(234, 246, 255, 0.8), 92vw 13vh 0 rgba(255, 214, 236, 0.6);
  animation: vb-or-twinkle 4.2s ease-in-out infinite;
}
.vb-or-stars::after {
  box-shadow:
    14vw 18vh 0 rgba(234, 246, 255, 0.7), 28vw 8vh 0 rgba(255, 214, 236, 0.6),
    41vw 20vh 0 rgba(155, 232, 255, 0.7), 53vw 11vh 0 rgba(234, 246, 255, 0.8),
    66vw 17vh 0 rgba(234, 246, 255, 0.6), 78vw 21vh 0 rgba(155, 232, 255, 0.7),
    88vw 9vh 0 rgba(234, 246, 255, 0.8), 96vw 19vh 0 rgba(255, 214, 236, 0.5);
  animation-delay: -2.1s;
}
/* day-only parallax clouds: flat 16-bit puffs on two pseudo-element
   layers, straight off the coconut beach stage select. each layer is a
   200vw strip whose cloud pattern repeats at +100vw, so a -100vw
   translate loops seamlessly and the drift is a pure composited
   transform. the strip starts below the header band so no near-white
   pill ever passes behind the chrome title or the clock, and the scene's
   overflow: hidden clips the strip's off-screen half. */
.vb-or-clouds {
  display: none;
  position: absolute;
  top: 12%;
  left: 0;
  width: 200vw;
  height: 43%;
}
.vb-display--outrun.vb-display--sky-day .vb-or-clouds { display: block; }
.vb-or-clouds::before,
.vb-or-clouds::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 99px;
  background: transparent;
}
/* far layer: small pale pills, slow drift */
.vb-or-clouds::before {
  width: 9vh;
  height: 3vh;
  box-shadow:
    8vw 5vh 0 0 rgba(255, 255, 255, 0.5), 10vw 3.4vh 0 -0.9vh rgba(255, 255, 255, 0.5),
    38vw 11vh 0 0 rgba(255, 255, 255, 0.45), 40vw 9.4vh 0 -0.9vh rgba(255, 255, 255, 0.45),
    72vw 4vh 0 0 rgba(255, 255, 255, 0.5), 74vw 2.4vh 0 -0.9vh rgba(255, 255, 255, 0.5),
    108vw 5vh 0 0 rgba(255, 255, 255, 0.5), 110vw 3.4vh 0 -0.9vh rgba(255, 255, 255, 0.5),
    138vw 11vh 0 0 rgba(255, 255, 255, 0.45), 140vw 9.4vh 0 -0.9vh rgba(255, 255, 255, 0.45),
    172vw 4vh 0 0 rgba(255, 255, 255, 0.5), 174vw 2.4vh 0 -0.9vh rgba(255, 255, 255, 0.5);
  animation: vb-or-cloud-drift 160s linear infinite;
}
/* near layer: bigger, a touch brighter, faster, for the parallax read
   (alpha capped so nothing near-white crosses behind board text) */
.vb-or-clouds::after {
  width: 14vh;
  height: 4.8vh;
  box-shadow:
    20vw 15vh 0 0 rgba(255, 255, 255, 0.68), 23vw 12.6vh 0 -1.4vh rgba(255, 255, 255, 0.62),
    56vw 8vh 0 0 rgba(255, 255, 255, 0.66), 59vw 5.6vh 0 -1.4vh rgba(255, 255, 255, 0.6),
    87vw 18vh 0 0 rgba(255, 255, 255, 0.68), 90vw 15.6vh 0 -1.4vh rgba(255, 255, 255, 0.62),
    120vw 15vh 0 0 rgba(255, 255, 255, 0.68), 123vw 12.6vh 0 -1.4vh rgba(255, 255, 255, 0.62),
    156vw 8vh 0 0 rgba(255, 255, 255, 0.66), 159vw 5.6vh 0 -1.4vh rgba(255, 255, 255, 0.6),
    187vw 18vh 0 0 rgba(255, 255, 255, 0.68), 190vw 15.6vh 0 -1.4vh rgba(255, 255, 255, 0.62);
  animation: vb-or-cloud-drift 95s linear infinite;
}
.vb-or-sun {
  position: absolute;
  right: 7vw;
  bottom: 38%;
  width: 44vh;
  height: 44vh;
  border-radius: 50%;
  background: var(--or-sun-grad);
  -webkit-mask-image:
    linear-gradient(#000 0 54%, transparent 54%),
    repeating-linear-gradient(180deg, transparent 0 2.2%, #000 2.2% 8.4%);
  mask-image:
    linear-gradient(#000 0 54%, transparent 54%),
    repeating-linear-gradient(180deg, transparent 0 2.2%, #000 2.2% 8.4%);
  filter: drop-shadow(0 0 42px var(--or-sun-glow));
  opacity: var(--or-sun-opacity, 0.85);
}
.vb-or-ground {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: var(--or-ground-h, 38%);
  overflow: hidden;
  /* scenery skies fill the risen ground opaquely so it occludes the
     sun's lower band - the road ahead crests toward the sun. classic
     dusk keeps none and the page gradient shows through unchanged. */
  background: var(--or-ground-grad, none);
}
/* the floor is oversized and slides one grid cell per loop along its own
   rotated plane, so the scroll is a pure composited transform */
.vb-or-grid {
  position: absolute;
  left: -30%;
  right: -30%;
  top: -40%;
  bottom: -120%;
  transform-origin: 50% 0;
  /* static pose doubles as the reduced-motion fallback when the scroll
     animation (which restates this transform) is disabled */
  transform: perspective(38vh) rotateX(var(--or-tilt, 62deg));
  background-image:
    linear-gradient(rgba(var(--or-grid-rgb, 34, 211, 238), 0.30) 2px, transparent 2px),
    linear-gradient(90deg, rgba(var(--or-grid-rgb, 34, 211, 238), 0.30) 2px, transparent 2px);
  background-size: 7vh 7vh;
  animation: vb-or-grid-run 1.8s linear infinite;
}
/* the horizon: a neon seam where sky meets floor */
.vb-or-ground::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 2px;
  background: var(--or-horizon);
  box-shadow: 0 0 18px 2px var(--or-horizon-glow);
}
/* haze so the grid fades in from the horizon instead of popping */
.vb-or-ground::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 34%;
  z-index: 1;
  background: linear-gradient(180deg, var(--or-haze), transparent);
}

/* the scenery layer (every sky except classic dusk, which keeps the
   original scene untouched): a road riding the grid plane, mountain
   ridges on the seam, palm silhouettes framing the board, day gulls. */

/* the road lies on the exact same plane as the grid: identical box,
   identical transform, and the very same scroll keyframes, so road and
   grid move in lockstep by construction. the asphalt is translucent on
   purpose - the grid ghosts through at low alpha and reads as wet neon
   pavement instead of burying the signature floor. shoulders reuse
   --or-grid-rgb so the road glow stays in the same neon family the
   horizon seam and sun already use. dash period = one 7vh grid cell,
   so the 7vh scroll loop is seamless. */
.vb-or-road {
  position: absolute;
  left: -30%;
  right: -30%;
  top: -40%;
  bottom: -120%;
  transform-origin: 50% 0;
  transform: perspective(38vh) rotateX(var(--or-tilt, 62deg));
  background-image:
    linear-gradient(180deg, rgba(var(--or-dash-rgb), 0.85) 0 50%, transparent 50% 100%),
    linear-gradient(90deg,
      transparent 0 calc(50% - 13vw - 1.1vh),
      rgba(var(--or-grid-rgb), 0.22) calc(50% - 13vw - 1.1vh) calc(50% - 13vw - 0.35vh),
      rgba(var(--or-grid-rgb), 0.9) calc(50% - 13vw - 0.35vh) calc(50% - 13vw),
      rgba(var(--or-road-rgb), 0.55) calc(50% - 13vw) calc(50% + 13vw),
      rgba(var(--or-grid-rgb), 0.9) calc(50% + 13vw) calc(50% + 13vw + 0.35vh),
      rgba(var(--or-grid-rgb), 0.22) calc(50% + 13vw + 0.35vh) calc(50% + 13vw + 1.1vh),
      transparent calc(50% + 13vw + 1.1vh) 100%);
  background-size: 0.6vw 7vh, 100% 100%;
  background-position: 50% 0, 0 0;
  background-repeat: repeat-y, no-repeat;
  animation: vb-or-grid-run 1.8s linear infinite;
  pointer-events: none;
}
/* mountain ridges: bases sit exactly on the seam so the neon horizon
   line underlines their feet. the far ridge paints behind the sun (the
   disc occludes it); the near ridge overlaps only the sun's lower edge.
   tall peaks bias left of center as a counterweight to the sun at right. */
.vb-or-ridge-far {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(var(--or-seam, 62%) - 7vh);
  height: 7vh;
  background: var(--or-ridge-far);
  clip-path: polygon(0 100%, 0 60%, 5% 34%, 11% 62%, 17% 40%, 24% 68%, 30% 28%, 37% 58%, 44% 44%, 52% 70%, 59% 38%, 66% 62%, 74% 48%, 82% 66%, 90% 52%, 96% 64%, 100% 58%, 100% 100%);
}
.vb-or-ridge-near {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(var(--or-seam, 62%) - 11vh);
  height: 11vh;
  background: rgba(var(--or-ridge-rgb), var(--or-ridge-a));
  clip-path: polygon(0 100%, 0 55%, 6% 18%, 12% 62%, 18% 8%, 26% 70%, 33% 30%, 41% 78%, 50% 60%, 58% 82%, 68% 72%, 76% 88%, 84% 68%, 91% 84%, 100% 78%, 100% 100%);
}
/* palms: one reusable silhouette asset, tinted per sky via the mask */
.vb-or-palm {
  position: absolute;
  pointer-events: none;
  background: rgba(var(--or-palm-rgb), 0.9);
  -webkit-mask: var(--or-palm-svg) center / contain no-repeat;
  mask: var(--or-palm-svg) center / contain no-repeat;
}
@supports not ((mask: url('')) or (-webkit-mask: url(''))) {
  .vb-or-palm { background: var(--or-palm-svg-baked) center / contain no-repeat; }
}
/* corner giants: a near-mirrored pair anchoring the horizontal symmetry,
   crisp in the side gutters and bottom strip, poster-frame silhouettes
   through the glass. their fill is darker than the board glass, so they
   raise text contrast behind the panel rather than fighting it. crowns
   stay well below the header band. */
.vb-or-palm--l {
  left: -6vw;
  bottom: -4vh;
  width: 30vw;
  height: 62vh;
  transform-origin: 12% 100%;
  animation: vb-or-sway 17s ease-in-out infinite alternate;
}
.vb-or-palm--r {
  right: -7vw;
  bottom: -2vh;
  width: 26vw;
  height: 54vh;
  transform: scaleX(-1) rotate(3deg);
  transform-origin: 88% 100%;
  animation: vb-or-sway-r 14s ease-in-out infinite alternate;
}
/* seam-scale accents: a mini palm standing among the near-ridge peaks
   (silhouettes stand on landforms, never float) and one palm cut against
   the striped sun's lower-left limb - the canonical outrun frame. both
   have their feet exactly on the seam. */
.vb-or-palm--mini {
  left: 16vw;
  top: calc(var(--or-seam, 62%) - 6vh);
  width: 3.5vw;
  height: 6vh;
  background: rgba(var(--or-ridge-rgb), var(--or-ridge-a));
}
.vb-or-palm--sun {
  left: 70.5vw;
  top: calc(var(--or-seam, 62%) - 8vh);
  width: 4vw;
  height: 8vh;
  transform: scaleX(-1) rotate(-4deg);
}
/* day only: a gull flock crossing the open sky in a slow 90s pass - the
   one moving beat that sells day as a time of day, not just a palette */
.vb-or-gulls {
  display: none;
  position: absolute;
  /* the open-sky corridor: below the chrome title, above the glass board */
  top: 13%;
  left: 0;
  width: 9vw;
  height: 3vh;
  opacity: 0;
  pointer-events: none;
  background: url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%20190%2040'%3E%3Cg%20stroke='%23ffffff'%20stroke-opacity='.85'%20stroke-width='3'%20fill='none'%20stroke-linecap='round'%3E%3Cpath%20d='M6,30Q14,20,22,28Q30,20,38,30'/%3E%3Cpath%20d='M52,16Q59,8,66,15Q73,8,80,16'%20stroke-width='2.6'/%3E%3Cpath%20d='M96,34Q103,26,110,32Q117,26,124,34'%20stroke-width='2.6'/%3E%3Cpath%20d='M118,10Q124,4,130,9Q136,4,142,10'%20stroke-width='2.2'/%3E%3Cpath%20d='M150,26Q158,17,166,24Q174,17,182,26'/%3E%3C/g%3E%3C/svg%3E") center / contain no-repeat;
  animation: vb-or-gulls 90s linear infinite;
}
.vb-display--outrun.vb-display--sky-day .vb-or-gulls { display: block; }

@media (prefers-reduced-motion: reduce) {
  .vb-display { --vp-row-anim: none; }
  .vb-dp-rotbar-fill,
  .vb-dp-live-dot,
  .vb-display--ember .vb-dp-chip.vb-dp-st--unsafe_dvir,
  .vb-display--ember .vb-dp-depart-in--late,
  .vb-display--ember::after,
  .vb-display--ember .vb-dp-frame,
  .vb-display--ember .vb-dp-chip.vb-dp-st--oos,
  .vb-display--ember .vb-dp-return-time--late,
  .vb-display--ember .vb-dp-return-eta,
  .vb-ss-orb,
  .vb-dp-rows--ss .vb-dp-row.vb-ss-enter-active,
  .vb-dp-rows--ss .vb-dp-row.vb-ss-leave-active,
  .vb-or-grid,
  .vb-or-stars::before,
  .vb-or-stars::after,
  .vb-or-clouds::before,
  .vb-or-clouds::after,
  .vb-or-road,
  .vb-or-palm--l,
  .vb-or-palm--r,
  .vb-or-fuel--late,
  .vb-display--outrun .vb-dp-chip.vb-dp-st--unsafe_dvir,
  .vb-display--midnight .vb-dp-rows .vb-dp-rowface,
  .vb-display--showroom .vb-dp-rows .vb-dp-rowface { animation: none; }
  /* without its fold-away animation the outgoing face would sit on top
     of the new reading forever */
  .vb-dp-rowback { display: none; }
  /* the ticker still advances (burn-in protection is function, not
     decoration); it just steps instead of sliding */
  .vb-ss-move { transition: none; }
  /* the gulls park mid-sky as static scenery instead of crossing */
  .vb-or-gulls { animation: none; transform: translate(60vw, 0); opacity: 0.8; }
}
</style>

<style>
/* print path for vsm2, active only while the board is mounted
   (body.vsm-board-print set in mounted/beforeDestroy). hides the app chrome
   and the screen-only controls, unclips the responsive table, reveals the
   self-documenting header, and forces the chip fills to print. mirrors the
   trip-sheet wall board. */
@media print {
  body.vsm-board-print #navbar,
  body.vsm-board-print .fa-launcher-root,
  body.vsm-board-print .vb-rail-actions-col,
  body.vsm-board-print .vb-chip-filters,
  body.vsm-board-print .vb-error,
  body.vsm-board-print .vb-picker-pop,
  body.vsm-board-print .vb-expand-btn,
  body.vsm-board-print .vb-status-caret {
    display: none !important;
  }
  body.vsm-board-print .vb-print-meta {
    display: flex !important;
    flex-direction: column;
    gap: 2px;
    margin: 0 0 10px 0;
  }
  body.vsm-board-print .vb-print-meta-title { font-size: 16px; font-weight: 700; }
  body.vsm-board-print .vb-print-meta-sub,
  body.vsm-board-print .vb-print-meta-when { font-size: 11px; color: #555; }
  /* print the whole roster rather than an inner scroll viewport */
  body.vsm-board-print .vb-card,
  body.vsm-board-print .table-responsive { overflow: visible !important; }
  /* keep the status-chip fills on paper */
  body.vsm-board-print .vb-root,
  body.vsm-board-print .vb-status-chip {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}

/* tv display keyframes live unscoped on purpose: the scoped compiler
   renames @keyframes with the component hash but cannot rewrite names
   referenced through css variables (--vp-row-anim), which would leave the
   var-driven entrance animations pointing at nothing. names are
   vb-dp/vb-ss namespaced, so the global registry is safe. */
@keyframes vb-dp-fade-up {
  from { transform: translateY(14px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
/* rolling-prism panel turn, projected to 2d: both faces share one moving
   seam - the incoming face unrolls downward from the row's top edge
   (top edge pinned, bottom edge sweeping down) while the outgoing face
   compresses onto the row's bottom edge (bottom pinned, top sweeping
   down), and with matched translate+scale the two sweeping edges meet
   frame for frame, so the turn reads as one rigid drum. crucially the
   pinned edges mean NEITHER face ever paints outside the row box, so no
   ancestor clipping is needed - which is the whole point: webkit's
   compositor skips ancestor clips on 3d-transformed descendant layers,
   and the earlier rotateX/translateZ build painted stray slivers over
   neighbor rows on safari no matter how the row tried to clip them.
   the out face reaches the bottom edge (its 0.45s) exactly as the in
   face lands flat (82% of 0.55s), then the in face alone plays the
   small mechanical settle. settled frame is transform: none. */
@keyframes vb-dp-prism-out {
  0% {
    transform: translateY(0) scaleY(1);
    animation-timing-function: ease-in-out;
  }
  99% { visibility: visible; }
  100% {
    transform: translateY(50%) scaleY(0);
    visibility: hidden;
  }
}
@keyframes vb-dp-prism-in {
  0% {
    transform: translateY(-50%) scaleY(0);
    animation-timing-function: ease-in-out;
  }
  82% {
    transform: translateY(0) scaleY(1);
    animation-timing-function: ease-out;
  }
  91% { transform: translateY(0.8%) scaleY(0.985); }
  100% { transform: none; }
}
/* gameshow flap: hinged on the row's top edge, falls into place and
   rebounds once (transform-origin set on the rows). the settled frame is
   transform: none so fill-mode: both never parks a 3d layer per row.
   still used by the screen saver ticker's entrances. */
@keyframes vb-dp-flip-down {
  0% { transform: perspective(1200px) rotateX(-90deg); opacity: 0; }
  55% { transform: perspective(1200px) rotateX(10deg); opacity: 1; }
  78% { transform: perspective(1200px) rotateX(-5deg); }
  100% { transform: none; opacity: 1; }
}
/* the departing ticker row folds backwards at its bottom hinge WHILE
   riding up with the list slide (translateY tracks the one-row step the
   move transition makes underneath it). the fold is front-loaded: the
   panel is nearly edge-on by half time, so what crosses into the header
   zone is only a thin sliver, and the row arriving into the vacated slot
   never stacks under a still-flat panel. */
@keyframes vb-ss-flip-out {
  0% { transform: perspective(1200px) translateY(0) rotateX(0deg); opacity: 1; }
  55% { transform: perspective(1200px) translateY(-40%) rotateX(72deg); opacity: 0.85; }
  100% { transform: perspective(1200px) translateY(-100%) rotateX(88deg); opacity: 0; }
}
@keyframes vb-ss-drift-a {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(13vw, 9vh); }
}
@keyframes vb-ss-drift-b {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-11vw, 12vh); }
}
@keyframes vb-ss-drift-c {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(9vw, -10vh); }
}
@keyframes vb-dp-crt-roll {
  0% { transform: translateY(-120%); }
  16% { transform: translateY(760%); }
  100% { transform: translateY(760%); }
}
@keyframes vb-dp-crt-jitter {
  0%, 100% { opacity: 1; }
  9% { opacity: 0.96; }
  10% { opacity: 1; }
  47% { opacity: 0.97; }
  48% { opacity: 1; }
  76% { opacity: 0.95; }
  78% { opacity: 1; }
}
/* the phosphor var resolves per element, so the breathe glow follows the
   amber/green toggle without a second keyframe set */
@keyframes vb-dp-ember-breathe {
  0%, 100% { box-shadow: 0 0 18px rgba(var(--vp-phos-rgb, 255, 176, 0), 0.45); }
  50% { box-shadow: 0 0 32px rgba(var(--vp-phos-rgb, 255, 176, 0), 0.8); }
}
@keyframes vb-dp-ember-glow {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
}
@keyframes vb-dp-flicker {
  0% { opacity: 0; }
  18% { opacity: 0.85; }
  26% { opacity: 0.2; }
  38% { opacity: 1; }
  52% { opacity: 0.6; }
  100% { opacity: 1; }
}
@keyframes vb-dp-led-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
}
@keyframes vb-dp-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
@keyframes vb-dp-rotate-progress {
  from { width: 0; }
  to { width: 100%; }
}
/* outrun: rows race in from the left with a speed skew and settle */
@keyframes vb-or-race-in {
  0% { transform: translateX(-6vw) skewX(-10deg); opacity: 0; }
  60% { transform: translateX(1vw) skewX(3deg); opacity: 1; }
  100% { transform: none; opacity: 1; }
}
/* one grid cell per loop along the rotated plane = endless road. the
   tilt var resolves per themed element; webkit samples it at animation
   start, which is safe because the scene remounts (fresh animations)
   whenever the scenery gate flips and the tilt never changes otherwise */
@keyframes vb-or-grid-run {
  from { transform: perspective(38vh) rotateX(var(--or-tilt, 62deg)) translateY(0); }
  to { transform: perspective(38vh) rotateX(var(--or-tilt, 62deg)) translateY(7vh); }
}
/* day clouds: the pattern repeats at +100vw, so this loops seamlessly */
@keyframes vb-or-cloud-drift {
  from { transform: translateX(0); }
  to { transform: translateX(-100vw); }
}
@keyframes vb-or-twinkle {
  0%, 100% { opacity: 0.9; }
  50% { opacity: 0.25; }
}
/* corner palms breathe a fraction of a degree, desynced left vs right */
@keyframes vb-or-sway {
  from { transform: rotate(-0.4deg); }
  to { transform: rotate(0.4deg); }
}
@keyframes vb-or-sway-r {
  from { transform: scaleX(-1) rotate(2.6deg); }
  to { transform: scaleX(-1) rotate(3.4deg); }
}
/* the day gulls cross right to left with a mid-flight dip, fading at the
   edges so they never pop into or out of existence on screen */
@keyframes vb-or-gulls {
  0% { transform: translate(110vw, 0); opacity: 0; }
  3% { opacity: 0.8; }
  50% { transform: translate(45vw, -0.6vh); }
  97% { opacity: 0.8; }
  100% { transform: translate(-20vw, 0); opacity: 0; }
}

/* tv display: page chrome to hide lives outside this component's scope;
   the body class is set while display mode is active. covers the classic
   navbar, the horizon top bar and rail, and the fleet assistant launcher
   (whose z-index floats above any overlay). the /vsm2 standalone mounts
   none of these; the overflow rule still applies. */
body.vb-display-active { overflow: hidden; }
body.vb-display-active #navbar,
body.vb-display-active .fi-tn,
body.vb-display-active .fi-sn,
body.vb-display-active .fa-launcher-root { display: none !important; }
</style>
